/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  PlusIcon,
  MinusIcon,
  LocationMarkerIcon,
  UserIcon,
  ExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  SearchIcon,
  InformationCircleIcon,
  CalendarIcon,
  XIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SparklesIcon,
  AdjustmentsIcon,
  CogIcon,
  ShieldExclamationIcon,
  ChevronLeftIcon,
  AnnotationIcon,
  RefreshIcon,
  CameraIcon,
  PhotographIcon,
  PlayIcon,
  PauseIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  InboxIcon,
  ClipboardListIcon,
  DotsVerticalIcon
} from '@heroicons/react/outline'
import connexion from '../../connexion'
import DashboardButton from '../UI/DashboardButton'
import { prepareImage } from '../Inventory/Field/fieldPhotos'
import { hasOtherModules } from '../UI/dashboardAccess'
import { Status, Urgency, LocationType } from './types'
import {
  INTERVENTION_CATEGORIES,
  EQUIPMENTS
} from './constants'
// --- Utility functions ---

function parseDuration(str) {
  if (!str) return null
  const hm = str.match(/(\d+)h(\d*)/)
  if (hm) return (parseInt(hm[1]) * 60 + (parseInt(hm[2]) || 0)) * 60
  const m = str.match(/(\d+)\s*min/)
  if (m) return parseInt(m[1]) * 60
  if (str === 'Journée') return 8 * 3600
  return null
}

function formatDuration(seconds) {
  if (!seconds) return null
  const h = Math.floor(seconds / 3600)
  const min = Math.floor((seconds % 3600) / 60)
  if (h === 0) return min + ' min'
  if (min === 0) return h + 'h'
  return h + 'h' + String(min).padStart(2, '0')
}

// Toggle identique à celui des paramètres généraux (Settings/ConfigWebapp.jsx) —
// en div pour échapper aux styles Materialize sur <button>
function SettingsToggle({ checked, onChange, ariaLabel }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={onChange}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange() } }}
      style={{ cursor: 'pointer', flexShrink: 0 }}
    >
      <div
        style={{
          width: '2.75rem',
          height: '1.5rem',
          borderRadius: '9999px',
          backgroundColor: checked ? '#2563eb' : '#d1d5db',
          transition: 'background-color 0.2s',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '2px',
            left: checked ? 'calc(100% - 1.25rem - 2px)' : '2px',
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '50%',
            backgroundColor: '#fff',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </div>
    </div>
  )
}

// Avatars colorés par nom (initiales) — palette stable par hachage
const AVATAR_COLORS = ['#4338ca', '#0e9488', '#b45309', '#7c3aed', '#be123c', '#1d4ed8']
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0)
    return '?'
  return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase()
}
function avatarColor(name) {
  if (!name)
    return '#94a3b8'
  let h = 0
  for (let i = 0; i < name.length; i++)
    h = (h * 31 + name.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

// Une échéance est stockée sans heure (minuit du jour choisi) : elle n'est
// dépassée qu'une fois le jour entièrement écoulé — « aujourd'hui » reste
// valide jusqu'à 23h59
// Filtres de la liste desktop, cumulables : un choix par groupe (Urgents/Important/Normal, Ménage/Technique,
// Aujourd'hui/À venir, À faire/En cours/Terminés) plus « En retard », combinés par ET. Sans choix d'état, les terminées
// sont écartées. isToday : règle « Aujourd'hui » (aujourd'hui ou en retard) appliquée à une intervention.
const NO_DESKTOP_FILTERS = { urgency: null, type: null, when: null, state: null, late: false }

function hasDesktopFilters(filters) {
  return !!(filters.urgency || filters.type || filters.when || filters.state || filters.late)
}

function matchesDesktopFilters(t, filters, isToday) {
  if (filters.state === 'Terminés') {
    if (t.status !== Status.DONE) return false
  } else {
    if (t.status === Status.DONE) return false
    if (filters.state === 'Todo' && t.status !== Status.TODO) return false
    if (filters.state === 'Progress' && t.status !== Status.IN_PROGRESS) return false
  }
  if (filters.urgency === 'Urgent' && t.urgency !== Urgency.CRITICAL) return false
  if (filters.urgency === 'Important' && t.urgency !== Urgency.IMPORTANT) return false
  if (filters.urgency === 'Normal' && t.urgency !== Urgency.NORMAL) return false
  if (filters.type && t.type !== filters.type) return false
  if (filters.when === 'Today' && !isToday(t)) return false
  if (filters.when === 'Upcoming' && isToday(t)) return false
  if (filters.late && (t.status === Status.DONE || !isDeadlinePast(t.deadline))) return false // une intervention terminée n'est plus en retard
  return true
}

function isDeadlinePast(deadline) {
  if (!deadline)
    return false
  const end = new Date(deadline)
  end.setHours(23, 59, 59, 999)
  return end.getTime() < Date.now()
}

// « Aujourd'hui » / « Hier » / « Demain » plutôt que la date quand c'est le cas
function formatDayLabel(dateLike) {
  const d = new Date(dateLike)
  const day = new Date(d)
  day.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((day - today) / 86400000)
  if (diff === 0)
    return "Aujourd'hui"
  if (diff === -1)
    return 'Hier'
  if (diff === 1)
    return 'Demain'
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function ticketToServer(t, userId) {
  return {
    created_by: userId,
    created_time: Math.round(new Date(t.createdAt).getTime() / 1000),
    label: t.label || null,
    description: t.description,
    location_id: t.locationId,
    type: t.type,
    status: t.status,
    urgency: t.urgency || 'NORMAL',
    category: t.category || null,
    equipment: t.equipment || null,
    estimated_duration: parseDuration(t.estimatedDuration),
    deadline: t.deadline ? Math.round(new Date(t.deadline).getTime() / 1000) : null,
    is_customer_request: t.isCustomerRequest != null ? t.isCustomerRequest : false,
    customer_permission: t.customerPermission != null ? t.customerPermission : false,
    conversation_id: t.conversationId || null,
    assigned_to: t.assignedTo || null,
    photo_urls: (t.photoUrls || []).filter(u => !u.startsWith('blob:')),
    history: (t.history || []).map(event => {
      if (!event.photoUrls || event.photoUrls.length === 0) return event
      const filtered = event.photoUrls.filter(u => !u.startsWith('blob:'))
      if (filtered.length === event.photoUrls.length) return event
      const copy = Object.assign({}, event)
      if (filtered.length > 0) copy.photoUrls = filtered
      else delete copy.photoUrls
      return copy
    })
  }
}

function serverToTicket(doc) {
  return {
    id: doc._id ? doc._id.toString() : doc.id,
    label: doc.label || '',
    description: doc.description,
    locationId: doc.location_id,
    type: doc.type,
    status: doc.status,
    urgency: doc.urgency || 'NORMAL',
    category: doc.category || null,
    equipment: doc.equipment || null,
    estimatedDuration: formatDuration(doc.estimated_duration),
    deadline: doc.deadline ? new Date(doc.deadline * 1000).toISOString() : null,
    isCustomerRequest: doc.is_customer_request != null ? doc.is_customer_request : false,
    customerPermission: doc.customer_permission != null ? doc.customer_permission : false,
    conversationId: doc.conversation_id ? String(doc.conversation_id) : null,
    assignedTo: doc.assigned_to || null,
    photoUrls: doc.photo_urls || [],
    history: doc.history || [],
    createdAt: doc.created_time ? new Date(doc.created_time * 1000).toISOString() : new Date().toISOString(),
    createdBy: doc.created_by ? doc.created_by.toString() : '',
    createdUserBy: doc.created_user_by ? doc.created_user_by.toString() : null,
    createdUserName: doc.created_user_name || null
  }
}

// --- Push web (notifications agents) ---

// Conversion de la clé publique VAPID pour pushManager.subscribe
// (même helper que webapp/src/registerServiceWorker.js)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i)
    outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}

// --- Offline queue ---

const OFFLINE_QUEUE_KEY = 'interventions_offline_queue'

function getOfflineQueue() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]') }
  catch { return [] }
}

function saveOfflineQueue(queue) {
  try { localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue)) }
  catch {}
}

function enqueueOfflineAction(action) {
  const queue = getOfflineQueue()
  queue.push(Object.assign({ _queuedAt: Date.now() }, action))
  saveOfflineQueue(queue)
}

// --- StatusBadge ---

const StatusBadge = ({ status, compact = false }) => {
  const config = {
    [Status.TODO]: {
      icon: <ClockIcon className="w-3.5 h-3.5 text-slate-500" />,
      bg: 'bg-slate-50 border border-slate-200',
      text: 'text-slate-600 font-bold',
      label: 'À faire'
    },
    [Status.IN_PROGRESS]: {
      icon: <PlayIcon className="w-3 h-3 text-amber-500" />,
      bg: 'bg-amber-50 border border-amber-200',
      text: 'text-amber-700 font-bold',
      label: 'En cours'
    },
    [Status.BLOCKED]: {
      icon: <PauseIcon className="w-3 h-3 text-red-500" />,
      bg: 'bg-red-50 border border-red-200',
      text: 'text-red-700 font-bold',
      label: 'En pause'
    },
    [Status.DONE]: {
      icon: <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />,
      bg: 'bg-emerald-50 border border-emerald-200',
      text: 'text-emerald-700 font-bold',
      label: 'Terminé'
    },
  }
  const item = config[status] || config[Status.TODO]
  const { icon, bg, text, label } = item
  if (compact)
  {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs uppercase tracking-wider ${bg} ${text}`}>
        {icon}
        <span>{label}</span>
      </span>
    )
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs uppercase tracking-wider shadow-sm ${bg} ${text}`}>
      {icon}
      <span>{label}</span>
    </span>
  )
}

// --- UrgencyBadge ---

const UrgencyBadge = ({ urgency, variant = 'subtle', done = false, className = '', compact = false }) => {
  if (variant === 'solid') {
    const config = {
      [Urgency.NORMAL]: { icon: null, bg: 'bg-slate-600 border border-slate-500', text: 'text-white font-black', label: 'Normal' },
      [Urgency.IMPORTANT]: { icon: <ExclamationIcon className="w-3 h-3 text-white" />, bg: 'bg-amber-500 border border-amber-400', text: 'text-white font-black', label: 'Important' },
      [Urgency.CRITICAL]: { icon: <ShieldExclamationIcon className="w-3 h-3 text-white" style={{ animation: 'pulse 2s infinite' }} />, bg: 'bg-red-600 border border-red-500', text: 'text-white font-black', label: 'Urgent' },
    }
    const item = config[urgency] || config[Urgency.NORMAL]
    const bg = done ? 'bg-slate-100 border border-slate-300' : item.bg
    const text = done ? 'text-slate-400 font-extrabold' : item.text
    const icon = done ? null : item.icon
    const { label } = item
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs uppercase tracking-widest leading-none shadow-md ${bg} ${text} ${className}`}>
        {icon}
        <span>{label}</span>
      </span>
    )
  }
  const config = {
    [Urgency.NORMAL]: { icon: null, bg: 'bg-slate-50 border border-slate-200', text: 'text-slate-500 font-bold', label: 'Normal' },
    [Urgency.IMPORTANT]: { icon: <ExclamationIcon className="w-3.5 h-3.5 text-amber-600" />, bg: 'bg-amber-50 border border-amber-200', text: 'text-amber-800 font-bold', label: 'Important' },
    [Urgency.CRITICAL]: { icon: <ShieldExclamationIcon className="w-3.5 h-3.5 text-red-600" />, bg: 'bg-red-50 border border-red-200', text: 'text-red-800 font-bold', label: 'Urgent' },
  }
  const item = config[urgency] || config[Urgency.NORMAL]
  const { icon, bg, text, label } = item
  if (compact)
  {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-2xs uppercase tracking-wider ${bg} ${text} ${className}`}>
        {icon}
        <span>{label}</span>
      </span>
    )
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-sm uppercase tracking-wider ${bg} ${text} ${className}`}>
      {icon}
      <span>{label}</span>
    </span>
  )
}

// --- DurationStepper ---

function DurationStepper({ value, onChange }) {
  const parseMinutes = (v) => {
    if (!v) return 0
    const str = String(v).toLowerCase()
    if (str.includes('journée')) return 480
    if (str.includes('h')) {
      const hMatch = str.match(/(\d+)h/i)
      const mMatch = str.match(/h(\d+)/i) || str.match(/(\d+)\s*min/i)
      const h = hMatch ? parseInt(hMatch[1]) : 0
      const m = mMatch ? parseInt(mMatch[1]) : 0
      return h * 60 + m
    }
    const m = parseInt(str.replace(/\D/g, '')) || 0
    return m
  }
  const minutes = parseMinutes(value)
  const handleAdjust = (delta) => {
    const next = Math.max(0, minutes + delta)
    onChange(`${next} min`)
  }
  return (
    <div className="bg-slate-100 p-1.5 rounded-3xl flex items-center justify-between border border-slate-100">
      <button
        type="button"
        onClick={() => handleAdjust(-5)}
        className="w-14 h-14 rounded-3xl flex items-center justify-center text-slate-400 border border-slate-100 shadow-sm transition-colors hover:text-blue-600"
        style={{ backgroundImage: 'none', backgroundColor: '#ffffff', transition: 'transform 0.15s ease' }}
        onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.88)' }}
        onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
        onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
      >
        <MinusIcon className="w-6 h-6" />
      </button>
      <div className="flex items-center gap-1 px-4">
        <input
          type="number"
          value={minutes}
          onChange={(e) => { const val = parseInt(e.target.value) || 0; onChange(`${val} min`) }}
          className="browser-default block w-20 text-2xl font-black text-slate-900 tracking-tighter text-center"
          style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', MozAppearance: 'textfield', WebkitAppearance: 'none', margin: 0, padding: 0 }}
        />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">MIN</span>
      </div>
      <button
        type="button"
        onClick={() => handleAdjust(5)}
        className="w-14 h-14 rounded-3xl flex items-center justify-center text-slate-400 border border-slate-100 shadow-sm transition-colors hover:text-blue-600"
        style={{ backgroundImage: 'none', backgroundColor: '#ffffff', transition: 'transform 0.15s ease' }}
        onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.88)' }}
        onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
        onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
      >
        <PlusIcon className="w-6 h-6" />
      </button>
    </div>
  )
}

// Vignettes : le serveur garde les photos envoyées en pleine taille (2048 px au plus) et en miniatures de 720 et
// 360 px. Une adresse /dashboard/files/get-image/<id> sans taille devient /<id>/360 (grilles) ou /<id>/720 (grand
// format) ; toute autre adresse (aperçu local, autre site) reste telle quelle.
function thumbUrl(url, size = 360) {
  return typeof url === 'string' ? url.replace(/(\/dashboard\/files\/get-image\/[0-9a-f]{24})$/, '$1/' + size) : url
}

// --- PhotoUploadSelector ---

function PhotoUploadSelector({ photos, onPhotosChange, onUploadingChange }) {
  const [pendingUploads, setPendingUploads] = useState({})
  const photosRef = useRef(photos)
  const filesRef = useRef({})
  const mountedRef = useRef(true)

  useEffect(() => { photosRef.current = photos }, [photos])

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const uploadingCount = Object.values(pendingUploads).filter(s => s === 'uploading').length

  useEffect(() => {
    if (onUploadingChange) onUploadingChange(uploadingCount)
  }, [uploadingCount]) // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFile = (blobUrl, file) => {
    setPendingUploads(prev => Object.assign({}, prev, { [blobUrl]: 'uploading' }))
    const formData = new FormData()
    formData.append('file', file)
    formData.append('path', 'interventions/')
    fetch(connexion.connect.url + 'dashboard/files/add-image', {
      method: 'POST',
      body: formData
    })
    .then(res => res.json())
    .then(ret => {
      if (!mountedRef.current) return
      if (!ret || !ret._id) throw new Error('upload failed')
      const serverUrl = connexion.connect.url + 'dashboard/files/get-image/' + ret._id
      onPhotosChange(photosRef.current.map(u => u === blobUrl ? serverUrl : u))
      URL.revokeObjectURL(blobUrl)
      delete filesRef.current[blobUrl]
      setPendingUploads(prev => { const n = Object.assign({}, prev); delete n[blobUrl]; return n })
    })
    .catch(() => {
      if (!mountedRef.current) return
      setPendingUploads(prev => Object.assign({}, prev, { [blobUrl]: 'error' }))
    })
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    e.target.value = ''

    // Photos de téléphone (3 à 8 Mo) : réduites une par une (2048 px au plus) avant l'envoi
    files.reduce((chain, file) => chain.then(done => prepareImage(file).then(prepared => done.concat([prepared]))), Promise.resolve([]))
      .then(prepared => {
        if (!mountedRef.current) return
        const blobUrls = prepared.map(p => URL.createObjectURL(p.preview || p.file))
        onPhotosChange([...photosRef.current, ...blobUrls])
        prepared.forEach((p, i) => {
          filesRef.current[blobUrls[i]] = p.file
        })
        blobUrls.forEach((u, i) => uploadFile(u, prepared[i].file))
      })
  }

  const removePhoto = (index) => {
    const url = photos[index]
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
      delete filesRef.current[url]
      setPendingUploads(prev => { const n = Object.assign({}, prev); delete n[url]; return n })
    }
    const newPhotos = [...photos]
    newPhotos.splice(index, 1)
    onPhotosChange(newPhotos)
  }

  const errorCount = Object.values(pendingUploads).filter(s => s === 'error').length

  const inputId = useMemo(() => `photo-upload-${Math.random().toString(36).substr(2, 9)}`, [])
  // Téléphone : « Appareil photo » (capture) et « Galerie » (plusieurs photos) ; ordinateur : un seul bouton
  const isTouch = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches, [])
  const openInput = (id) => { const el = document.getElementById(id); if (el) el.click() }

  return (
    <section>
      <label className="text-sm uppercase font-black text-slate-800 mb-2 mt-0 block tracking-widest px-1">
        Photos (Optionnel)
        {uploadingCount > 0 && (
          <span className="ml-2 text-xs font-bold text-blue-500 normal-case tracking-normal">
            Envoi en cours…
          </span>
        )}
        {errorCount > 0 && uploadingCount === 0 && (
          <span className="ml-2 text-xs font-bold text-red-500 normal-case tracking-normal">
            {errorCount} photo{errorCount > 1 ? 's' : ''} non envoyée{errorCount > 1 ? 's' : ''}
          </span>
        )}
      </label>
      <input type="file" id={inputId + '-camera'} accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
      <input type="file" id={inputId} accept="image/*" multiple className="hidden" onChange={handleFileChange} />
      <div className="grid grid-cols-2 gap-3">
        {photos.map((url, idx) => {
          const status = pendingUploads[url]
          return (
            <div key={idx} className="relative group" style={{ aspectRatio: '1 / 1' }}>
              <img
                src={thumbUrl(url)}
                alt={`Preview ${idx}`}
                className={`w-full h-full object-cover rounded-2xl border-2 shadow-sm ${status === 'error' ? 'border-red-300 opacity-60' : 'border-slate-100'}`}
              />
              <button
                onClick={() => removePhoto(idx)}
                className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full shadow-lg active:scale-90 hover:bg-red-700 transition-colors"
                style={{ backgroundImage: 'none' }}
              >
                <XIcon className="w-3 h-3" />
              </button>
              {status === 'uploading' && (
                <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                  <span className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                </div>
              )}
              {status === 'error' && (
                <button
                  type="button"
                  onClick={() => {
                    const file = filesRef.current[url]
                    if (file)
                      uploadFile(url, file)
                  }}
                  aria-label="Réessayer l'envoi de la photo"
                  className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center gap-1.5 text-white cursor-pointer"
                  style={{ backgroundImage: 'none', backgroundColor: 'rgba(15,23,42,0.55)' }}
                >
                  <RefreshIcon className="w-6 h-6" />
                  <span className="text-xs font-bold uppercase tracking-wider">Réessayer</span>
                </button>
              )}
            </div>
          )
        })}
        {isTouch && (
          <button
            type="button"
            onClick={() => openInput(inputId + '-camera')}
            className={`bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-500 transition-all hover:bg-white hover:border-slate-400 active:scale-95 ${photos.length === 0 ? 'py-8' : ''}`}
            style={{ backgroundImage: 'none', backgroundColor: '#f8fafc', aspectRatio: photos.length === 0 ? 'auto' : '1 / 1' }}
          >
            <CameraIcon className="w-8 h-8 text-slate-400" />
            <span className="text-xs font-bold uppercase tracking-widest">Appareil photo</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => openInput(inputId)}
          className={`bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-500 transition-all hover:bg-white hover:border-slate-400 active:scale-95 ${photos.length === 0 ? (isTouch ? 'py-8' : 'col-span-2 py-8') : ''}`}
          style={{ backgroundImage: 'none', backgroundColor: '#f8fafc', aspectRatio: photos.length === 0 ? 'auto' : '1 / 1' }}
        >
          {isTouch ? <PhotographIcon className="w-8 h-8 text-slate-400" /> : <PlusIcon className="w-8 h-8 text-slate-300" />}
          <span className="text-xs font-bold uppercase tracking-widest">{isTouch ? 'Galerie' : (photos.length === 0 ? 'Ajouter des photos' : 'Ajouter')}</span>
        </button>
      </div>
    </section>
  )
}

// --- TicketEditView ---

function TicketEditView({ ticket, currentUserName, onSave, onBack }) {
  const [editingTicket, setEditingTicket] = useState(ticket)
  const [categorySearch, setCategorySearch] = useState(ticket.category || '')
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  const [equipmentSearch, setEquipmentSearch] = useState(ticket.equipment || '')
  const [isEquipmentOpen, setIsEquipmentOpen] = useState(false)

  const filteredCategories = INTERVENTION_CATEGORIES.filter(c =>
    c.toLowerCase().includes(categorySearch.toLowerCase())
  )
  const filteredEquipments = EQUIPMENTS.filter(e =>
    e.toLowerCase().includes(equipmentSearch.toLowerCase())
  )

  const handleSave = () => {
    const event = {
      id: Math.random().toString(36).substr(2, 5),
      type: 'modification',
      user: currentUserName || 'Admin',
      timestamp: new Date().toISOString(),
      details: 'Mise à jour des détails de l\'intervention'
    }
    const finalCategory = INTERVENTION_CATEGORIES.includes(categorySearch) ? categorySearch : (editingTicket.category || null)
    const finalEquipment = EQUIPMENTS.includes(equipmentSearch) ? equipmentSearch : (editingTicket.equipment || null)
    onSave({ ...editingTicket, category: finalCategory, equipment: finalEquipment, history: [...(editingTicket.history || []), event] })
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundImage: 'none' }}>
            <ChevronLeftIcon className="w-5 h-5 text-slate-500" />
          </button>
          <p className="text-base font-black text-slate-900">Modifier l'intervention</p>
        </div>
        <button onClick={onBack} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">
          Annuler
        </button>
      </header>

      <div className="p-6 space-y-8 pb-32">
        <section className="space-y-4">
          <span className="text-xs uppercase font-black text-slate-800 tracking-wider block border-b border-slate-200 pb-2">Origine de la demande</span>
          <div className="flex gap-2">
            <button
              onClick={() => setEditingTicket({ ...editingTicket, isCustomerRequest: true })}
              className={`flex-1 py-4 px-2 rounded-2xl border-2 font-bold text-xs flex items-center justify-center gap-2 ${editingTicket.isCustomerRequest ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-100 text-slate-500'}`}
              style={{ backgroundImage: 'none', backgroundColor: editingTicket.isCustomerRequest ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <UserIcon className="w-4 h-4" /> Demande Client
            </button>
            <button
              onClick={() => setEditingTicket({ ...editingTicket, isCustomerRequest: false })}
              className={`flex-1 py-4 px-2 rounded-2xl border-2 font-bold text-xs flex items-center justify-center gap-2 ${editingTicket.isCustomerRequest === false ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-100 text-slate-500'}`}
              style={{ backgroundImage: 'none', backgroundColor: editingTicket.isCustomerRequest === false ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <SearchIcon className="w-4 h-4" /> Ticket Interne
            </button>
          </div>
          {editingTicket.isCustomerRequest && (
            <div className="bg-white p-5 rounded-2xl border border-slate-200 flex items-center justify-between shadow-sm">
              <div className="space-y-0.5">
                <p className="text-sm font-bold text-slate-800">Accord absence</p>
                <p className="text-xs text-slate-400 font-medium">Autorisation d'entrer en absence client</p>
              </div>
              <button
                onClick={() => setEditingTicket({ ...editingTicket, customerPermission: !editingTicket.customerPermission })}
                className="w-12 h-6 rounded-full relative flex-shrink-0"
                style={{ backgroundImage: 'none', backgroundColor: editingTicket.customerPermission ? '#10b981' : '#e2e8f0', transition: 'background-color 0.2s ease' }}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${editingTicket.customerPermission ? 'right-1' : 'left-1'}`} />
              </button>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <span className="text-xs uppercase font-black text-slate-800 tracking-wider block border-b border-slate-200 pb-2">Classification technique</span>
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-bold uppercase tracking-widest ml-1">Catégorie d'intervention</label>
            <div className="relative">
              <SearchIcon className="absolute left-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher une catégorie..."
                value={categorySearch}
                onChange={e => { setCategorySearch(e.target.value); setIsCategoryOpen(true) }}
                onFocus={() => setIsCategoryOpen(true)}
                className="browser-default block w-full py-4 pl-12 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all"
                style={{ outline: 'none', boxShadow: 'none' }}
              />
              <button onClick={() => setIsCategoryOpen(!isCategoryOpen)} className="absolute right-4 inset-y-0 my-auto" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}>
                <ChevronDownIcon className={`w-5 h-5 text-slate-300 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {isCategoryOpen && (
              <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                {filteredCategories.length > 0 ? filteredCategories.map(c => (
                  <button key={c} onClick={() => { setCategorySearch(c); setIsCategoryOpen(false) }} className={`w-full px-4 py-3 text-left text-sm font-bold border-b border-slate-50 last:border-0 ${categorySearch === c ? 'text-blue-600' : 'text-slate-700'}`} style={{ backgroundImage: 'none', backgroundColor: categorySearch === c ? '#eff6ff' : 'transparent' }}>
                    {c}
                  </button>
                )) : (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0"><SearchIcon className="w-4 h-4 text-slate-300" /></div>
                    <div>
                      <p className="text-sm font-bold text-slate-400">Aucun résultat</p>
                      <p className="text-xs text-slate-300 font-medium">"{categorySearch}" ne correspond à aucune catégorie</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-bold uppercase tracking-widest ml-1">Équipement visé</label>
            <div className="relative">
              <CogIcon className="absolute left-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher un équipement..."
                value={equipmentSearch}
                onChange={e => { setEquipmentSearch(e.target.value); setIsEquipmentOpen(true) }}
                onFocus={() => setIsEquipmentOpen(true)}
                className="browser-default block w-full py-4 pl-12 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all"
                style={{ outline: 'none', boxShadow: 'none' }}
              />
              <button onClick={() => setIsEquipmentOpen(!isEquipmentOpen)} className="absolute right-4 inset-y-0 my-auto" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}>
                <ChevronDownIcon className={`w-5 h-5 text-slate-300 transition-transform ${isEquipmentOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {isEquipmentOpen && (
              <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                {filteredEquipments.length > 0 ? filteredEquipments.map(e => (
                  <button key={e} onClick={() => { setEquipmentSearch(e); setIsEquipmentOpen(false) }} className={`w-full px-4 py-3 text-left text-sm font-bold border-b border-slate-50 last:border-0 ${equipmentSearch === e ? 'text-blue-600' : 'text-slate-700'}`} style={{ backgroundImage: 'none', backgroundColor: equipmentSearch === e ? '#eff6ff' : 'transparent' }}>
                    {e}
                  </button>
                )) : (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0"><CogIcon className="w-4 h-4 text-slate-300" /></div>
                    <div>
                      <p className="text-sm font-bold text-slate-400">Aucun résultat</p>
                      <p className="text-xs text-slate-300 font-medium">"{equipmentSearch}" ne correspond à aucun équipement</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <span className="text-xs uppercase font-black text-slate-800 tracking-wider block border-b border-slate-200 pb-2">Engagement & délais</span>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between ml-1">
              <label className="text-xs text-slate-400 font-bold uppercase tracking-widest">Date d'échéance</label>
              <div className="flex gap-2">
                <button onClick={() => setEditingTicket({ ...editingTicket, deadline: new Date().toISOString() })} className="text-xs font-bold text-blue-600 px-3 py-1.5 rounded-xl" style={{ backgroundImage: 'none', backgroundColor: '#eff6ff', transition: 'transform 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>Aujourd'hui</button>
                <button onClick={() => { const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); setEditingTicket({ ...editingTicket, deadline: tomorrow.toISOString() }) }} className="text-xs font-bold text-blue-600 px-3 py-1.5 rounded-xl" style={{ backgroundImage: 'none', backgroundColor: '#eff6ff', transition: 'transform 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>Demain</button>
              </div>
            </div>
            <div className="relative">
              <CalendarIcon className="absolute left-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              <input
                type="date"
                value={(editingTicket.deadline ? editingTicket.deadline.split('T')[0] : '') || ''}
                onChange={e => setEditingTicket({ ...editingTicket, deadline: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="browser-default block w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 focus:border-blue-500 shadow-sm transition-all"
                style={{ outline: 'none', boxShadow: 'none' }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-bold uppercase tracking-widest ml-1">Durée estimée</label>
            <DurationStepper value={editingTicket.estimatedDuration || ''} onChange={(v) => setEditingTicket({ ...editingTicket, estimatedDuration: v })} />
          </div>
        </section>

        <section className="space-y-4">
          <span className="text-xs uppercase font-black text-slate-800 tracking-wider block border-b border-slate-200 pb-2">Description du problème</span>
          <div className="space-y-1.5">
            <label className="text-xs text-slate-400 font-bold uppercase tracking-widest ml-1">Détails complémentaires</label>
            <textarea
              rows={4}
              value={editingTicket.description || ''}
              onChange={e => setEditingTicket({ ...editingTicket, description: e.target.value })}
              className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none shadow-sm"
              style={{ minHeight: '100px' }}
            />
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-xl mx-auto p-6 bg-white border-t border-slate-100">
          <button
            onClick={handleSave}
            className="w-full py-4 text-white text-base rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
            style={{ backgroundImage: 'none', backgroundColor: '#2563eb', transition: 'transform 0.15s ease' }}
            onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.97)' }}
            onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
            onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
          >
            <CheckCircleIcon className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}

// --- TicketEditViewDesktop ---

function TicketEditViewDesktop({ ticket, currentUserName, lieux, teams, employees, onSave, onBack }) {
  const [editingTicket, setEditingTicket] = useState(ticket)
  const [categorySearch, setCategorySearch] = useState(ticket.category || '')
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  const [equipmentSearch, setEquipmentSearch] = useState(ticket.equipment || '')
  const [isEquipmentOpen, setIsEquipmentOpen] = useState(false)

  const initialLieuName = (() => {
    const lieu = (lieux || []).find(l => l.id === ticket.locationId)
    return (lieu && lieu.name) || ticket.locationId || ''
  })()
  const [lieuSearch, setLieuSearch] = useState(initialLieuName)
  const [isLieuDropdownOpen, setIsLieuDropdownOpen] = useState(false)

  const filteredCategories = INTERVENTION_CATEGORIES.filter(c =>
    c.toLowerCase().includes(categorySearch.toLowerCase())
  )
  const filteredEquipments = EQUIPMENTS.filter(e =>
    e.toLowerCase().includes(equipmentSearch.toLowerCase())
  )
  const filteredLieux = (lieux || []).filter(l =>
    l.name.toLowerCase().includes(lieuSearch.toLowerCase()) ||
    l.id.toLowerCase().includes(lieuSearch.toLowerCase())
  )

  const [isAssignOpen, setIsAssignOpen] = useState(false)

  const handleSave = () => {
    const changedFields = []
    if (editingTicket.locationId !== ticket.locationId) changedFields.push('localisation')
    if (editingTicket.type !== ticket.type) changedFields.push("type d'intervention")
    if (JSON.stringify(editingTicket.assignedTo) !== JSON.stringify(ticket.assignedTo)) changedFields.push('collaborateur assigné')
    if (editingTicket.urgency !== ticket.urgency) changedFields.push('urgence')
    if (editingTicket.description !== ticket.description) changedFields.push('description')
    if (editingTicket.descriptionExtra !== ticket.descriptionExtra) changedFields.push("consignes d'accès")
    if (categorySearch !== (ticket.category || '')) changedFields.push('catégorie')
    if (equipmentSearch !== (ticket.equipment || '')) changedFields.push('équipement')
    if (editingTicket.deadline !== ticket.deadline) changedFields.push('échéance')
    if (editingTicket.estimatedDuration !== ticket.estimatedDuration) changedFields.push('durée estimée')
    if (editingTicket.customerPermission !== ticket.customerPermission) changedFields.push("accord d'absence")

    const changesText = changedFields.length > 0
      ? 'Modification de : ' + changedFields.join(', ')
      : "Mise à jour des informations de l'intervention"

    const event = {
      id: Math.random().toString(36).substr(2, 5),
      type: 'modification',
      user: currentUserName || 'Admin',
      timestamp: new Date().toISOString(),
      details: changesText
    }
    const finalCategory = INTERVENTION_CATEGORIES.includes(categorySearch) ? categorySearch : (editingTicket.category || null)
    const finalEquipment = EQUIPMENTS.includes(equipmentSearch) ? equipmentSearch : (editingTicket.equipment || null)
    onSave({
      ...editingTicket,
      label: (editingTicket.description || '').substring(0, 30) || editingTicket.label,
      category: finalCategory,
      equipment: finalEquipment,
      history: [...(editingTicket.history || []), event]
    })
  }

  const types = [
    { id: 'Technique', title: 'Technique', icon: <CogIcon className="w-5 h-5" /> },
    { id: 'Ménage', title: 'Ménage', icon: <SparklesIcon className="w-5 h-5" /> },
    { id: 'Divers', title: 'Divers', icon: <AnnotationIcon className="w-5 h-5" /> }
  ]

  const durationOptions = ['15 min', '30 min', '45 min', '1 h', '2 h', '4 h', '1 journée']
  const durationToMinutes = (v) => {
    if (!v) return 0
    const str = String(v).toLowerCase()
    if (str.includes('journée')) return 480
    if (str.includes('h')) {
      const hMatch = str.match(/(\d+)\s*h/)
      const mMatch = str.match(/h\s*(\d+)/) || str.match(/(\d+)\s*min/)
      return (hMatch ? parseInt(hMatch[1]) : 0) * 60 + (mMatch ? parseInt(mMatch[1]) : 0)
    }
    return parseInt(str.replace(/\D/g, '')) || 0
  }
  const durationMinutes = durationToMinutes(editingTicket.estimatedDuration)
  const setDurationMinutes = (next) => {
    setEditingTicket(prev => ({ ...prev, estimatedDuration: `${Math.max(0, next)} min` }))
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 relative">

      {/* Header */}
      <header className="px-6 py-5 border-b border-slate-200 bg-white sticky top-0 z-20 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            onClick={onBack}
            aria-label="Retour à la fiche intervention"
            className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center transition-colors duration-200 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-2xs font-black uppercase tracking-widest text-slate-400">Édition</span>
              <span className="text-2xs font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                {(() => { const l = (lieux || []).find(x => x.id === editingTicket.locationId); return (l && l.name) || 'Sans lieu' })()}
              </span>
            </div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight leading-none mt-1" style={{ marginBottom: 0 }}>
              Modifier l'intervention
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={editingTicket.status} compact />
          {editingTicket.urgency !== Urgency.NORMAL && (
            <UrgencyBadge urgency={editingTicket.urgency} compact />
          )}
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 pb-28">
        <div className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-6 pb-12">

          {/* Colonne gauche — 8 cols */}
          <div className="lg:col-span-8 space-y-6">

            {/* Panneau 1 : Informations principales */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <PencilIcon className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-black uppercase text-slate-800 tracking-wider" style={{ margin: 0 }}>Informations principales</h3>
              </div>

              <div className="space-y-1.5">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Type d'intervention
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {types.map((t) => {
                    const isSelected = editingTicket.type === t.id
                    // Couleur par type, alignée sur la fiche et la liste : indigo Technique, teal Ménage
                    const tone = t.id === 'Technique'
                      ? { border: '#6366f1', bg: '#eef2ff', icon: '#4f46e5', text: 'text-indigo-700' }
                      : t.id === 'Ménage'
                        ? { border: '#14b8a6', bg: '#f0fdfa', icon: '#0d9488', text: 'text-teal-700' }
                        : { border: '#64748b', bg: '#f8fafc', icon: '#475569', text: 'text-slate-700' }
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setEditingTicket(prev => ({ ...prev, type: t.id }))}
                        className={`py-4 px-2 rounded-2xl border-2 transition-colors duration-200 flex flex-col items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isSelected ? 'shadow-sm' : 'hover:bg-slate-50'
                        }`}
                        style={{ backgroundImage: 'none', backgroundColor: isSelected ? tone.bg : '#ffffff', borderColor: isSelected ? tone.border : '#e2e8f0' }}
                      >
                        <div
                          className="w-8 h-8 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: isSelected ? tone.icon : '#f1f5f9', color: isSelected ? '#ffffff' : '#94a3b8' }}
                        >
                          {t.icon}
                        </div>
                        <span className={`text-xs font-black uppercase tracking-wide ${isSelected ? tone.text : 'text-slate-500'}`}>{t.title}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Niveau d'urgence
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: Urgency.NORMAL, text: 'Normal', dot: '#94a3b8', selBg: '#f8fafc', selBorder: '#475569', selText: '#1e293b' },
                    { id: Urgency.IMPORTANT, text: 'Important', dot: '#d97706', selBg: '#fffbeb', selBorder: '#d97706', selText: '#92400e' },
                    { id: Urgency.CRITICAL, text: 'Urgent', dot: '#dc2626', selBg: '#fef2f2', selBorder: '#dc2626', selText: '#991b1b' }
                  ].map((level) => {
                    const isSelected = editingTicket.urgency === level.id
                    return (
                      <button
                        key={level.id}
                        type="button"
                        onClick={() => setEditingTicket({ ...editingTicket, urgency: level.id })}
                        className="py-3.5 px-2 rounded-2xl border-2 transition-colors duration-200 flex flex-col items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          backgroundImage: 'none',
                          backgroundColor: isSelected ? level.selBg : '#ffffff',
                          borderColor: isSelected ? level.selBorder : '#e2e8f0'
                        }}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: level.dot }} />
                        <span className="text-xs font-black uppercase tracking-wide" style={{ color: isSelected ? level.selText : '#94a3b8' }}>{level.text}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Panneau 2 : Localisation & Accès */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5 relative">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <LocationMarkerIcon className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-black uppercase text-slate-800 tracking-wider" style={{ margin: 0 }}>Localisation & Accès</h3>
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Emplacement réel de l'intervention
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <LocationMarkerIcon className="w-4 h-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Saisir ou choisir un emplacement, zone..."
                    value={lieuSearch}
                    onFocus={() => setIsLieuDropdownOpen(true)}
                    onChange={(e) => {
                      setLieuSearch(e.target.value)
                      setIsLieuDropdownOpen(true)
                    }}
                    className="browser-default w-full pl-10 pr-10 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 transition-all"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                  {lieuSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setLieuSearch('')
                        setEditingTicket(prev => ({ ...prev, locationId: '' }))
                      }}
                      aria-label="Effacer l'emplacement"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer focus:outline-none focus:text-slate-700"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {isLieuDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsLieuDropdownOpen(false)} />
                    <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 max-h-52 overflow-y-auto py-1 text-xs">
                      {filteredLieux.length === 0 ? (
                        <div className="p-4 text-center text-slate-400 font-bold italic">Aucun lieu trouvé</div>
                      ) : (
                        filteredLieux.map(l => (
                          <button
                            key={l.id}
                            type="button"
                            onClick={() => {
                              setEditingTicket(prev => ({ ...prev, locationId: l.id }))
                              setLieuSearch(l.name)
                              setIsLieuDropdownOpen(false)
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between border-b border-slate-50 last:border-0 cursor-pointer focus:outline-none focus:bg-slate-50"
                            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                          >
                            <div>
                              <span className="font-semibold text-sm text-slate-800 block">{l.name}</span>
                              {l.zone && <span className="text-slate-400 text-xs font-medium uppercase">{l.zone}</span>}
                            </div>
                            <span className="text-3xs uppercase font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
                              {l.type}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              {editingTicket.locationId && (
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                      <ShieldExclamationIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-slate-800 block leading-tight">Accord d'absence client</span>
                      <span className="text-2xs text-slate-400 font-medium block mt-0.5">Autorisation d'accès en cas d'absence des résidents</span>
                    </div>
                  </div>
                  <SettingsToggle
                    checked={!!editingTicket.customerPermission}
                    onChange={() => setEditingTicket(prev => ({ ...prev, customerPermission: !prev.customerPermission }))}
                    ariaLabel="Accord d'absence client"
                  />
                </div>
              )}
            </div>

            {/* Panneau 3 : Descriptions & Consignes */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <AnnotationIcon className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-black uppercase text-slate-800 tracking-wider" style={{ margin: 0 }}>Descriptions & Consignes</h3>
              </div>

              <div className="space-y-1.5">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Détails du problème / Description
                </label>
                <textarea
                  rows={4}
                  placeholder="Décrivez précisément le travail à faire ou le problème signalé..."
                  value={editingTicket.description || ''}
                  onChange={e => setEditingTicket({ ...editingTicket, description: e.target.value })}
                  className="browser-default w-full p-4 bg-slate-50 border border-slate-200 hover:bg-slate-100 focus:bg-white focus:border-blue-500 rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 transition-all resize-none"
                  style={{ outline: 'none', boxShadow: 'none' }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Consignes d'accès & détails avancés
                </label>
                <textarea
                  rows={3}
                  placeholder="Ex : Code barrière 4B82, Chien de garde, Clé disponible à l'accueil..."
                  value={editingTicket.descriptionExtra || ''}
                  onChange={e => setEditingTicket({ ...editingTicket, descriptionExtra: e.target.value })}
                  className="browser-default w-full p-4 bg-slate-50 border border-slate-200 hover:bg-slate-100 focus:bg-white focus:border-blue-500 rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 transition-all resize-none"
                  style={{ outline: 'none', boxShadow: 'none' }}
                />
              </div>
            </div>

          </div>

          {/* Colonne droite — 4 cols */}
          <div className="lg:col-span-4 space-y-6">

            {/* Panneau 4 : Suivi & Affectation */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <UserIcon className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-black uppercase text-slate-800 tracking-wider" style={{ margin: 0 }}>Suivi & Affectation</h3>
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Assigné à / Intervenant responsable
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsAssignOpen(prev => !prev)}
                    className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 border rounded-2xl text-sm font-medium text-left flex items-center justify-between transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: isAssignOpen ? '#ffffff' : '#f8fafc', borderColor: isAssignOpen ? '#3b82f6' : '#e2e8f0' }}
                  >
                    <span className={editingTicket.assignedTo ? 'text-slate-800' : 'text-slate-400'}>
                      {editingTicket.assignedTo ? editingTicket.assignedTo.name : '— Non assigné'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isAssignOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isAssignOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsAssignOpen(false)} />
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 max-h-56 overflow-y-auto py-1">
                        <button
                          type="button"
                          onClick={() => { setEditingTicket(prev => ({ ...prev, assignedTo: null })); setIsAssignOpen(false) }}
                          className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${!editingTicket.assignedTo ? 'text-blue-600 font-bold' : 'text-slate-500 font-medium'} text-sm`}
                          style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                        >
                          — Non assigné
                          {!editingTicket.assignedTo && <span className="text-blue-500 text-xs">✓</span>}
                        </button>
                        {(teams || []).length > 0 && (
                          <>
                            <div className="px-4 py-1.5 text-2xs font-black text-slate-400 uppercase tracking-widest bg-slate-50 border-b border-slate-100">Équipes</div>
                            {(teams || []).map(team => {
                              const isSel = editingTicket.assignedTo && editingTicket.assignedTo.type === 'team' && editingTicket.assignedTo.id === team
                              return (
                                <button
                                  key={team}
                                  type="button"
                                  onClick={() => { setEditingTicket(prev => ({ ...prev, assignedTo: { type: 'team', id: team, name: team } })); setIsAssignOpen(false) }}
                                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer text-sm ${isSel ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <UserGroupIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                                    {team}
                                  </div>
                                  {isSel && <span className="text-blue-500 text-xs">✓</span>}
                                </button>
                              )
                            })}
                          </>
                        )}
                        {(employees || []).length > 0 && (
                          <>
                            <div className="px-4 py-1.5 text-2xs font-black text-slate-400 uppercase tracking-widest bg-slate-50 border-b border-slate-100 border-t border-slate-100">Individuel</div>
                            {(employees || []).map(emp => {
                              const isSel = editingTicket.assignedTo && editingTicket.assignedTo.type === 'employee' && editingTicket.assignedTo.id === emp.id
                              return (
                                <button
                                  key={emp.id}
                                  type="button"
                                  onClick={() => { setEditingTicket(prev => ({ ...prev, assignedTo: { type: 'employee', id: emp.id, name: emp.name } })); setIsAssignOpen(false) }}
                                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer text-sm ${isSel ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <UserIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                                    {emp.name}
                                  </div>
                                  {isSel && <span className="text-blue-500 text-xs">✓</span>}
                                </button>
                              )
                            })}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Date limite d'échéance
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { label: "Aujourd'hui", offset: 0 },
                    { label: 'Demain', offset: 1 },
                    { label: 'Dans 3j', offset: 3 },
                    { label: '7 jours', offset: 7 }
                  ].map(({ label, offset }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        const date = new Date()
                        date.setDate(date.getDate() + offset)
                        setEditingTicket(prev => ({ ...prev, deadline: date.toISOString() }))
                      }}
                      className="py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold uppercase transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <CalendarIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="date"
                    value={editingTicket.deadline ? editingTicket.deadline.split('T')[0] : ''}
                    onChange={e => setEditingTicket({ ...editingTicket, deadline: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    className="browser-default w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 hover:bg-slate-100 focus:bg-white focus:border-blue-500 rounded-2xl text-base font-medium text-slate-800 cursor-pointer"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Durée d'intervention estimée
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {durationOptions.map(dur => {
                    const isSelected = durationMinutes > 0 && durationToMinutes(dur) === durationMinutes
                    return (
                      <button
                        key={dur}
                        type="button"
                        onClick={() => setEditingTicket(prev => ({ ...prev, estimatedDuration: dur }))}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors duration-200 cursor-pointer border inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSelected ? 'border-blue-500 text-white' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                        style={{ backgroundImage: 'none', backgroundColor: isSelected ? '#2563eb' : '#f8fafc' }}
                      >
                        <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        {dur}
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-2xl p-1.5">
                  <button
                    type="button"
                    onClick={() => setDurationMinutes(durationMinutes - 5)}
                    aria-label="Réduire la durée de 5 minutes"
                    className="w-9 h-9 rounded-xl border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-blue-600 transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                  >
                    <MinusIcon className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={durationMinutes}
                      onChange={e => setDurationMinutes(parseInt(e.target.value) || 0)}
                      aria-label="Durée estimée en minutes"
                      className="browser-default w-16 text-center text-base font-black text-slate-900"
                      style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', margin: 0, padding: 0, MozAppearance: 'textfield', WebkitAppearance: 'none' }}
                    />
                    <span className="text-2xs font-bold text-slate-400 uppercase tracking-widest">min</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDurationMinutes(durationMinutes + 5)}
                    aria-label="Augmenter la durée de 5 minutes"
                    className="w-9 h-9 rounded-xl border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-blue-600 transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Panneau 5 : Classification Technique */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5 relative">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <CogIcon className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-black uppercase text-slate-800 tracking-wider" style={{ margin: 0 }}>Classification technique</h3>
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Catégorie technique
                </label>
                <div className="relative">
                  <SearchIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Ex : Plomberie, Chauffage..."
                    value={categorySearch}
                    onChange={e => { setCategorySearch(e.target.value); setIsCategoryOpen(true) }}
                    onFocus={() => setIsCategoryOpen(true)}
                    className="browser-default w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 hover:bg-slate-100 focus:bg-white rounded-2xl text-base font-medium focus:border-blue-500 text-slate-800 placeholder-slate-400 transition-all"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    aria-label="Afficher la liste des catégories"
                    className="absolute right-3 top-3 text-slate-400 hover:text-blue-600 transition-colors duration-200 cursor-pointer focus:outline-none focus:text-blue-600"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {isCategoryOpen && (
                  <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto py-1">
                    {filteredCategories.length > 0 ? filteredCategories.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => { setCategorySearch(c); setIsCategoryOpen(false) }}
                        className={`w-full px-4 py-2.5 text-left text-sm border-b border-slate-50 last:border-0 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors focus:outline-none focus:bg-slate-50 ${categorySearch === c ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                        style={{ backgroundImage: 'none', backgroundColor: categorySearch === c ? '#eff6ff' : 'transparent' }}
                      >
                        <span>{c}</span>
                        {categorySearch === c && <CheckCircleIcon className="w-3.5 h-3.5 text-blue-600" />}
                      </button>
                    )) : (
                      <div className="p-3 text-xs font-bold text-slate-400 italic">Aucune catégorie correspondante</div>
                    )}
                  </div>
                )}
                {categorySearch.trim() && !isCategoryOpen && !INTERVENTION_CATEGORIES.includes(categorySearch) && (
                  <p className="text-2xs my-0 text-amber-600 font-semibold flex items-center gap-1">
                    <ExclamationIcon className="w-3 h-3 flex-shrink-0" />
                    Sélectionnez une valeur dans la liste, sinon la catégorie précédente sera conservée
                  </p>
                )}
              </div>

              <div className="space-y-1.5 relative">
                <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                  Équipement concerné
                </label>
                <div className="relative">
                  <CogIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Ex : Chauffe-eau, Climatiseur..."
                    value={equipmentSearch}
                    onChange={e => { setEquipmentSearch(e.target.value); setIsEquipmentOpen(true) }}
                    onFocus={() => setIsEquipmentOpen(true)}
                    className="browser-default w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 hover:bg-slate-100 focus:bg-white rounded-2xl text-base font-medium focus:border-blue-500 text-slate-800 placeholder-slate-400 transition-all"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => setIsEquipmentOpen(!isEquipmentOpen)}
                    aria-label="Afficher la liste des équipements"
                    className="absolute right-3 top-3 text-slate-400 hover:text-blue-600 transition-colors duration-200 cursor-pointer focus:outline-none focus:text-blue-600"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isEquipmentOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {isEquipmentOpen && (
                  <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto py-1">
                    {filteredEquipments.length > 0 ? filteredEquipments.map(e => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => { setEquipmentSearch(e); setIsEquipmentOpen(false) }}
                        className={`w-full px-4 py-2.5 text-left text-sm border-b border-slate-50 last:border-0 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors focus:outline-none focus:bg-slate-50 ${equipmentSearch === e ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                        style={{ backgroundImage: 'none', backgroundColor: equipmentSearch === e ? '#eff6ff' : 'transparent' }}
                      >
                        <span>{e}</span>
                        {equipmentSearch === e && <CheckCircleIcon className="w-3.5 h-3.5 text-blue-600" />}
                      </button>
                    )) : (
                      <div className="p-3 text-xs font-bold text-slate-400 italic">Aucun équipement correspondant</div>
                    )}
                  </div>
                )}
                {equipmentSearch.trim() && !isEquipmentOpen && !EQUIPMENTS.includes(equipmentSearch) && (
                  <p className="text-2xs my-0 text-amber-600 font-semibold flex items-center gap-1">
                    <ExclamationIcon className="w-3 h-3 flex-shrink-0" />
                    Sélectionnez une valeur dans la liste, sinon l'équipement précédent sera conservé
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-5 bg-slate-50 border-t border-slate-200 flex items-center justify-center sticky bottom-0 z-20 shrink-0">
        <div className="max-w-6xl w-full space-y-2.5">
          {!editingTicket.locationId && (
            <p className="text-xs my-0 text-amber-600 font-semibold flex items-center justify-center gap-1.5">
              <ExclamationIcon className="w-3.5 h-3.5 flex-shrink-0" />
              Renseignez un emplacement pour pouvoir sauvegarder
            </p>
          )}
          <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={onBack}
            className="w-1/3 py-3.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-extrabold text-xs uppercase tracking-widest rounded-2xl transition-colors duration-200 text-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!editingTicket.locationId}
            className="flex-1 py-3.5 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-md transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
          >
            <CheckCircleIcon className="w-4 h-4" />
            <span>Sauvegarder les modifications</span>
          </button>
          </div>
        </div>
      </footer>

      {/* Backdrop fermeture dropdowns */}
      {(isCategoryOpen || isEquipmentOpen || isLieuDropdownOpen) && (
        <div
          className="fixed inset-0 z-10 pointer-events-auto"
          onClick={() => {
            setIsCategoryOpen(false)
            setIsEquipmentOpen(false)
            setIsLieuDropdownOpen(false)
          }}
        />
      )}
    </div>
  )
}

// --- CreationDetailsForm ---

function CreationDetailsForm({ data, onUpdate, onConfirm, isLocatif, footerLeft, onBack }) {
  const [categorySearch, setCategorySearch] = useState(data.category || '')
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const filteredCategories = INTERVENTION_CATEGORIES.filter(c =>
    c.toLowerCase().includes(categorySearch.toLowerCase())
  )

  const handleDateSelect = (date) => {
    onUpdate({ deadline: date.toISOString() })
    setIsDatePickerOpen(false)
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-20 flex items-center gap-3">
        <button onClick={onBack} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundImage: 'none' }}>
          <ChevronLeftIcon className="w-5 h-5 text-slate-500" />
        </button>
        <p className="text-base font-black text-slate-900">Enrichir l'intervention</p>
      </header>

      <div className="p-6 space-y-8 pb-32">
        <section className="space-y-4">
          <label className="text-xs text-slate-400 font-bold ml-1 uppercase tracking-widest">Date d'échéance</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onUpdate({ deadline: new Date().toISOString() })}
              className={`py-6 rounded-2xl border-2 font-bold flex flex-col items-center gap-1 ${(data.deadline ? data.deadline.split('T')[0] : null) === new Date().toISOString().split('T')[0] ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
              style={{ backgroundImage: 'none', backgroundColor: (data.deadline ? data.deadline.split('T')[0] : null) === new Date().toISOString().split('T')[0] ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <span className="text-sm">Aujourd'hui</span>
              <span className="text-xs opacity-60">{new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
            </button>
            <button
              onClick={() => { const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); onUpdate({ deadline: tomorrow.toISOString() }) }}
              className={`py-6 rounded-2xl border-2 font-bold flex flex-col items-center gap-1 ${(data.deadline ? data.deadline.split('T')[0] : null) === new Date(Date.now() + 86400000).toISOString().split('T')[0] ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
              style={{ backgroundImage: 'none', backgroundColor: (data.deadline ? data.deadline.split('T')[0] : null) === new Date(Date.now() + 86400000).toISOString().split('T')[0] ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <span className="text-sm">Demain</span>
              <span className="text-xs opacity-60">{new Date(Date.now() + 86400000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
            </button>
          </div>
          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="w-full py-4 pl-4 pr-4 bg-white rounded-2xl border border-slate-200 text-slate-800 font-semibold text-sm flex items-center justify-between shadow-sm transition-all"
            style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
            onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.98)' }}
            onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
            onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
          >
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              <span>{data.deadline && !isNaN(new Date(data.deadline).getTime()) ? new Date(data.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Sélectionner une date'}</span>
            </div>
            <ChevronDownIcon className="w-4 h-4 text-slate-300" />
          </button>
        </section>

        <section className="space-y-4">
          <label className="text-xs text-slate-500 font-bold ml-1 uppercase tracking-widest">Durée estimée</label>
          <DurationStepper value={data.estimatedDuration || ''} onChange={(v) => onUpdate({ estimatedDuration: v })} />
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest col-span-2">Origine de la demande</p>
            <span className={`bg-blue-50 text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full uppercase transition-opacity ${data.isCustomerRequest ? 'opacity-100' : 'opacity-0'}`}>Client</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onUpdate({ isCustomerRequest: false })}
              className={`flex-1 py-4 px-2 rounded-2xl border-2 font-bold text-xs flex items-center justify-center gap-2 ${data.isCustomerRequest === false ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
              style={{ backgroundImage: 'none', backgroundColor: data.isCustomerRequest === false ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <SearchIcon className="w-4 h-4" /> Ticket Interne
            </button>
            <button
              onClick={() => onUpdate({ isCustomerRequest: true })}
              className={`flex-1 py-4 px-2 rounded-2xl border-2 font-bold text-xs flex items-center justify-center gap-2 ${data.isCustomerRequest ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
              style={{ backgroundImage: 'none', backgroundColor: data.isCustomerRequest ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
              onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }}
              onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }}
              onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <UserIcon className="w-4 h-4" /> Demande Client
            </button>
          </div>
          {(data.locationIds || []).some(id => isLocatif(id)) && (
            <div className="bg-white p-6 rounded-3xl border border-slate-100 flex items-center justify-between shadow-sm mt-3">
              <div className="space-y-0.5 flex-1 pr-4">
                <p className="text-sm font-bold text-slate-800">Accord absence</p>
                <p className="text-xs text-slate-400">Autorisation d'entrer en absence client</p>
              </div>
              <button
                onClick={() => onUpdate({ customerPermission: !data.customerPermission })}
                className="w-12 h-6 flex-shrink-0 rounded-full transition-colors relative"
                style={{ backgroundImage: 'none', backgroundColor: data.customerPermission ? '#10b981' : '#e2e8f0' }}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${data.customerPermission ? 'right-1' : 'left-1'}`} />
              </button>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <p className="text-xs my-0 uppercase font-black text-slate-800 tracking-wider px-1 border-b border-slate-200 pb-2">Détails techniques</p>
          <div className="space-y-3">
            <label className="text-xs text-slate-400 font-bold ml-1 uppercase tracking-widest">Type d'intervention</label>
            <button
              onClick={() => { setCategorySearch(''); setIsCategoryOpen(true) }}
              className="w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-sm font-semibold shadow-sm text-left relative"
              style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
            >
              <span className={data.category ? 'text-slate-800' : 'text-slate-400'}>{data.category || 'Sélectionner un type...'}</span>
              <ChevronDownIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
            </button>
          </div>
        </section>
      </div>

      {isDatePickerOpen && (
        <>
          <motion.div key="date-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={() => setIsDatePickerOpen(false)} className="fixed inset-0" style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(15,23,42,0.55)', zIndex: 200 }} />
          <motion.div key="date-sheet" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 32, stiffness: 380 }} className={`fixed bottom-0 left-0 right-0 ${footerLeft || ''} transition-all duration-300`} style={{ zIndex: 201 }}>
            <div className="max-w-xl mx-auto bg-white rounded-t-3xl shadow-2xl">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mt-4 mb-6" />
              <div className="px-6 pb-8 space-y-5">
                <h4 className="text-lg font-black text-slate-900 my-0">Choisir une échéance</h4>
                <div className="relative">
                  <CalendarIcon className="absolute left-4 inset-y-0 my-auto w-5 h-5 text-slate-400 pointer-events-none" />
                  <input type="date" className="browser-default block w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 shadow-sm transition-all" style={{ outline: 'none' }} onChange={e => { if (e.target.value) handleDateSelect(new Date(e.target.value)) }} value={data.deadline ? data.deadline.split('T')[0] : ''} />
                </div>
                <button onClick={() => setIsDatePickerOpen(false)} className="w-full py-4 rounded-2xl font-bold text-sm text-white" style={{ backgroundImage: 'none', backgroundColor: '#0f172a' }}>Confirmer</button>
              </div>
            </div>
          </motion.div>
        </>
      )}

      <div className={`fixed bottom-0 left-0 right-0 ${footerLeft || ''} z-40 transition-all duration-300`}>
        <div className="max-w-xl mx-auto p-6 bg-white border-t border-slate-100 flex gap-3">
          <button onClick={onConfirm} className="text-xl w-full py-5 bg-blue-600 text-white rounded-3xl font-bold shadow-xl shadow-blue-200 active:scale-95 transition-all flex items-center justify-center gap-2">
            <CheckCircleIcon className="w-5 h-5" /> Créer l'intervention
          </button>
        </div>
      </div>

      {isCategoryOpen && (
        <>
          <motion.div key="cat-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={() => setIsCategoryOpen(false)} className="fixed inset-0" style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(15,23,42,0.55)', zIndex: 200 }} />
          <motion.div key="cat-sheet" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 32, stiffness: 380 }} className={`fixed bottom-0 left-0 right-0 ${footerLeft || ''} transition-all duration-300`} style={{ zIndex: 201 }}>
            <div className="max-w-xl mx-auto bg-white rounded-t-3xl shadow-2xl flex flex-col" style={{ maxHeight: '70vh' }}>
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mt-4 mb-4 flex-shrink-0" />
              <div className="px-6 pb-3 flex-shrink-0">
                <h4 className="text-lg font-black text-slate-900 mt-0 mb-4">Type d'intervention</h4>
                <div className="relative">
                  <SearchIcon className="absolute left-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
                  <input type="text" placeholder="Rechercher un type..." value={categorySearch} onChange={e => setCategorySearch(e.target.value)} className="browser-default block w-full py-4 pl-12 pr-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 shadow-sm" style={{ outline: 'none' }} />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-8">
                {filteredCategories.length > 0 ? filteredCategories.map(c => (
                  <button key={c} onClick={() => { setCategorySearch(c); setIsCategoryOpen(false); onUpdate({ category: c }) }} className={`w-full py-4 text-left text-sm font-bold border-b border-slate-50 last:border-0 ${categorySearch === c ? 'text-blue-600' : 'text-slate-700'}`} style={{ backgroundImage: 'none', backgroundColor: categorySearch === c ? '#eff6ff' : 'transparent' }}>
                    {c}
                  </button>
                )) : (
                  <p className="py-6 text-xs font-bold text-slate-400 italic text-center">Aucun résultat</p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </div>
  )
}

// --- SelectTypeView ---

function SelectTypeView({ onCancel, onSelect }) {
  const types = [
    { id: 'CREATE_MENAGE', title: 'Ménage', desc: 'Sortie, déshivernage, etc.', icon: '🧹', color: 'border-blue-600 bg-blue-50 text-blue-700' },
    { id: 'CREATE_TECH', title: 'Technique', desc: 'Réparation, électricité, etc.', icon: '🛠️', color: 'border-slate-200 bg-white text-slate-800' },
    { id: 'CREATE_DIVERS', title: 'Divers', desc: 'Livraison, accueil, etc.', icon: '📋', color: 'border-slate-200 bg-white text-slate-800' }
  ]
  return (
    <div className="flex flex-col h-full bg-white overflow-y-auto pb-24">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
          <p className="text-base font-black text-slate-900">Type de création</p>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>
      <div className="flex-1 p-6 space-y-4 overflow-y-auto">
        {types.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              const typeMap = { 'CREATE_MENAGE': 'Ménage', 'CREATE_TECH': 'Technique', 'CREATE_DIVERS': 'Divers' }
              const assignMap = {
                'CREATE_MENAGE': { type: 'team', id: 'Équipe ménage', name: 'Équipe ménage' },
                'CREATE_TECH': { type: 'team', id: 'Équipe technique', name: 'Équipe technique' },
                'CREATE_DIVERS': { type: 'team', id: 'Réception', name: 'Réception' },
              }
              onSelect(type.id === 'CREATE_MENAGE' ? 'CREATE_MENAGE' : type.id, { type: typeMap[type.id], assignedTo: assignMap[type.id], isCustomerRequest: false, customerPermission: true })
            }}
            className={`w-full text-left p-5 rounded-3xl border-2 transition-all active:scale-95 ${type.color} shadow-sm`}
          >
            <div className="flex justify-between items-start mb-1">
              <p className="font-bold">{type.title}</p>
              <span className="text-xl">{type.icon}</span>
            </div>
            <p className="text-xs opacity-75 font-bold uppercase tracking-widest">{type.desc}</p>
          </button>
        ))}
      </div>
    </div>
  )
}

// --- SelectMenageSubtypeView ---

function SelectMenageSubtypeView({ onUpdate, onCancel, onBack, onNext }) {
  const subtypes = [
    { id: 'Ménage sortie vacancier', icon: '🚪' },
    { id: 'Ménage déshivernage', icon: '❄️' },
    { id: 'Ménage de printemps', icon: '🌱' },
    { id: 'Autre ménage', icon: '🧹' }
  ]
  return (
    <div className="flex flex-col h-full bg-white overflow-y-auto">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundImage: 'none' }}>
            <ArrowLeftIcon className="w-4 h-4 text-slate-500" />
          </button>
          <p className="text-base font-black text-slate-900">Précision du ménage</p>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>
      <div className="flex-1 p-6 space-y-4">
        {subtypes.map(s => (
          <button
            key={s.id}
            onClick={() => { onUpdate({ label: s.id, isCustomerRequest: false }); onNext() }}
            className="w-full text-left p-5 rounded-3xl border-2 border-slate-100 bg-white hover:border-blue-200 hover:bg-blue-50/10 transition-all active:scale-95 shadow-sm flex items-center justify-between"
          >
            <span className="font-bold text-slate-800">{s.id}</span>
            <span className="text-xl">{s.icon}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// --- CreateMenageView ---

function CreateMenageView({ newIntervention, onUpdate, onCancel, onDetails, onCreate, lieux, getLieuName, isLocatif, teams, employees, footerLeft, searchQuery, setSearchQuery, assignSearchQuery, setAssignSearchQuery }) {
  const filteredLieux = lieux.filter(l => l.name.toLowerCase().includes(searchQuery.toLowerCase()))
  return (
    <div className="flex flex-col h-full bg-white relative">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
          <div>
            <p className="text-base font-black text-slate-900 leading-tight">Saisie rapide</p>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest leading-tight">Ménage</p>
          </div>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>
      <div className="flex-1 p-6 space-y-6 overflow-y-auto pb-32">
        <section className="relative z-30">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block mb-2">Localisation</span>
          <div>
            <div className="relative">
              <input type="text" placeholder="MH, Sanitaires, Accueil..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchQuery.endsWith('*')) {
                    const prefix = searchQuery.slice(0, -1).toLowerCase()
                    const toAdd = lieux.filter(l => l.name.toLowerCase().startsWith(prefix) && !(newIntervention.locationIds || []).includes(l.id)).map(l => l.id)
                    if (toAdd.length > 0) onUpdate({ locationIds: [...(newIntervention.locationIds || []), ...toAdd] })
                    setSearchQuery('')
                  }
                }}
              />
              <SearchIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
            </div>
            {searchQuery.length > 0 && (() => {
              const available = filteredLieux.filter(l => !(newIntervention.locationIds || []).includes(l.id))
              return (
                <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                  {available.length > 0 ? available.map(l => (
                    <button key={l.id} onClick={() => { onUpdate({ locationIds: [...(newIntervention.locationIds || []), l.id] }); setSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                      <div>
                        <p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{l.name}</p>
                        <p className="text-xs text-slate-400 uppercase font-bold">{l.zone || l.special_type || (l.type === LocationType.LOCATIF ? 'Emplacement' : l.type === LocationType.EQUIPMENT ? 'Équipement' : 'Zone commune')}</p>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                    </button>
                  )) : (
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0"><SearchIcon className="w-4 h-4 text-slate-300" /></div>
                      <div><p className="text-sm font-bold text-slate-400">Aucun résultat</p><p className="text-xs text-slate-300 font-medium">"{searchQuery}" ne correspond à aucun lieu</p></div>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
          {(newIntervention.locationIds || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(newIntervention.locationIds || []).map(id => (
                <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-bold text-blue-800">
                  <LocationMarkerIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  {getLieuName(id)}
                  <button onClick={() => onUpdate({ locationIds: (newIntervention.locationIds || []).filter(lid => lid !== id) })} className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}>
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>

        {(newIntervention.locationIds || []).some(id => isLocatif(id)) && (
          <div className="bg-white p-5 rounded-3xl border border-slate-100 flex items-center justify-between shadow-sm">
            <div className="space-y-0.5 flex-1 pr-4">
              <p className="text-sm font-bold text-slate-800">Accord absence</p>
              <p className="text-xs text-slate-400">Autorisation d'entrer en absence client</p>
            </div>
            <button onClick={() => onUpdate({ customerPermission: !newIntervention.customerPermission })} className="w-12 h-6 flex-shrink-0 rounded-full transition-colors relative" style={{ backgroundImage: 'none', backgroundColor: newIntervention.customerPermission ? '#10b981' : '#e2e8f0' }}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${newIntervention.customerPermission ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block">Description</span>
          <textarea rows={3} value={newIntervention.description || ''} placeholder="Décrire la mission de ménage..." className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none shadow-sm" style={{ minHeight: '80px' }} onChange={e => onUpdate({ description: e.target.value, label: e.target.value.substring(0, 30) })} />
        </div>

        <section>
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest">Urgence</label>
          <div className="grid grid-cols-3 gap-2">
            {[Urgency.NORMAL, Urgency.IMPORTANT, Urgency.CRITICAL].map(u => {
              const isSelected = newIntervention.urgency === u
              const config = {
                [Urgency.NORMAL]: { icon: <CheckCircleIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-blue-50 border-blue-600 text-blue-700', activeBg: '#eff6ff' },
                [Urgency.IMPORTANT]: { icon: <ExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-orange-50 border-orange-600 text-orange-700', activeBg: '#fff7ed' },
                [Urgency.CRITICAL]: { icon: <ShieldExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-red-50 border-red-600 text-red-700', activeBg: '#fef2f2' },
              }
              const style = isSelected ? config[u].active : config[u].color
              return (
                <button key={u} onClick={() => onUpdate({ urgency: u })} className={`py-6 rounded-2xl border-2 font-bold text-xs uppercase tracking-wider flex flex-col items-center gap-2 ${style} ${!isSelected && 'hover:border-slate-200'}`} style={{ backgroundImage: 'none', backgroundColor: isSelected ? config[u].activeBg : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
                  <div className={isSelected ? 'scale-110 transition-transform' : 'opacity-60'}>{config[u].icon}</div>
                  {{ [Urgency.NORMAL]: 'Normal', [Urgency.IMPORTANT]: 'Important', [Urgency.CRITICAL]: 'Urgent' }[u]}
                </button>
              )
            })}
          </div>
        </section>

        <section className="relative z-20 space-y-4">
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest px-1">Assigner à</label>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => onUpdate({ assignedTo: { type: 'team', id: 'Équipe ménage', name: 'Équipe ménage' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-sm flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'border-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><SparklesIcon className="w-5 h-5" /></div>
              Équipe Ménage
            </button>
            <button onClick={() => onUpdate({ assignedTo: { type: 'team', id: 'Équipe technique', name: 'Équipe technique' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-sm flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'border-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><AdjustmentsIcon className="w-5 h-5" /></div>
              Équipe Technique
            </button>
          </div>
          <p className="text-center text-xs text-slate-500 font-bold uppercase tracking-widest">Ou choisir une personne</p>
          {!newIntervention.assignedTo ? (
            <div>
              <div className="relative">
                <input type="text" placeholder="Chercher équipe / collaborateur..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={assignSearchQuery} onChange={e => setAssignSearchQuery(e.target.value)} />
                <UserIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              </div>
              <div className="mt-2 bg-white border-2 border-slate-100 rounded-2xl shadow-xl max-h-48 overflow-y-auto">
                {[...teams.map(t => ({ assignedTo: { type: 'team', id: t, name: t }, displayName: t, displayType: 'Équipe' })), ...employees.map(e => ({ assignedTo: { type: 'employee', id: e.id, name: e.name }, displayName: e.name, displayType: 'Collaborateur' }))].filter(item => item.displayName.toLowerCase().includes(assignSearchQuery.toLowerCase())).map((item, idx) => (
                  <button key={idx} onClick={() => { onUpdate({ assignedTo: item.assignedTo }); setAssignSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                    <div><p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{item.displayName}</p><p className="text-xs text-slate-400 uppercase font-bold">{item.displayType}</p></div>
                    <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                  </button>
                ))}
                {[...teams.map(t => ({ displayName: t })), ...employees.map(e => ({ displayName: e.name }))].filter(item => item.displayName.toLowerCase().includes(assignSearchQuery.toLowerCase())).length === 0 && assignSearchQuery.length > 0 && (
                  <div className="p-6 text-center"><p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Désolé</p><p className="text-xs text-slate-300">Aucun résultat pour "{assignSearchQuery}"</p></div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl flex justify-between items-center transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 text-slate-500 rounded-xl flex items-center justify-center text-xs font-bold shadow-sm">{((newIntervention.assignedTo && newIntervention.assignedTo.name) || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                <div><p className="font-bold text-slate-800 text-sm">{newIntervention.assignedTo && newIntervention.assignedTo.name}</p><p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Assigné à</p></div>
              </div>
              <button onClick={() => onUpdate({ assignedTo: undefined })} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><XIcon className="w-6 h-6" /></button>
            </div>
          )}
        </section>

        <PhotoUploadSelector photos={newIntervention.photoUrls || []} onPhotosChange={(urls) => onUpdate({ photoUrls: urls })} />

        {(!(newIntervention.locationIds || []).length || !newIntervention.description) && (
          <p className="text-center text-xs text-slate-400 font-bold uppercase tracking-wider mt-2">{!(newIntervention.locationIds || []).length ? 'Sélectionnez au moins un lieu' : 'Décrivez la mission'} pour continuer</p>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-30 p-4 bg-white border-t border-slate-100 flex gap-3">
        <button
          type="button"
          onClick={() => onDetails('15 min')}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description || !newIntervention.assignedTo}
          className="w-2/5 py-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-2xl font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-xs uppercase tracking-wider active:scale-95"
          style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
        >
          <SparklesIcon className="w-4 h-4 text-amber-500" />
          Détails avancés
        </button>
        <button
          onClick={onCreate}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description || !newIntervention.assignedTo}
          className="flex-1 py-4 text-white rounded-2xl font-black transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm uppercase tracking-wider active:scale-95 shadow-md"
          style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
        >
          Créer l'intervention
        </button>
      </div>
    </div>
  )
}

// --- CreateTechView ---

function CreateTechView({ newIntervention, onUpdate, onCancel, onDetails, onCreate, lieux, getLieuName, isLocatif, teams, employees, footerLeft, searchQuery, setSearchQuery, assignSearchQuery, setAssignSearchQuery }) {
  const filteredLieux = lieux.filter(l => l.name.toLowerCase().includes(searchQuery.toLowerCase()))
  return (
    <div className="flex flex-col h-full bg-white relative">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
          <div>
            <p className="text-base font-black text-slate-900 leading-tight">Saisie rapide</p>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest leading-tight">Technique</p>
          </div>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>
      <div className="flex-1 p-6 space-y-6 overflow-y-auto pb-32">
        <section className="relative z-30">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block mb-2">Localisation</span>
          <div>
            <div className="relative">
              <input type="text" placeholder="MH, Sanitaires, Accueil..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchQuery.endsWith('*')) {
                    const prefix = searchQuery.slice(0, -1).toLowerCase()
                    const toAdd = lieux.filter(l => l.name.toLowerCase().startsWith(prefix) && !(newIntervention.locationIds || []).includes(l.id)).map(l => l.id)
                    if (toAdd.length > 0) onUpdate({ locationIds: [...(newIntervention.locationIds || []), ...toAdd] })
                    setSearchQuery('')
                  }
                }}
              />
              <SearchIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
            </div>
            {searchQuery.length > 0 && (() => {
              const available = filteredLieux.filter(l => !(newIntervention.locationIds || []).includes(l.id))
              return (
                <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                  {available.length > 0 ? available.map(l => (
                    <button key={l.id} onClick={() => { onUpdate({ locationIds: [...(newIntervention.locationIds || []), l.id] }); setSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                      <div><p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{l.name}</p><p className="text-xs text-slate-400 uppercase font-bold">{l.zone || l.special_type || (l.type === LocationType.LOCATIF ? 'Emplacement' : l.type === LocationType.EQUIPMENT ? 'Équipement' : 'Zone commune')}</p></div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                    </button>
                  )) : (
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0"><SearchIcon className="w-4 h-4 text-slate-300" /></div>
                      <div><p className="text-sm font-bold text-slate-400">Aucun résultat</p><p className="text-xs text-slate-300 font-medium">"{searchQuery}" ne correspond à aucun lieu</p></div>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
          {(newIntervention.locationIds || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(newIntervention.locationIds || []).map(id => (
                <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-bold text-blue-800">
                  <LocationMarkerIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />{getLieuName(id)}
                  <button onClick={() => onUpdate({ locationIds: (newIntervention.locationIds || []).filter(lid => lid !== id) })} className="ml-0.5 p-2 -m-1.5 text-blue-400 hover:text-red-500 transition-colors" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}><XIcon className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </section>

        {(newIntervention.locationIds || []).some(id => isLocatif(id)) && (
          <div className="bg-white p-5 rounded-3xl border border-slate-100 flex items-center justify-between shadow-sm">
            <div className="space-y-0.5 flex-1 pr-4"><p className="text-sm font-bold text-slate-800">Accord absence</p><p className="text-xs text-slate-400">Autorisation d'entrer en absence client</p></div>
            <button onClick={() => onUpdate({ customerPermission: !newIntervention.customerPermission })} className="w-12 h-6 flex-shrink-0 rounded-full transition-colors relative" style={{ backgroundImage: 'none', backgroundColor: newIntervention.customerPermission ? '#10b981' : '#e2e8f0' }}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${newIntervention.customerPermission ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block">Description / Précision</span>
          <textarea rows={3} value={newIntervention.description || ''} placeholder="Décrire simplement le problème..." className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none shadow-sm" style={{ minHeight: '80px' }} onChange={e => onUpdate({ description: e.target.value, label: e.target.value.substring(0, 30) })} />
        </div>

        <section>
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest">Urgence</label>
          <div className="grid grid-cols-3 gap-2">
            {[Urgency.NORMAL, Urgency.IMPORTANT, Urgency.CRITICAL].map(u => {
              const isSelected = newIntervention.urgency === u
              const config = {
                [Urgency.NORMAL]: { icon: <CheckCircleIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-blue-50 border-blue-600 text-blue-700', activeBg: '#eff6ff' },
                [Urgency.IMPORTANT]: { icon: <ExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-orange-50 border-orange-600 text-orange-700', activeBg: '#fff7ed' },
                [Urgency.CRITICAL]: { icon: <ShieldExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-red-50 border-red-600 text-red-700', activeBg: '#fef2f2' },
              }
              const style = isSelected ? config[u].active : config[u].color
              return (
                <button key={u} onClick={() => onUpdate({ urgency: u })} className={`py-6 rounded-2xl border-2 font-bold text-xs uppercase tracking-wider flex flex-col items-center gap-2 ${style} ${!isSelected && 'hover:border-slate-200'}`} style={{ backgroundImage: 'none', backgroundColor: isSelected ? config[u].activeBg : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
                  <div className={isSelected ? 'scale-110 transition-transform' : 'opacity-60'}>{config[u].icon}</div>
                  {{ [Urgency.NORMAL]: 'Normal', [Urgency.IMPORTANT]: 'Important', [Urgency.CRITICAL]: 'Urgent' }[u]}
                </button>
              )
            })}
          </div>
        </section>

        <section className="relative z-20 space-y-4">
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest px-1">Assigner à</label>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => onUpdate({ assignedTo: { type: 'team', id: 'Équipe technique', name: 'Équipe technique' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-xs flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'border-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><AdjustmentsIcon className="w-5 h-5" /></div>
              Équipe Technique
            </button>
            <button onClick={() => onUpdate({ assignedTo: { type: 'team', id: 'Équipe ménage', name: 'Équipe ménage' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-xs flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'border-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><SparklesIcon className="w-5 h-5" /></div>
              Équipe Ménage
            </button>
          </div>
          <p className="text-center text-xs text-slate-500 font-bold uppercase tracking-widest">Ou choisir une personne</p>
          {!newIntervention.assignedTo ? (
            <div>
              <div className="relative">
                <input type="text" placeholder="Chercher équipe / collaborateur..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={assignSearchQuery} onChange={e => setAssignSearchQuery(e.target.value)} />
                <UserIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              </div>
              <div className="mt-2 bg-white border-2 border-slate-100 rounded-2xl shadow-xl max-h-48 overflow-y-auto">
                {[...teams.map(t => ({ assignedTo: { type: 'team', id: t, name: t }, displayName: t, displayType: 'Équipe' })), ...employees.map(e => ({ assignedTo: { type: 'employee', id: e.id, name: e.name }, displayName: e.name, displayType: 'Collaborateur' }))].filter(item => item.displayName.toLowerCase().includes(assignSearchQuery.toLowerCase())).map((item, idx) => (
                  <button key={idx} onClick={() => { onUpdate({ assignedTo: item.assignedTo }); setAssignSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                    <div><p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{item.displayName}</p><p className="text-xs text-slate-400 uppercase font-bold">{item.displayType}</p></div>
                    <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                  </button>
                ))}
                {[...teams.map(t => ({ displayName: t })), ...employees.map(e => ({ displayName: e.name }))].filter(item => item.displayName.toLowerCase().includes(assignSearchQuery.toLowerCase())).length === 0 && assignSearchQuery.length > 0 && (
                  <div className="p-6 text-center"><p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Désolé</p><p className="text-xs text-slate-300">Aucun résultat pour "{assignSearchQuery}"</p></div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl flex justify-between items-center transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 text-slate-500 rounded-xl flex items-center justify-center text-xs font-bold shadow-sm">{((newIntervention.assignedTo && newIntervention.assignedTo.name) || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                <div><p className="font-bold text-slate-800 text-sm">{newIntervention.assignedTo && newIntervention.assignedTo.name}</p><p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Assigné à</p></div>
              </div>
              <button onClick={() => onUpdate({ assignedTo: undefined })} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><XIcon className="w-6 h-6" /></button>
            </div>
          )}
        </section>

        <PhotoUploadSelector photos={newIntervention.photoUrls || []} onPhotosChange={(urls) => onUpdate({ photoUrls: urls })} />

        {(!(newIntervention.locationIds || []).length || !newIntervention.description) && (
          <p className="text-center text-xs text-slate-400 font-bold uppercase tracking-wider mt-2">{!(newIntervention.locationIds || []).length ? 'Sélectionnez au moins un lieu' : 'Décrivez le problème'} pour continuer</p>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-30 p-4 bg-white border-t border-slate-100 flex gap-3">
        <button
          type="button"
          onClick={() => onDetails('5 min')}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description || !newIntervention.assignedTo}
          className="w-2/5 py-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-2xl font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-xs uppercase tracking-wider active:scale-95"
          style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
        >
          <SparklesIcon className="w-4 h-4 text-amber-500" />
          Détails avancés
        </button>
        <button
          onClick={onCreate}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description || !newIntervention.assignedTo}
          className="flex-1 py-4 text-white rounded-2xl font-black transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm uppercase tracking-wider active:scale-95 shadow-md"
          style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
        >
          Créer l'intervention
        </button>
      </div>
    </div>
  )
}

// --- CreateDiversView ---

function CreateDiversView({ newIntervention, onUpdate, onCancel, onDetails, onCreate, lieux, getLieuName, isLocatif, teams, employees, footerLeft, searchQuery, setSearchQuery, assignSearchQuery, setAssignSearchQuery }) {
  const filteredLieux = lieux.filter(l => l.name.toLowerCase().includes(searchQuery.toLowerCase()))
  return (
    <div className="flex flex-col h-full bg-white relative">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
          <div>
            <p className="text-base font-black text-slate-900 leading-tight">Saisie rapide</p>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest leading-tight">Divers</p>
          </div>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>
      <div className="flex-1 p-6 space-y-6 overflow-y-auto pb-32">
        <section className="relative z-30">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block mb-2">Localisation</span>
          <div>
            <div className="relative">
              <input type="text" placeholder="Accueil, Parking, S001..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchQuery.endsWith('*')) {
                    const prefix = searchQuery.slice(0, -1).toLowerCase()
                    const toAdd = lieux.filter(l => l.name.toLowerCase().startsWith(prefix) && !(newIntervention.locationIds || []).includes(l.id)).map(l => l.id)
                    if (toAdd.length > 0) onUpdate({ locationIds: [...(newIntervention.locationIds || []), ...toAdd] })
                    setSearchQuery('')
                  }
                }}
              />
              <SearchIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
            </div>
            {searchQuery.length > 0 && (() => {
              const available = filteredLieux.filter(l => !(newIntervention.locationIds || []).includes(l.id))
              return (
                <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                  {available.length > 0 ? available.map(l => (
                    <button key={l.id} onClick={() => { onUpdate({ locationIds: [...(newIntervention.locationIds || []), l.id] }); setSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                      <div><p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{l.name}</p><p className="text-xs text-slate-400 uppercase font-bold">{l.zone || l.special_type || (l.type === LocationType.LOCATIF ? 'Emplacement' : l.type === LocationType.EQUIPMENT ? 'Équipement' : 'Zone commune')}</p></div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                    </button>
                  )) : (
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0"><SearchIcon className="w-4 h-4 text-slate-300" /></div>
                      <div><p className="text-sm font-bold text-slate-400">Aucun résultat</p><p className="text-xs text-slate-300 font-medium">"{searchQuery}" ne correspond à aucun lieu</p></div>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
          {(newIntervention.locationIds || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(newIntervention.locationIds || []).map(id => (
                <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-bold text-blue-800">
                  <LocationMarkerIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />{getLieuName(id)}
                  <button onClick={() => onUpdate({ locationIds: (newIntervention.locationIds || []).filter(lid => lid !== id) })} className="ml-0.5 p-2 -m-1.5 text-blue-400 hover:text-red-500 transition-colors" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}><XIcon className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </section>

        {(newIntervention.locationIds || []).some(id => isLocatif(id)) && (
          <div className="bg-white p-5 rounded-3xl border border-slate-100 flex items-center justify-between shadow-sm">
            <div className="space-y-0.5 flex-1 pr-4"><p className="text-sm font-bold text-slate-800">Accord absence</p><p className="text-xs text-slate-400">Autorisation d'entrer en absence client</p></div>
            <button onClick={() => onUpdate({ customerPermission: !newIntervention.customerPermission })} className="w-12 h-6 flex-shrink-0 rounded-full transition-colors relative" style={{ backgroundImage: 'none', backgroundColor: newIntervention.customerPermission ? '#10b981' : '#e2e8f0' }}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${newIntervention.customerPermission ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-sm uppercase font-black text-slate-800 tracking-widest block">Description / Précision</span>
          <textarea rows={3} value={newIntervention.description || ''} placeholder="Préciser la demande..." className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none shadow-sm" style={{ minHeight: '80px' }} onChange={e => onUpdate({ description: e.target.value, label: e.target.value.substring(0, 30) })} />
        </div>

        <section>
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest">Urgence</label>
          <div className="grid grid-cols-3 gap-2">
            {[Urgency.NORMAL, Urgency.IMPORTANT, Urgency.CRITICAL].map(u => {
              const isSelected = newIntervention.urgency === u
              const config = {
                [Urgency.NORMAL]: { icon: <CheckCircleIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-blue-50 border-blue-600 text-blue-700', activeBg: '#eff6ff' },
                [Urgency.IMPORTANT]: { icon: <ExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-orange-50 border-orange-600 text-orange-700', activeBg: '#fff7ed' },
                [Urgency.CRITICAL]: { icon: <ShieldExclamationIcon className="w-5 h-5"/>, color: 'bg-white border-slate-200 text-slate-500', active: 'bg-red-50 border-red-600 text-red-700', activeBg: '#fef2f2' },
              }
              const style = isSelected ? config[u].active : config[u].color
              return (
                <button key={u} onClick={() => onUpdate({ urgency: u })} className={`py-6 rounded-2xl border-2 font-bold text-xs uppercase tracking-wider flex flex-col items-center gap-2 ${style} ${!isSelected && 'hover:border-slate-200'}`} style={{ backgroundImage: 'none', backgroundColor: isSelected ? config[u].activeBg : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
                  <div className={isSelected ? 'scale-110 transition-transform' : 'opacity-60'}>{config[u].icon}</div>
                  {{ [Urgency.NORMAL]: 'Normal', [Urgency.IMPORTANT]: 'Important', [Urgency.CRITICAL]: 'Urgent' }[u]}
                </button>
              )
            })}
          </div>
        </section>

        <section className="relative z-20 space-y-4">
          <label className="text-sm uppercase font-black text-slate-800 mb-2 block tracking-widest px-1">Assigner à <span className="normal-case font-medium text-slate-300">(optionnel)</span></label>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => onUpdate({ assignedTo: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? undefined : { type: 'team', id: 'Équipe ménage', name: 'Équipe ménage' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-sm flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-100 text-slate-500'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe ménage') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><SparklesIcon className="w-5 h-5" /></div>
              Équipe Ménage
            </button>
            <button onClick={() => onUpdate({ assignedTo: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? undefined : { type: 'team', id: 'Équipe technique', name: 'Équipe technique' } })} className={`py-4 px-2 rounded-2xl border-2 font-bold text-sm flex flex-col items-center gap-2 ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'border-blue-600 text-white shadow-lg shadow-blue-100' : 'border-slate-100 text-slate-500'}`} style={{ backgroundImage: 'none', backgroundColor: (newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? '#2563eb' : '#ffffff', transition: 'transform 0.15s ease, background-color 0.15s ease' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.93)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 120) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(newIntervention.assignedTo && newIntervention.assignedTo.id === 'Équipe technique') ? 'bg-white bg-opacity-20' : 'bg-blue-50 text-blue-600 shadow-sm'}`}><AdjustmentsIcon className="w-5 h-5" /></div>
              Équipe Technique
            </button>
          </div>
          <p className="text-center text-xs text-slate-500 font-bold uppercase tracking-widest">Ou choisir une personne</p>
          {!newIntervention.assignedTo ? (
            <div>
              <div className="relative">
                <input type="text" placeholder="Chercher équipe / collaborateur..." className="browser-default block w-full py-4 pl-4 pr-12 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:border-blue-500 shadow-sm transition-all" style={{ outline: 'none', boxShadow: 'none' }} value={assignSearchQuery} onChange={e => setAssignSearchQuery(e.target.value)} />
                <UserIcon className="absolute right-4 inset-y-0 my-auto w-5 h-5 text-slate-300 pointer-events-none" />
              </div>
              <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-sm max-h-48 overflow-y-auto">
                {[...teams.map(t => ({ assignedTo: { type: 'team', id: t, name: t }, displayName: t, displayType: 'Équipe' })), ...employees.map(e => ({ assignedTo: { type: 'employee', id: e.id, name: e.name }, displayName: e.name, displayType: 'Collaborateur' }))].filter(item => item.displayName.toLowerCase().includes(assignSearchQuery.toLowerCase())).map((item, idx) => (
                  <button key={idx} onClick={() => { onUpdate({ assignedTo: item.assignedTo }); setAssignSearchQuery('') }} className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 flex justify-between items-center group">
                    <div><p className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors">{item.displayName}</p><p className="text-xs text-slate-400 uppercase font-bold">{item.displayType}</p></div>
                    <ChevronRightIcon className="w-4 h-4 text-slate-200 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl flex justify-between items-center transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 text-slate-500 rounded-xl flex items-center justify-center text-xs font-bold shadow-sm">{((newIntervention.assignedTo && newIntervention.assignedTo.name) || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</div>
                <div><p className="font-bold text-slate-800 text-sm">{newIntervention.assignedTo && newIntervention.assignedTo.name}</p><p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Assigné à</p></div>
              </div>
              <button onClick={() => onUpdate({ assignedTo: undefined })} className="p-2 text-slate-300 hover:text-red-500 transition-colors" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}><XIcon className="w-6 h-6" /></button>
            </div>
          )}
        </section>

      </div>

      <div className="absolute bottom-0 left-0 right-0 z-30 p-4 bg-white border-t border-slate-100 flex gap-3">
        <button
          type="button"
          onClick={() => onDetails('15 min')}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description}
          className="w-2/5 py-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-2xl font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 text-xs uppercase tracking-wider active:scale-95"
          style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
        >
          <SparklesIcon className="w-4 h-4 text-amber-500" />
          Détails avancés
        </button>
        <button
          onClick={onCreate}
          disabled={!(newIntervention.locationIds || []).length || !newIntervention.description}
          className="flex-1 py-4 text-white rounded-2xl font-black transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm uppercase tracking-wider active:scale-95 shadow-md"
          style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
        >
          Créer l'intervention
        </button>
      </div>
    </div>
  )
}

// --- ReviewView ---

function ReviewView({ newIntervention, onUpdate, onCancel, onConfirm, getLieuName, footerLeft, isReviewEditOpen, isReviewEditClosing, closeReviewEdit, currentUserName }) {
  return (
    <>
    <div className="flex flex-col h-full bg-white overflow-y-auto pb-24">
      <header className="px-6 pt-12 pb-5 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
          <p className="text-base font-black text-slate-900">Récapitulatif</p>
        </div>
        <button onClick={onCancel} className="p-2 text-indigo-500 hover:text-indigo-700 active:scale-95 transition-all font-bold text-xs uppercase tracking-widest">Annuler</button>
      </header>

      <div className="p-6 space-y-4">
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-4 py-3 border-b border-slate-50">
            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-lg italic shrink-0"><span role="img" aria-label="lieu">📍</span></div>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-1">{(newIntervention.locationIds || []).length > 1 ? 'Lieux' : 'Lieu'}</p>
              <div className="flex flex-wrap gap-1.5">
                {(newIntervention.locationIds || []).map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-bold text-blue-800">{getLieuName(id)}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 py-3 border-b border-slate-50">
            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-lg italic"><span role="img" aria-label="assignation">📋</span></div>
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold tracking-widest">Assignation</p>
              <p className="font-bold text-slate-800">{(newIntervention.assignedTo && newIntervention.assignedTo.name) || newIntervention.assignedTo}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 py-3 border-b border-slate-50">
            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-lg italic"><span role="img" aria-label="urgence">⚡</span></div>
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold tracking-widest">Urgence</p>
              <p className={`font-bold ${newIntervention.urgency === Urgency.CRITICAL ? 'text-red-600' : 'text-blue-600'}`}>{newIntervention.urgency}</p>
            </div>
          </div>
          <div className="py-3">
            <p className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-2">Problème constaté</p>
            <p className="text-sm text-slate-700 bg-slate-50 p-4 rounded-2xl leading-relaxed italic border border-slate-100">"{newIntervention.description || 'Pas de description détaillée'}"</p>
          </div>
          {newIntervention.photoUrls && newIntervention.photoUrls.length > 0 && (
            <div className="py-3">
              <p className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-2">Photos jointes</p>
              <div className="grid grid-cols-2 gap-2">
                {newIntervention.photoUrls.map((url, idx) => (
                  <div key={idx} className="w-full rounded-2xl overflow-hidden border-2 border-slate-100" style={{ aspectRatio: '1 / 1' }}>
                    <img src={thumbUrl(url)} alt={`Intervention ${idx}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-8">
          <button onClick={onConfirm} className="w-full py-5 bg-blue-600 text-white rounded-3xl font-bold shadow-xl shadow-blue-200 active:scale-95 transition-all">Confirmer & Créer</button>
          <button onClick={closeReviewEdit} className="w-full py-4 text-slate-400 font-bold text-sm mt-2" style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}>Modifier</button>
        </div>
      </div>
    </div>

    {(isReviewEditOpen || isReviewEditClosing) && (
      <>
        <div
          onClick={closeReviewEdit}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', zIndex: 40, animation: isReviewEditClosing ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards' : 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
        />
        <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-50 transition-all duration-300`}>
          <div className="max-w-xl mx-auto" style={{ backgroundColor: '#ffffff', borderRadius: '24px 24px 0 0', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: isReviewEditClosing ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards' : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)', boxShadow: '0 -8px 32px rgba(15,23,42,0.15)' }}>
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              <TicketEditView
                ticket={newIntervention}
                currentUserName={currentUserName}
                onSave={(updatedTicket) => { const { history, ...rest } = updatedTicket; onUpdate(rest); closeReviewEdit() }}
                onBack={closeReviewEdit}
              />
            </div>
          </div>
        </div>
      </>
    )}
    </>
  )
}

// --- ConfirmationView ---

function ConfirmationView({ confirmAnimated, onHome, onNewTicket }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-emerald-600 text-white overflow-y-auto">
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-24 h-24 bg-white bg-opacity-20 backdrop-blur-md rounded-full flex items-center justify-center mb-8 shadow-2xl" style={{ opacity: confirmAnimated ? 1 : 0, transform: confirmAnimated ? 'scale(1)' : 'scale(0.4)', transition: 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)', transitionDelay: '0ms' }}>
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-emerald-600">
            <CheckCircleIcon className="w-10 h-10" />
          </div>
        </div>
        <h3 className="text-3xl font-black mt-0 mb-3 tracking-tight" style={{ opacity: confirmAnimated ? 1 : 0, transform: confirmAnimated ? 'translateY(0)' : 'translateY(14px)', transition: 'opacity 0.5s ease, transform 0.5s ease', transitionDelay: '160ms' }}>C'est envoyé !</h3>
        <p className="text-sm text-emerald-50 mb-10 leading-relaxed font-medium" style={{ maxWidth: 240, marginLeft: 'auto', marginRight: 'auto', opacity: confirmAnimated ? 0.9 : 0, transform: confirmAnimated ? 'translateY(0)' : 'translateY(14px)', transition: 'opacity 0.5s ease, transform 0.5s ease', transitionDelay: '270ms' }}>L'intervention a été créée et l'équipe a été notifiée.</p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <button onClick={onHome} className="w-full py-5 bg-white text-emerald-700 rounded-3xl font-extrabold shadow-xl" style={{ backgroundImage: 'none', opacity: confirmAnimated ? 1 : 0, transform: confirmAnimated ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.5s ease, transform 0.5s ease', transitionDelay: '390ms' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.96)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 100) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>Retour à l'accueil</button>
          <button onClick={onNewTicket} className="w-full py-4 text-white rounded-3xl font-bold text-sm" style={{ backgroundImage: 'none', backgroundColor: 'rgba(6, 78, 59, 0.45)', opacity: confirmAnimated ? 1 : 0, transform: confirmAnimated ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.5s ease, transform 0.5s ease', transitionDelay: '470ms' }} onPointerDown={e => { e.currentTarget.style.transform = 'scale(0.96)' }} onPointerUp={e => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'scale(1)' }, 100) }} onPointerLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}>Nouveau ticket</button>
        </div>
      </div>
    </div>
  )
}

// --- Main Application ---


export default function InterventionsApp({ user, establishment, history, match, sidebarCollapsed }) {
  const userId = user && user._id ? user._id.toString() : null
  const footerLeft = sidebarCollapsed ? '' : 'lg:left-56'

  const [view, setView] = useState('HOME');
  const [tickets, setTickets] = useState([]);
  const [filter1, setFilter1] = useState(() => {
    const team = (user && user.team) ? user.team.toLowerCase() : ''
    if (team.includes('technique')) return 'Technique'
    if (team.includes('ménage')) return 'Ménage'
    return null
  });
  const [filter2, setFilter2] = useState(null);
  const [filter3, setFilter3] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketsLoaded, setTicketsLoaded] = useState(false); // la liste du serveur a répondu (une adresse /interventions/<id> peut alors être résolue)
  const [isReadOnlyDetailsOpen, setIsReadOnlyDetailsOpen] = useState(false);
  const [isEditBottomSheetOpen, setIsEditBottomSheetOpen] = useState(false);
  const [isReadOnlyClosing, setIsReadOnlyClosing] = useState(false);
  const [isEditClosing, setIsEditClosing] = useState(false);
  const [isReviewEditOpen, setIsReviewEditOpen] = useState(false);
  const [isReviewEditClosing, setIsReviewEditClosing] = useState(false);

  const userName = user ? ((user.firstname || '') + ' ' + (user.lastname || '')).trim() : ''
  const userTeam = user ? (user.team || '') : ''
  const canWrite = !!(user && user.permissions && (user.permissions.interventions & 2))
  const showDashboardButton = hasOtherModules(user ? user.permissions : null, 'interventions')

  // Modèle de permissions (décision Alexandra 05/07) :
  // - lecture seule = équipier terrain : il AGIT sur les tickets assignés à lui
  //   ou à son équipe (changer le statut, publier observations/photos), mais ne
  //   façonne pas les tickets (ni création, ni édition description/lieu,
  //   ni réassignation, ni suppression — réservés à l'écriture)
  const canActOnTicket = useCallback((ticket) => {
    if (canWrite)
      return true
    if (!ticket || !ticket.assignedTo)
      return false
    const a = ticket.assignedTo
    if (a.type === 'employee')
      return a.id === userId || (!!userName && a.name === userName)
    if (a.type === 'team')
    {
      if (!userTeam || !a.name)
        return false
      const mine = userTeam.toLowerCase()
      const assigned = a.name.toLowerCase()
      return assigned.includes(mine) || mine.includes(assigned)
    }
    return false
  }, [canWrite, userId, userName, userTeam])

  // Resync silencieux de l'abonnement push de ce navigateur : Chrome fait
  // tourner les abonnements sans prévenir, l'upsert par clé auth côté serveur
  // rend l'opération idempotente. L'activation vit dans Mon compte (/parametres).
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window))
      return
    navigator.serviceWorker.getRegistration()
    .then(registration => {
      if (!registration)
        return
      return registration.pushManager.getSubscription()
      .then(subscription => {
        if (subscription && userId)
        {
          fetch(connexion.connect.url + 'dashboard/push/subscribe', {
            method: 'POST',
            body: JSON.stringify({ user: userId, subscription: subscription })
          }).catch(() => {})
        }
      })
    })
    .catch(() => {})
  }, [userId])

  // Deep-link ?ticket=<id> (clic sur une notification push) : capturé une seule
  // fois au premier rendu, appliqué dès que la liste est chargée. Le ref reste
  // posé jusqu'à ce que la sélection soit effective : il suspend l'auto-sélection
  // desktop, qui sinon écrase la sélection dans le même cycle d'effets.
  // Lien /interventions/<id> : même traitement (l'identifiant est dans l'adresse, voir routeTicketId).
  const routeTicketId = (match && match.params && match.params.id) ? match.params.id : null
  const deepLinkTicketId = useRef(undefined)
  if (deepLinkTicketId.current === undefined)
  {
    const search = (history && history.location && history.location.search) || ''
    deepLinkTicketId.current = new URLSearchParams(search).get('ticket') || routeTicketId
  }

  // Lien ?lieu=<id> (fiche d'un emplacement, page Emplacements) : ouvre la création d'une
  // intervention avec ce lieu déjà choisi. Capturé une seule fois, consommé au choix du type
  // (ou oublié si l'on annule), pour ne pas pré-remplir une création suivante.
  const presetLocationId = useRef(undefined)
  if (presetLocationId.current === undefined)
  {
    const search = (history && history.location && history.location.search) || ''
    presetLocationId.current = new URLSearchParams(search).get('lieu')
  }

  const takePresetLocation = () => {
    const id = presetLocationId.current
    presetLocationId.current = null
    return id ? { locationIds: [id] } : {}
  }

  useEffect(() => {
    if (!presetLocationId.current)
      return
    setView('SELECT_TYPE')
    if (history && history.replace)
      history.replace('/interventions')
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deepLinkTicketId.current || tickets.length === 0)
      return
    const found = tickets.find(t => t.id === deepLinkTicketId.current)
    if (found)
    {
      setSelectedTicketId(found.id)
      setView('DETAILS')
    }
    else
      deepLinkTicketId.current = null
    // L'adresse de la fiche est déjà la bonne quand on arrive par /interventions/<id> : on ne la remplace
    // (en gardant son state, qui porte la page d'origine) que pour un lien ?ticket=<id> ou un identifiant inconnu.
    const target = found ? '/interventions/' + found.id : '/interventions'
    if (history && history.replace && history.location && history.location.pathname !== target)
      history.replace(target, history.location.state)
  }, [tickets])

  // L'adresse décide quelle intervention est ouverte (mobile : la fiche, desktop : le panneau) :
  //   /interventions/<id> ouvre l'intervention (lien depuis un tour de terrain, notification, retour du navigateur) ;
  //   /interventions referme la fiche. Ouvrir une intervention dans l'app ajoute son adresse à l'historique,
  //   la refermer revient à /interventions (ou à la page d'où l'on venait, state.from).
  const routeApplied = useRef(null) // identifiant de l'adresse déjà appliqué à l'écran
  const pushedDetail = useRef(false) // l'adresse de la fiche ouverte a été ajoutée par l'app (retour possible)

  useEffect(() => {
    if (!routeTicketId)
    {
      if (routeApplied.current)
      {
        routeApplied.current = null
        pushedDetail.current = false
        setView(current => (current === 'DETAILS' ? 'HOME' : current))
      }
      return
    }
    if (!ticketsLoaded)
      return
    const found = tickets.find(t => t.id === routeTicketId)
    if (!found)
    {
      deepLinkTicketId.current = null
      if (history && history.replace)
        history.replace('/interventions')
      return
    }
    routeApplied.current = found.id
    deepLinkTicketId.current = null
    setSelectedTicketId(found.id)
    setView('DETAILS')
  }, [routeTicketId, tickets, ticketsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== 'DETAILS' || !selectedTicketId || !history || !history.push)
      return
    if (routeTicketId === selectedTicketId)
    {
      routeApplied.current = selectedTicketId
      return
    }
    if (deepLinkTicketId.current)
      return
    pushedDetail.current = true
    routeApplied.current = selectedTicketId
    // Ouverte depuis un tour : state.from garde la page d'origine, state.depth le nombre d'entrées à remonter pour y revenir
    const current_state = history.location ? history.location.state : undefined
    const next_state = (current_state && current_state.from) ? Object.assign({}, current_state, { depth: (current_state.depth || 1) + 1 }) : current_state
    history.push('/interventions/' + selectedTicketId, next_state)
  }, [view, selectedTicketId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === 'DETAILS' || !routeApplied.current || !history)
      return
    routeApplied.current = null
    if (pushedDetail.current && history.goBack)
    {
      pushedDetail.current = false
      history.goBack()
    }
    else if (history.replace)
      history.replace('/interventions')
  }, [view]) // eslint-disable-line react-hooks/exhaustive-deps

  // Onglet Analyse (desktop, pilotage direction)
  const [desktopView, setDesktopView] = useState('board') // 'board' | 'analytics'
  const [analyticsDays, setAnalyticsDays] = useState(30)
  const [analytics, setAnalytics] = useState(null)
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false)

  useEffect(() => {
    if (desktopView !== 'analytics')
      return
    setIsAnalyticsLoading(true)
    fetch(connexion.connect.url + 'dashboard/interventions/analytics?days=' + analyticsDays)
      .then(r => r.json())
      .then(data => {
        if (data !== false)
          setAnalytics(data)
        setIsAnalyticsLoading(false)
      })
      .catch(() => setIsAnalyticsLoading(false))
  }, [desktopView, analyticsDays])

  // Panneau Paramètres des interventions (desktop) — réglages d'établissement
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isSettingsClosing, setIsSettingsClosing] = useState(false)
  const [digestConfig, setDigestConfig] = useState({ enabled: false, frequency: 'daily', recipients: [] })
  const [autoMenageConfig, setAutoMenageConfig] = useState({ enabled: false })
  const [pushConfig, setPushConfig] = useState({ enabled: true })
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [settingsSaveState, setSettingsSaveState] = useState(null) // null | 'saving' | 'saved' | 'error'
  const [newRecipientLabel, setNewRecipientLabel] = useState('')
  const [newRecipientEmail, setNewRecipientEmail] = useState('')

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true)
    setSettingsSaveState(null)
    fetch(connexion.connect.url + 'dashboard/interventions/config')
      .then(r => r.json())
      .then(config => {
        if (config && config.digest)
          setDigestConfig({
            enabled: config.digest.enabled === true,
            frequency: config.digest.frequency === 'weekly' ? 'weekly' : 'daily',
            recipients: Array.isArray(config.digest.recipients) ? config.digest.recipients : []
          })
        if (config && config.auto_menage)
          setAutoMenageConfig({ enabled: config.auto_menage.enabled === true })
        if (config && config.push)
          setPushConfig({ enabled: config.push.enabled !== false })
        setSettingsLoaded(true)
      })
      .catch(() => setSettingsLoaded(true))
  }, [])

  const closeSettings = useCallback(() => {
    setIsSettingsClosing(true)
    setTimeout(() => {
      setIsSettingsClosing(false)
      setIsSettingsOpen(false)
    }, 260)
  }, [])

  const saveDigestConfig = useCallback((next) => {
    setDigestConfig(next)
    setSettingsSaveState('saving')
    fetch(connexion.connect.url + 'dashboard/interventions/config/update', {
      method: 'POST',
      body: JSON.stringify({ key: 'digest', value: next })
    })
      .then(r => r.json())
      .then(ret => setSettingsSaveState(ret === false ? 'error' : 'saved'))
      .catch(() => setSettingsSaveState('error'))
  }, [])

  const saveAutoMenageConfig = useCallback((next) => {
    setAutoMenageConfig(next)
    setSettingsSaveState('saving')
    fetch(connexion.connect.url + 'dashboard/interventions/config/update', {
      method: 'POST',
      body: JSON.stringify({ key: 'auto_menage', value: next })
    })
      .then(r => r.json())
      .then(ret => setSettingsSaveState(ret === false ? 'error' : 'saved'))
      .catch(() => setSettingsSaveState('error'))
  }, [])

  const savePushConfig = useCallback((next) => {
    setPushConfig(next)
    setSettingsSaveState('saving')
    fetch(connexion.connect.url + 'dashboard/interventions/config/update', {
      method: 'POST',
      body: JSON.stringify({ key: 'push', value: next })
    })
      .then(r => r.json())
      .then(ret => setSettingsSaveState(ret === false ? 'error' : 'saved'))
      .catch(() => setSettingsSaveState('error'))
  }, [])

  const userInitials = useMemo(() => {
    return userName
      .split(' ')
      .filter(Boolean)
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2) || 'U';
  }, [userName]);
  
  const [isDetailClosing, setIsDetailClosing] = useState(false);
  const [isEditOverlayOpen, setIsEditOverlayOpen] = useState(false);
  const [isEditOverlayClosing, setIsEditOverlayClosing] = useState(false);

  // Worker feedback states for ticket interaction
  const [workerComment, setWorkerComment] = useState('');
  const [workerPhotos, setWorkerPhotos] = useState([]);
  const [initialWorkerComment, setInitialWorkerComment] = useState('');
  const [initialWorkerPhotos, setInitialWorkerPhotos] = useState([]);
  const [isWorkerPhotosUploading, setIsWorkerPhotosUploading] = useState(false);
  const [isFeedbackSheetOpen, setIsFeedbackSheetOpen] = useState(false);
  const [isPauseSheetOpen, setIsPauseSheetOpen] = useState(false);
  const [isPauseSheetClosing, setIsPauseSheetClosing] = useState(false);
  const [isFeedbackClosing, setIsFeedbackClosing] = useState(false);
  const [isObservationsDrawerOpen, setIsObservationsDrawerOpen] = useState(false);
  const [editingMemoId, setEditingMemoId] = useState(null);
  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false);
  const [isConfirmDiscardClosing, setIsConfirmDiscardClosing] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  
  // Loading state — passer à true lors du fetch backend
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(() => getOfflineQueue().length);
  const flushQueueRef = useRef(null);

  // Creation/Edit States
  const EMPTY_INTERVENTION = { isCustomerRequest: false, photoUrls: [], customerPermission: false, locationIds: [] };
  const [newIntervention, setNewIntervention] = useState(EMPTY_INTERVENTION);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignSearchQuery, setAssignSearchQuery] = useState('');
  const [confirmAnimated, setConfirmAnimated] = useState(false);
  const [dashboardUsers, setDashboardUsers] = useState(null);
  const [pitches, setPitches] = useState(null);

  // Desktop states
  const [ticketSearchQuery, setTicketSearchQuery] = useState('');
  const [desktopFilters, setDesktopFilters] = useState(NO_DESKTOP_FILTERS);
  const [activeQuickAction, setActiveQuickAction] = useState(null)
  const [isQuickActionClosing, setIsQuickActionClosing] = useState(false)
  const [quickActionToast, setQuickActionToast] = useState(null)
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)
  const quickActionToastTimer = useRef(null)

  // Referme le panneau d'action rapide et replie l'historique quand on change de ticket
  useEffect(() => {
    setActiveQuickAction(null)
    setIsQuickActionClosing(false)
    setIsHistoryExpanded(false)
  }, [selectedTicketId])

  const closeQuickAction = () => {
    if (isQuickActionClosing)
      return ;
    setIsQuickActionClosing(true)
    setTimeout(() => {
      setIsQuickActionClosing(false)
      setActiveQuickAction(null)
    }, 220)
  }

  const showQuickActionToast = (message) => {
    setQuickActionToast(message)
    if (quickActionToastTimer.current)
      clearTimeout(quickActionToastTimer.current)
    quickActionToastTimer.current = setTimeout(() => setQuickActionToast(null), 2500)
  }

  // Undo de clôture — fenêtre de 8 s sur l'écran de succès
  const [undoDoneInfo, setUndoDoneInfo] = useState(null)
  const [undoCountdown, setUndoCountdown] = useState(0)

  useEffect(() => {
    if (!undoDoneInfo)
      return ;
    setUndoCountdown(8)
    const interval = setInterval(() => {
      setUndoCountdown(prev => {
        if (prev <= 1)
        {
          clearInterval(interval)
          setUndoDoneInfo(null)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [undoDoneInfo])

  const handleUndoDone = () => {
    if (!undoDoneInfo)
      return ;
    const ticket = tickets.find(t => t.id === undoDoneInfo.ticketId)
    if (!ticket)
      return ;
    const event = {
      id: Math.random().toString(36).substr(2, 5),
      type: 'status_change',
      user: userName,
      timestamp: new Date().toISOString(),
      from: Status.DONE,
      to: undoDoneInfo.previousStatus,
      details: 'Clôture annulée'
    }
    const updated = { ...ticket, status: undoDoneInfo.previousStatus, history: [...(ticket.history || []), event] }
    setTickets(prev => prev.map(t => t.id === updated.id ? updated : t))
    syncTicket(updated)
    setUndoDoneInfo(null)
    setSelectedTicketId(updated.id)
    setView('DETAILS')
  }
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [isEditPanelClosing, setIsEditPanelClosing] = useState(false)
  const [createDrawerSearch, setCreateDrawerSearch] = useState('')
  const [isCreateLieuDropdownOpen, setIsCreateLieuDropdownOpen] = useState(false)
  const [isCreateDetailsOpen, setIsCreateDetailsOpen] = useState(false)
  const [isAssignedToDropdownOpen, setIsAssignedToDropdownOpen] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [deletingTicketId, setDeletingTicketId] = useState(null)
  const [lightboxUrl, setLightboxUrl] = useState(null)
  const lightboxFullScreen = !!lightboxUrl && typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 767px)').matches
  const [lastSeenTimestamp] = useState(() => {
    const saved = localStorage.getItem('interventions_lastSeenAt')
    const now = Date.now()
    if (!saved)
    {
      const defaultTime = now - 12 * 60 * 60 * 1000
      localStorage.setItem('interventions_lastSeenAt', new Date(defaultTime).toISOString())
      return defaultTime
    }
    return new Date(saved).getTime()
  });

  useEffect(() => {
    fetch(connexion.connect.url + 'dashboard/users/list')
      .then(res => res.json())
      .then(users => { if (Array.isArray(users)) setDashboardUsers(users) })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(connexion.connect.url + 'dashboard/pitches/list')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setPitches(data) })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const teams = useMemo(() => {
    const seen = new Set()
    const result = []
    ;['Direction', 'Réception', 'Restauration'].forEach(t => { seen.add(t); result.push(t) })
    ;(dashboardUsers || []).forEach(u => {
      if (u.team && !seen.has(u.team))
      {
        seen.add(u.team)
        result.push(u.team)
      }
    })
    return result
  }, [dashboardUsers])

  const employees = useMemo(() => {
    if (!dashboardUsers || dashboardUsers.length === 0) return []
    return dashboardUsers.map(u => ({
      id: u._id ? u._id.toString() : (u.email || ''),
      name: ((u.firstname || '') + ' ' + (u.lastname || '')).trim() || u.email || ''
    })).filter(e => e.name)
  }, [dashboardUsers])

  // Résout le nom + prénom du créateur d'un ticket à partir de son _id (created_by)
  const getCreatorName = useCallback((createdBy) => {
    if (!createdBy || /^0+$/.test(createdBy))
      return 'PocketCamp'
    const match = employees.find(e => e.id === createdBy)
    // Id inconnu (compte supprimé, données importées) : ne jamais afficher l'id brut
    return (match && match.name) || 'Système'
  }, [employees])

  // Créateur affiché : prénom + nom du vacancier (résolu côté serveur via created_user_by)
  // si le ticket provient d'une conversation, sinon le membre du staff (created_by).
  const getTicketCreator = useCallback((ticket) => {
    if (ticket && ticket.createdUserName)
      return ticket.createdUserName
    return getCreatorName(ticket ? ticket.createdBy : null)
  }, [getCreatorName])

  // const LIEUX_COMMUNS = [
  //   { id: 'zone_restaurant',    name: 'Restaurant',             zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_piscine',       name: 'Piscine',                zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_poubelle',      name: 'Local poubelle',         zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_animation',     name: 'Salle animation',        zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_sanitaires',    name: 'Sanitaires',             zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_pickleball',    name: 'Terrain de pickleball',  zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_padel',         name: 'Terrain de padel',       zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_petanque',      name: 'Terrain de pétanque',    zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_jeux',          name: 'Aire de jeux',           zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_foot',          name: 'Terrain de foot',        zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  //   { id: 'zone_studio_sport',  name: 'Studio sport',           zone: 'Zone commune', type: LocationType.COMMON_ZONE },
  // ]

  const lieux = useMemo(() => {
    return (pitches || []).map(p => ({
      id: String(p._id),
      name: p.location,
      zone: p.zone || null,
      type: (p.type === null && p.special_type != null) ? LocationType.COMMON_ZONE : LocationType.LOCATIF,
      special_type: p.special_type || null
    }))
  }, [pitches])

  const getLieuName = (id) => { const lieu = lieux.find(l => l.id === id); return (lieu && lieu.name) || id; }
  const getLieuType = (id) => { if (!id) return undefined; const lieu = lieux.find(l => l.id === id); return lieu ? lieu.type : undefined; }
  const isLocatif = (id) => getLieuType(id) === LocationType.LOCATIF

  const filteredTickets = useMemo(() => {
    const isMine = (t) => {
      const a = t.assignedTo
      if (!a) return false
      return a.type === 'employee' && a.name === userName
    }
    const getAssignedTeam = (t) => {
      const a = t.assignedTo
      if (!a) return null
      if (a.type === 'team') return a.name
      if (a.type === 'employee') {
        const u = (dashboardUsers || []).find(u => (u._id && u._id.toString() === a.id) || u.email === a.id)
        return u ? u.team : null
      }
      return null
    }
    const statusMap = { 'Réalisé': Status.DONE, 'Nouveau': Status.TODO, 'En cours': Status.IN_PROGRESS, 'Bloqué': Status.BLOCKED }
    const urgencyMap = { 'Urgent': Urgency.CRITICAL, 'Important': Urgency.IMPORTANT, 'Normal': Urgency.NORMAL }
    return tickets.filter(t => {
      // Ligne 3 : statut (si aucun filtre statut, exclure les tickets terminés)
      if (filter3) { if (t.status !== statusMap[filter3]) return false }
      else { if (t.status === Status.DONE) return false }
      // Ligne 2 : urgence
      if (filter2 && t.urgency !== urgencyMap[filter2]) return false
      // Ligne 1 : équipe / assignation
      if (filter1 === 'Mes tâches') { if (!isMine(t)) return false }
      else if (filter1 === 'Mon équipe') { const team = getAssignedTeam(t); if (!team || !team.toLowerCase().includes(userTeam.toLowerCase())) return false }
      else if (filter1 === 'Technique') { const team = getAssignedTeam(t); if (!team || !team.toLowerCase().includes('technique')) return false }
      else if (filter1 === 'Ménage') { const team = getAssignedTeam(t); if (!team || !team.toLowerCase().includes('ménage')) return false }
      // Recherche libre : lieu, description, assigné
      if (mobileSearchQuery.trim())
      {
        const q = mobileSearchQuery.trim().toLowerCase()
        const assignedName = (t.assignedTo && t.assignedTo.name) || ''
        const matches = (
          getLieuName(t.locationId).toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.label || '').toLowerCase().includes(q) ||
          assignedName.toLowerCase().includes(q)
        )
        if (!matches) return false
      }
      return true
    }).sort((a, b) => {
      // Urgent d'abord, puis échéance la plus proche (sans échéance en dernier), puis création récente
      const urgencyRank = { [Urgency.CRITICAL]: 0, [Urgency.IMPORTANT]: 1, [Urgency.NORMAL]: 2 }
      const ua = urgencyRank[a.urgency] != null ? urgencyRank[a.urgency] : 2
      const ub = urgencyRank[b.urgency] != null ? urgencyRank[b.urgency] : 2
      if (ua !== ub) return ua - ub
      const da = a.deadline ? new Date(a.deadline).getTime() : Infinity
      const db = b.deadline ? new Date(b.deadline).getTime() : Infinity
      if (da !== db) return da - db
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [tickets, filter1, filter2, filter3, mobileSearchQuery, userName, userTeam, dashboardUsers, lieux]);

  const stats = useMemo(() => ({
    todo:     tickets.filter(t => t.status === Status.TODO).length,
    progress: tickets.filter(t => t.status === Status.IN_PROGRESS).length,
    urgent:   tickets.filter(t => t.urgency !== Urgency.NORMAL && t.status !== Status.DONE).length,
    blocked:  tickets.filter(t => t.status === Status.BLOCKED).length,
  }), [tickets]);

  const isTicketNew = (ticket) => {
    if (!lastSeenTimestamp) return false
    const ticketTime = new Date(ticket.createdAt).getTime()
    return ticketTime > lastSeenTimestamp && (Date.now() - ticketTime) < 24 * 60 * 60 * 1000
  }

  // Terminé depuis la dernière visite (badge « +N » sur le filtre Terminés) —
  // même mécanique lastSeenAt que le point bleu des nouveautés
  const isRecentlyDone = (ticket) => {
    if (!lastSeenTimestamp || ticket.status !== Status.DONE)
      return false
    const doneEvents = (ticket.history || []).filter(e => e.type === 'status_change' && e.to === Status.DONE)
    const last = doneEvents[doneEvents.length - 1]
    if (!last || !last.timestamp)
      return false
    const doneTime = new Date(last.timestamp).getTime()
    return doneTime > lastSeenTimestamp && (Date.now() - doneTime) < 24 * 60 * 60 * 1000
  }

  const splitTickets = (list) => ({
    newItems: list.filter(t => isTicketNew(t)),
    oldItems: list.filter(t => !isTicketNew(t)),
  })

  // « Aujourd'hui / À venir » (filtres et groupes de la liste desktop) : quand une échéance existe, c'est elle qui
  // décide (aujourd'hui ou dépassée → Aujourd'hui, future → À venir). Sans échéance : en cours, ou créé
  // aujourd'hui (nouveauté à trier) → Aujourd'hui ; le reste → À venir.
  const isTodayTicket = (t) => {
    const now = new Date()
    if (t.deadline)
    {
      const endOfDay = new Date(now)
      endOfDay.setHours(23, 59, 59, 999)
      return new Date(t.deadline) <= endOfDay
    }
    if (t.status === Status.IN_PROGRESS)
      return true
    return new Date(t.createdAt).toDateString() === now.toDateString()
  }

  const desktopFilteredTickets = useMemo(() => {
    const urgencyRank = {
      [Urgency.CRITICAL]:  2,
      [Urgency.IMPORTANT]: 1,
      [Urgency.NORMAL]:    0,
    }
    const filtered = tickets.filter(t => {
      if (!matchesDesktopFilters(t, desktopFilters, isTodayTicket)) return false
      if (ticketSearchQuery.trim())
      {
        const q = ticketSearchQuery.toLowerCase()
        const assignedName = (t.assignedTo && t.assignedTo.name) || ''
        return (
          (t.label || '').toLowerCase().includes(q) ||
          getLieuName(t.locationId).toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          assignedName.toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
        )
      }
      return true
    })
    return filtered.sort((a, b) => {
      const urgencyDiff = (urgencyRank[b.urgency] || 0) - (urgencyRank[a.urgency] || 0)
      if (urgencyDiff !== 0)
        return urgencyDiff
      // Échéance la plus proche d'abord (sans échéance en dernier) — aligné sur le tri mobile M1
      const da = a.deadline ? new Date(a.deadline).getTime() : Infinity
      const db = b.deadline ? new Date(b.deadline).getTime() : Infinity
      if (da !== db)
        return da - db
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [tickets, desktopFilters, ticketSearchQuery, lieux]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveQuickAction(null)
  }, [selectedTicketId]);

  // Desktop : garder le panneau détail ouvert en sélectionnant le premier ticket
  useEffect(() => {
    if (selectedTicketId && tickets.some(t => t.id === selectedTicketId))
    {
      // Sélection valide en place : le deep-link éventuel est consommé,
      // l'auto-sélection reprend son rôle normal
      deepLinkTicketId.current = null
      return
    }
    // Deep-link en attente d'application : ne pas écraser la sélection à venir
    if (deepLinkTicketId.current)
      return
    if (desktopFilteredTickets.length > 0)
      setSelectedTicketId(desktopFilteredTickets[0].id)
  }, [desktopFilteredTickets, selectedTicketId, tickets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem('interventions_lastSeenAt', new Date().toISOString())
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape')
        return
      if (lightboxUrl)
      {
        setLightboxUrl(null)
        return
      }
      if (deleteConfirmId)
      {
        setDeleteConfirmId(null)
        return
      }
      if (isObservationsDrawerOpen)
      {
        setIsObservationsDrawerOpen(false)
        setEditingMemoId(null)
        setWorkerComment('')
        setWorkerPhotos([])
        return
      }
      if (isCreateDrawerOpen)
        setIsCreateDrawerOpen(false)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [lightboxUrl, deleteConfirmId, isObservationsDrawerOpen, isCreateDrawerOpen]);

  const flushOfflineQueue = async () => {
    const queue = getOfflineQueue()
    if (queue.length === 0) return
    const remaining = []
    for (const item of queue) {
      try {
        const url = item.type === 'create'
          ? connexion.connect.url + 'dashboard/interventions/create'
          : connexion.connect.url + 'dashboard/interventions/update'
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.payload)
        })
        const serverDoc = await res.json()
        if (item.type === 'create' && serverDoc && serverDoc._id && item.tempId)
          setTickets(prev => prev.map(t =>
            t.id === item.tempId ? Object.assign({}, t, { id: serverDoc._id.toString() }) : t
          ))
      }
      catch {
        remaining.push(item)
      }
    }
    saveOfflineQueue(remaining)
    setPendingCount(remaining.length)
  }
  flushQueueRef.current = flushOfflineQueue

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (flushQueueRef.current) flushQueueRef.current()
    }
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    if (navigator.onLine && getOfflineQueue().length > 0)
      flushQueueRef.current()
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function syncTicket(t) {
    const payload = Object.assign({ id: t.id }, ticketToServer(t, userId))
    fetch(connexion.connect.url + 'dashboard/interventions/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(res => res.json())
    .catch(() => {
      enqueueOfflineAction({ type: 'update', payload })
      setPendingCount(getOfflineQueue().length)
    })
  }

  function handleDeleteTicket(ticketId) {
    setDeleteConfirmId(null)
    setDeletingTicketId(ticketId)
    fetch(connexion.connect.url + 'dashboard/interventions/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: ticketId })
    })
    .then(res => res.json())
    .catch(() => null)
    setTimeout(() => {
      setTickets(prev => prev.filter(t => t.id !== ticketId))
      setDeletingTicketId(null)
      if (selectedTicketId === ticketId)
      {
        setSelectedTicketId(null)
        setView('HOME')
      }
    }, 500)
  }

  // Chargement initial des tickets depuis le serveur
  useEffect(() => {
    setIsLoadingTickets(true)
    fetch(connexion.connect.url + 'dashboard/interventions/list')
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data))
          setTickets(data.map(serverToTicket))
        setIsLoadingTickets(false)
        setTicketsLoaded(true)
      })
      .catch(() => {
        setIsLoadingTickets(false)
        setTicketsLoaded(true)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Polling — rafraîchit la liste toutes les 30s
  useEffect(() => {
    const poll = () => {
      if (!navigator.onLine) return
      if (getOfflineQueue().length > 0) return
      fetch(connexion.connect.url + 'dashboard/interventions/list')
        .then(res => res.json())
        .then(data => {
          if (!data || !Array.isArray(data)) return
          const serverTickets = data.map(serverToTicket)
          setTickets(prev => {
            const serverMap = new Map(serverTickets.map(t => [t.id, t]))
            const localOnly = prev.filter(t => !serverMap.has(t.id))
            return [...serverTickets, ...localOnly]
          })
        })
        .catch(() => {})
    }
    const interval = setInterval(poll, 30000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = () => {
    const locationIds = newIntervention.locationIds && newIntervention.locationIds.length > 0
      ? newIntervention.locationIds
      : []
    if (locationIds.length === 0) return
    const creationTime = new Date().toISOString()
    const newTickets = locationIds.map(locationId => ({
      id: Math.random().toString(36).substr(2, 9),
      label: (newIntervention.description || '').substring(0, 30) || newIntervention.label || 'Intervention',
      assignedTo: newIntervention.assignedTo,
      status: Status.TODO,
      urgency: newIntervention.urgency || Urgency.NORMAL,
      description: newIntervention.description,
      createdAt: creationTime,
      createdBy: userName,
      type: newIntervention.type,
      history: [
        {
          id: Math.random().toString(36).substr(2, 5),
          type: 'creation',
          user: userName,
          timestamp: creationTime,
          details: `Ticket créé et assigné à ${(newIntervention.assignedTo && newIntervention.assignedTo.name) || newIntervention.assignedTo}`
        }
      ],
      ...newIntervention,
      locationId,
    }))
    setTickets(prev => [...newTickets, ...prev])
    setView('CONFIRMATION')
    newTickets.forEach(finalTicket => {
      const payload = ticketToServer(finalTicket, userId)
      fetch(connexion.connect.url + 'dashboard/interventions/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(res => res.json())
      .then(serverDoc => {
        if (serverDoc && serverDoc._id)
          setTickets(prev => prev.map(t =>
            t.id === finalTicket.id ? Object.assign({}, t, { id: serverDoc._id.toString() }) : t
          ))
      })
      .catch(() => {
        enqueueOfflineAction({ type: 'create', payload, tempId: finalTicket.id })
        setPendingCount(getOfflineQueue().length)
      })
    })
  };

  const handleCreateFromDrawer = () => {
    const locationIds = (newIntervention.locationIds || []).filter(Boolean)
    if (locationIds.length === 0) return
    const creationTime = new Date().toISOString()
    const newTickets = locationIds.map(locationId => ({
      id: Math.random().toString(36).substr(2, 9),
      label: newIntervention.description
        ? (newIntervention.description.length > 40 ? newIntervention.description.substring(0, 40) + '…' : newIntervention.description)
        : ('Intervention ' + (newIntervention.type || 'Technique')),
      assignedTo: newIntervention.assignedTo || null,
      status: Status.TODO,
      urgency: newIntervention.urgency || Urgency.NORMAL,
      description: newIntervention.description || '',
      createdAt: creationTime,
      createdBy: userName,
      type: newIntervention.type || 'Technique',
      isCustomerRequest: false,
      customerPermission: !!newIntervention.customerPermission,
      equipment: newIntervention.equipment || null,
      estimatedDuration: newIntervention.estimatedDuration || null,
      deadline: newIntervention.deadline || null,
      descriptionExtra: newIntervention.descriptionExtra || null,
      locationId,
      locationIds: [locationId],
      photoUrls: [],
      history: [
        {
          id: Math.random().toString(36).substr(2, 5),
          type: 'creation',
          user: userName,
          timestamp: creationTime,
          details: `Intervention créée du type ${newIntervention.type || 'Technique'}`
        }
      ]
    }))
    setTickets(prev => [...newTickets, ...prev])
    setIsCreateDrawerOpen(false)
    setIsCreateDetailsOpen(false)
    if (newTickets.length === 1)
    {
      setSelectedTicketId(newTickets[0].id)
      setView('DETAILS')
    }
    else
    {
      setSelectedTicketId(null)
      setView('HOME')
    }
    setNewIntervention(EMPTY_INTERVENTION)
    setCreateDrawerSearch('')
    newTickets.forEach(finalTicket => {
      const drawerPayload = ticketToServer(finalTicket, userId)
      fetch(connexion.connect.url + 'dashboard/interventions/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(drawerPayload)
      })
      .then(res => res.json())
      .then(serverDoc => {
        if (serverDoc && serverDoc._id)
          setTickets(prev => prev.map(t =>
            t.id === finalTicket.id ? Object.assign({}, t, { id: serverDoc._id.toString() }) : t
          ))
      })
      .catch(() => {
        enqueueOfflineAction({ type: 'create', payload: drawerPayload, tempId: finalTicket.id })
        setPendingCount(getOfflineQueue().length)
      })
    })
  }

  useEffect(() => {
    if (view === 'CONFIRMATION') {
      setConfirmAnimated(false);
      const t = setTimeout(() => setConfirmAnimated(true), 30);
      return () => clearTimeout(t);
    }
  }, [view]);

  const closeReadOnly = () => {
    setIsReadOnlyClosing(true);
    setTimeout(() => {
      setIsReadOnlyDetailsOpen(false);
      setIsReadOnlyClosing(false);
    }, 200);
  };

  const closeEdit = () => {
    setIsEditClosing(true);
    setTimeout(() => {
      setIsEditBottomSheetOpen(false);
      setIsEditClosing(false);
    }, 200);
  };

  const closeReviewEdit = () => {
    setIsReviewEditClosing(true);
    setTimeout(() => {
      setIsReviewEditOpen(false);
      setIsReviewEditClosing(false);
    }, 200);
  };



  // --- Screen Rederers ---

  function renderHomeSkeleton() {
    return (
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <header className="px-5 pt-12 pb-4 border-b border-slate-100 flex justify-between items-center shrink-0 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full skeleton-shimmer shrink-0" />
            <div className="space-y-1.5">
              <div className="h-2.5 w-20 skeleton-shimmer rounded-full" />
              <div className="h-3 w-28 skeleton-shimmer rounded-full" />
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-9 h-9 rounded-full skeleton-shimmer" />
            <div className="w-9 h-9 rounded-full skeleton-shimmer" />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto pb-24">
          {/* Filter pills */}
          <div className="px-5 my-5 flex gap-2">
            {[52, 44, 64, 56, 56, 60].map((w, i) => (
              <div key={i} className="h-8 rounded-full skeleton-shimmer shrink-0" style={{ width: w }} />
            ))}
          </div>

          {/* Cards */}
          <div className="px-4 space-y-3.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-slate-100 p-5 rounded-2xl shadow-sm space-y-3">
                <div className="flex justify-between items-center">
                  <div className="h-5 w-20 skeleton-shimmer rounded-full" />
                  <div className="h-5 w-12 skeleton-shimmer rounded-md" />
                </div>
                <div className="flex gap-4 items-center">
                  <div className="w-12 h-12 rounded-2xl skeleton-shimmer shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 w-24 skeleton-shimmer rounded-full" />
                    <div className="h-3.5 w-32 skeleton-shimmer rounded-full" />
                  </div>
                  <div className="w-5 h-5 rounded skeleton-shimmer shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderDetailSkeleton() {
    return (
      <div className="relative h-full bg-slate-50 flex flex-col overflow-hidden">
        {/* Floating buttons */}
        <div className="absolute top-4 left-4 w-11 h-11 rounded-full skeleton-shimmer z-30" />
        <div className="absolute top-4 right-4 h-11 w-24 rounded-3xl skeleton-shimmer z-30" />

        <div className="flex-1 overflow-y-auto px-6 pt-20 pb-40 space-y-6">
          {/* Status badge */}
          <div className="h-6 w-24 skeleton-shimmer rounded-full" />

          {/* Main card */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 space-y-3">
              <div className="h-5 w-28 skeleton-shimmer rounded-full" />
              <div className="h-6 w-full skeleton-shimmer rounded-lg" />
              <div className="h-5 w-3/4 skeleton-shimmer rounded-lg" />
            </div>
            <div className="grid grid-cols-2 divide-x divide-slate-100">
              <div className="p-5 space-y-2">
                <div className="h-3 w-16 skeleton-shimmer rounded-full" />
                <div className="h-6 w-20 skeleton-shimmer rounded-lg" />
              </div>
              <div className="p-5 space-y-2">
                <div className="h-3 w-20 skeleton-shimmer rounded-full" />
                <div className="h-5 w-24 skeleton-shimmer rounded-lg" />
              </div>
            </div>
          </div>

          {/* Mémos section */}
          <div className="space-y-4">
            <div className="h-3 w-40 skeleton-shimmer rounded-full px-1" />
            <div className="bg-white rounded-3xl border border-slate-200 py-8 px-6 flex flex-col items-center space-y-3">
              <div className="w-12 h-12 rounded-full skeleton-shimmer" />
              <div className="h-3 w-48 skeleton-shimmer rounded-full" />
              <div className="h-9 w-40 skeleton-shimmer rounded-xl" />
            </div>
          </div>
        </div>

        {/* Fixed footer */}
        <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-40 transition-all duration-300`}>
          <div className="max-w-xl mx-auto px-5 pt-3 pb-5 bg-white border-t border-slate-100 flex gap-3">
            <div className="flex-1 h-12 skeleton-shimmer rounded-2xl" />
            <div className="flex-1 h-12 skeleton-shimmer rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  function renderHome() {
    if (isLoadingTickets) return renderHomeSkeleton();
    return (
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <header className="px-5 pt-12 pb-4 border-b border-slate-100 flex justify-between items-center shrink-0 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-extrabold shadow-md shadow-blue-100 text-sm shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-slate-400 uppercase font-black tracking-widest leading-none mb-1">{establishment || 'Mon établissement'}</p>
              <h2 className="text-base my-0 font-black text-slate-800 tracking-tight leading-none uppercase truncate">
                {userName}
              </h2>
            </div>
          </div>
          
          {/* Floating Actions */}
          	<div className="flex items-center gap-1.5 shrink-0">

				{/* Retour au dashboard (décision 20 du planning inventaires staff) */}
				{showDashboardButton && (
				<DashboardButton variant="round" onClick={() => history.push('/')} />
				)}

           	 	{/* Float "+" button for Nouvelle Intervention */}
				{canWrite && (
				<button
				onClick={() => setView('SELECT_TYPE')}
				className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-90 text-white flex items-center justify-center shadow-md transition-all shrink-0 font-extrabold"
				title="Nouvelle intervention"
				style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
				>
				<PlusIcon className="w-5 h-5" />
				</button>
				)}

            	{/* Gear Cog Settings Button — l'activation des notifications
            	    vit dans Mon compte (/parametres), accessible via ⚙️ */}
				<button
				onClick={() => history.push('/parametres', { from: '/interventions' })}
				className="w-12 h-12 rounded-full bg-slate-50 border border-slate-200 hover:bg-slate-100 active:scale-95 text-slate-600 flex items-center justify-center shadow-sm transition-all shrink-0"
				title="Paramètres de l'application"
				style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
				>
				<CogIcon className="w-5 h-5" />
				</button>
			</div>	
        </header>

        {/* Bandeau hors-ligne */}
        {!isOnline && (
          <div className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border-b border-amber-200 shrink-0">
            <span className="w-2 h-2 bg-amber-500 rounded-full shrink-0 animate-pulse" />
            <span className="text-xs font-black text-amber-700 uppercase tracking-wider">
              Hors ligne{pendingCount > 0 ? ` · ${pendingCount} action${pendingCount > 1 ? 's' : ''} en attente` : ''}
            </span>
          </div>
        )}
        {isOnline && pendingCount > 0 && (
          <div className="flex items-center gap-2 px-5 py-2 bg-blue-50 border-b border-blue-100 shrink-0">
            <span className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin shrink-0" />
            <span className="text-xs font-black text-blue-600 uppercase tracking-wider">
              Synchronisation en cours…
            </span>
          </div>
        )}

        {/* Filters — deux lignes, sticky sous le header */}
        {(() => {
          const teamOf = (t) => {
            const a = t.assignedTo
            if (!a) return null
            if (a.type === 'team') return a.name
            if (a.type === 'employee') {
              const u = (dashboardUsers || []).find(u => (u._id && u._id.toString() === a.id) || u.email === a.id)
              return u ? u.team : null
            }
            return null
          }
          const activeTickets = tickets.filter(t => t.status !== Status.DONE)
          const teamCount = (needle) => activeTickets.filter(t => { const team = teamOf(t); return team && team.toLowerCase().includes(needle) }).length
          const myTasksCount = activeTickets.filter(t => t.assignedTo && t.assignedTo.type === 'employee' && t.assignedTo.name === userName).length;
          const isNonStandardTeam = userTeam && !['technique', 'ménage'].includes(userTeam.toLowerCase())
          const row1 = [
            { id: 'Mes tâches', label: 'Mes tâches', count: myTasksCount },
            ...(isNonStandardTeam ? [{ id: 'Mon équipe', label: 'Mon équipe', count: teamCount(userTeam.toLowerCase()) }] : []),
            { id: 'Technique', label: 'Technique', count: teamCount('technique') },
            { id: 'Ménage', label: 'Ménage', count: teamCount('ménage') },
          ];
          const row2 = [
            { id: 'Urgent',    label: 'Urgent',    count: activeTickets.filter(t => t.urgency === Urgency.CRITICAL).length,  active: 'border-red-300 text-red-800',    activeBg: '#fef2f2' },
            { id: 'Important', label: 'Important', count: activeTickets.filter(t => t.urgency === Urgency.IMPORTANT).length, active: 'border-amber-300 text-amber-800', activeBg: '#fffbeb' },
            { id: 'Normal',    label: 'Normal',    count: activeTickets.filter(t => t.urgency === Urgency.NORMAL).length,    active: 'border-slate-300 text-slate-700', activeBg: '#f8fafc' },
          ];
          const row3 = [
            { id: 'Nouveau',  label: 'Nouveau',  count: activeTickets.filter(t => t.status === Status.TODO).length,        active: 'border-blue-300 text-blue-800',    activeBg: '#eff6ff' },
            { id: 'En cours',  label: 'En cours',  count: activeTickets.filter(t => t.status === Status.IN_PROGRESS).length, active: 'border-amber-300 text-amber-800',  activeBg: '#fffbeb' },
            { id: 'Bloqué',    label: 'En pause',  count: activeTickets.filter(t => t.status === Status.BLOCKED).length,     active: 'border-red-300 text-red-800',      activeBg: '#fef2f2' },
            { id: 'Réalisé',  label: 'Terminé',  count: tickets.filter(t => t.status === Status.DONE).length,             active: 'border-emerald-300 text-emerald-800', activeBg: '#f0fdf4' },
          ];
          const hasExtendedFilter = filter2 !== null || filter3 !== null
          return (
            <div className="bg-white border-b border-slate-100 shrink-0">
              {/* Recherche */}
              <div className="px-5 pt-3">
                <div className="relative">
                  <SearchIcon className="absolute left-4 inset-y-0 my-auto w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="search"
                    placeholder="Lieu, description, assigné…"
                    value={mobileSearchQuery}
                    onChange={e => setMobileSearchQuery(e.target.value)}
                    className="browser-default w-full pl-11 pr-10 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-500 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 transition-all"
                    style={{ outline: 'none', boxShadow: 'none' }}
                  />
                  {mobileSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setMobileSearchQuery('')}
                      aria-label="Effacer la recherche"
                      className="absolute inset-y-0 right-0 px-3.5 flex items-center text-slate-400"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {/* Ligne 1 + bouton toggle */}
              <div className="flex items-center">
                <div className="flex-1 overflow-x-auto no-scrollbar" style={{ paddingTop: 2, paddingBottom: 2 }}>
                  <div className="px-5 pt-3 pb-2 flex gap-2">
                    {row1.map(({ id, label, count }) => {
                      const isActive = filter1 === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setFilter1(isActive ? null : id)}
                          className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${isActive ? 'border-blue-300 text-blue-800' : 'border-slate-200 text-slate-500'}`}
                          style={{ backgroundImage: 'none', backgroundColor: isActive ? '#dbeafe' : '#ffffff' }}
                        >
                          {label}
                          {count > 0 && <span className="ml-1.5">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (filtersExpanded) { setFilter2(null); setFilter3(null) }
                    setFiltersExpanded(v => !v)
                  }}
                  className="shrink-0 pr-5 pl-3 py-3.5 text-xs font-bold text-slate-400 hover:text-blue-600 transition-colors whitespace-nowrap"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  {filtersExpanded ? '− Filtres' : '+ Filtres'}
                  {!filtersExpanded && hasExtendedFilter && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-blue-500 inline-block align-middle" />
                  )}
                </button>
              </div>

              {/* Lignes 2 & 3 — masquées par défaut */}
              {filtersExpanded && (
                <>
                  <div className="px-5 pb-1.5 pt-0 flex gap-2">
                    {row2.map(({ id, label, active, activeBg, count }) => {
                      const isActive = filter2 === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setFilter2(isActive ? null : id)}
                          className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${isActive ? active : 'border-slate-200 text-slate-500'}`}
                          style={{ backgroundImage: 'none', backgroundColor: isActive ? activeBg : '#ffffff' }}
                        >
                          {label}
                          {count > 0 && <span className="ml-1.5">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="overflow-x-auto no-scrollbar" style={{ paddingTop: 2, paddingBottom: 2 }}>
                  <div className="px-5 pb-3 pt-1 flex gap-2">
                    {row3.map(({ id, label, active, activeBg, count }) => {
                      const isActive = filter3 === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setFilter3(isActive ? null : id)}
                          className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${isActive ? active : 'border-slate-200 text-slate-500'}`}
                          style={{ backgroundImage: 'none', backgroundColor: isActive ? activeBg : '#ffffff' }}
                        >
                          {label}
                          {count > 0 && <span className="ml-1.5">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        <div className="flex-1 overflow-y-auto pb-24 overscroll-contain">

        {/* Stats Summary - Geometric Grid */}
        {/* <div className="p-5 pb-2">
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-blue-50 p-2 py-3 rounded-2xl text-center border border-blue-100">
              <p className="text-sm font-bold text-blue-600">{stats.todo}</p>
              <p className="text-xs text-blue-400 uppercase font-bold tracking-wider">À faire</p>
            </div>
            <div className="bg-orange-50 p-2 py-3 rounded-2xl text-center border border-orange-100">
              <p className="text-sm font-bold text-orange-600">{stats.progress}</p>
              <p className="text-xs text-orange-400 uppercase font-bold tracking-wider">En cours</p>
            </div>
            <div className="bg-red-50 p-2 py-3 rounded-2xl text-center border border-red-100">
              <p className="text-sm font-bold text-red-600">{stats.urgent}</p>
              <p className="text-xs text-red-400 uppercase font-bold tracking-wider">Urgent</p>
            </div>
            <div className="bg-slate-50 p-2 py-3 rounded-2xl text-center border border-slate-100">
              <p className="text-sm font-bold text-slate-600">{stats.blocked}</p>
              <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Bloqué</p>
            </div>
          </div>
        </div> */}

        {/* List */}
        <div className="px-4 pt-4 space-y-3.5">
          <>
            {filteredTickets.length === 0 && (() => {
              const emptyConfig = {
                'All':        { emoji: '🎉', title: 'Tout est à jour !', subtitle: 'Aucune intervention active en cours.' },
                'Mes tâches': { emoji: '✅', title: 'Aucune tâche à votre nom', subtitle: 'Les tâches de votre équipe restent disponibles.', action: { label: 'Voir toutes les interventions →', onClick: () => setFilter1(null) } },
                'Mon équipe': { emoji: '👥', title: `Aucune tâche pour l'équipe ${userTeam}`, subtitle: 'Les nouvelles tâches apparaîtront ici.' },
                'Technique':  { emoji: '🔧', title: 'Aucune intervention technique en cours', subtitle: 'Les nouvelles tâches apparaîtront ici.' },
                'Ménage':     { emoji: '🧹', title: 'Aucune intervention ménage en cours', subtitle: 'Les nouvelles tâches apparaîtront ici.' },
                'Réalisé':   { emoji: '📋', title: 'Aucune intervention terminée', subtitle: 'Les tâches clôturées par vous ou votre équipe apparaîtront ici.' },
                'Nouveau':   { emoji: '🆕', title: 'Aucune intervention à faire', subtitle: 'Toutes les tâches ont été prises en charge.' },
                'En cours':   { emoji: '⏳', title: 'Aucune intervention en cours', subtitle: '' },
                'Bloqué':     { emoji: '🚧', title: 'Aucune intervention en pause', subtitle: 'Tout avance sans obstacle.' },
                'Urgent':     { emoji: '🟢', title: 'Aucune intervention urgente', subtitle: 'Tout est sous contrôle.' },
                'Important':  { emoji: '🟡', title: 'Aucune intervention importante', subtitle: 'Aucune tâche marquée "Important" en cours.' },
                'Normal':     { emoji: '🔵', title: 'Aucune intervention normale en cours', subtitle: '' },
              };
              const cfg = emptyConfig[filter1] || emptyConfig[filter2] || emptyConfig[filter3] || { emoji: '📭', title: 'Aucun résultat pour ces filtres', subtitle: '' };
              return (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center text-3xl mb-4">{cfg.emoji}</div>
                  <p className="font-black text-slate-800 text-base mb-1">{cfg.title}</p>
                  <p className="text-sm text-slate-400 font-medium mb-6">{cfg.subtitle}</p>
                  {cfg.action && (
                    <button
                      onClick={cfg.action.onClick}
                      className="py-3 font-bold text-sm text-blue-600"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      {cfg.action.label}
                    </button>
                  )}
                </div>
              );
            })()}

            {filteredTickets.map((ticket) => {
              // Establish theme bases based on:
              // status terminé -> vert (green)
              // else urgency critical -> rouge (red)
              // else urgency important -> jaune (amber)
              // else urgency normal (default) -> bleu (blue)
              const isNew = isTicketNew(ticket);

              let theme = {
                bg: 'bg-blue-50 hover:bg-blue-100 transition-colors',
                border: 'border border-blue-200',
                text: 'text-blue-900',
                meta: 'text-blue-700',
                iconBg: 'bg-blue-100 text-blue-700 border border-blue-200'
              };

              if (ticket.status === Status.DONE) {
                theme = {
                  bg: 'bg-emerald-50 hover:bg-emerald-100 transition-colors',
                  border: 'border border-emerald-200',
                  text: 'text-emerald-900',
                  meta: 'text-emerald-700',
                  iconBg: 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                };
              } else if (ticket.urgency === Urgency.CRITICAL) {
                theme = {
                  bg: 'bg-red-50 hover:bg-red-100 transition-colors',
                  border: 'border border-red-300',
                  text: 'text-red-900',
                  meta: 'text-red-700',
                  iconBg: 'bg-red-100 text-red-700 border border-red-200'
                };
              } else if (ticket.urgency === Urgency.IMPORTANT) {
                theme = {
                  bg: 'bg-amber-50 hover:bg-amber-100 transition-colors',
                  border: 'border border-amber-300',
                  text: 'text-amber-900',
                  meta: 'text-amber-700',
                  iconBg: 'bg-amber-100 text-amber-700 border border-amber-200'
                };
              }

              return (
                <motion.div
                  key={ticket.id}
                  layout
                  initial={{ opacity: 0, y: 14 }}
                  animate={ticket.urgency === Urgency.CRITICAL && ticket.status !== Status.DONE ? {
                    opacity: 1,
                    y: 0,
                    scale: [1, 1.018, 1],
                  } : { opacity: 1, y: 0 }}
                  transition={ticket.urgency === Urgency.CRITICAL && ticket.status !== Status.DONE ? {
                    scale: {
                      repeat: Infinity,
                      duration: 1.8,
                      ease: "easeInOut"
                    },
                    layout: { duration: 0.2 }
                  } : {
                    layout: { duration: 0.2 }
                  }}
                  onClick={() => {
                    setSelectedTicketId(ticket.id);
                    setView('DETAILS');
                  }}
                  className={`relative ${theme.bg} ${theme.border} p-5 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 active:scale-95 cursor-pointer flex flex-col justify-between`}
                >
                  {/* Badge flottant urgence — seulement si Important/Urgent */}
                  {ticket.status !== Status.DONE && ticket.urgency !== Urgency.NORMAL && (
                    <UrgencyBadge urgency={ticket.urgency} variant="solid" className="absolute -top-3 right-6 z-20" />
                  )}

                  {/* Top line with metadata / badges */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      {ticket.status !== Status.TODO ? (
                        <StatusBadge status={ticket.status} />
                      ) : isNew ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs uppercase tracking-wider shadow-sm bg-blue-600 text-white font-bold">
                          Nouveau
                        </span>
                      ) : null}
                      {ticket.deadline && (() => {
                        const deadlineDay = new Date(ticket.deadline)
                        deadlineDay.setHours(0, 0, 0, 0)
                        const today = new Date()
                        today.setHours(0, 0, 0, 0)
                        const isDue = deadlineDay.getTime() <= today.getTime() && ticket.status !== Status.DONE
                        return (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-md border flex items-center gap-1 whitespace-nowrap ${isDue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-slate-600 border-slate-200'}`}>
                            <span role="img" aria-label="échéance">📅</span> {new Date(ticket.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </span>
                        )
                      })()}
                    </div>
                  </div>
                  
                  {/* Content body row */}
                  <div className="flex gap-4 items-center mb-1">
                    <div className={`w-12 h-12 ${theme.iconBg} rounded-2xl flex items-center justify-center text-xl shrink-0`}>
                      {ticket.type === 'Ménage' ? '💫' : '⚒️'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-black font-mono text-lg my-2 text-slate-800 leading-tight tracking-tight flex items-center gap-1">
                        <LocationMarkerIcon className="w-3.5 h-3.5 shrink-0 opacity-60" />
                        <span className="truncate">{getLieuName(ticket.locationId)}</span>
                      </h3>
                      <p className="text-sm text-slate-500 font-bold tracking-wide mt-0.5 flex items-center gap-1.5">
                        <UserIcon className="w-3 h-3 opacity-70" />
                        <span>{(ticket.assignedTo && ticket.assignedTo.name) || 'Non assigné'}</span>
                      </p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-slate-400 shrink-0" />
                  </div>
                  
                  {/* Description footer if exists */}
                  {ticket.description && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
                      {isNew && (
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" aria-label="Nouveau" />
                      )}
                      <p className="flex-1 min-w-0 text-sm font-mono font-semibold text-slate-900 leading-relaxed truncate">
                        {ticket.description}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </>
        </div>

        </div>{/* end scrollable */}
      </div>
    );
  }


  // La fiche a été ouverte depuis un tour de terrain (state.from posé par l'app terrain)
  const openedFromTour = !!(history && history.location && history.location.state && typeof history.location.state.from === 'string' && history.location.state.from.indexOf('/inventaires-terrain') === 0)

  // Bouton retour de la fiche, en vue mobile seulement (le desktop n'a pas de fiche plein écran). Ouverte depuis
  // un tour de terrain (state.from), la fiche revient directement au tour, sans passer par la liste des
  // interventions : l'app terrain a mis le tour juste avant la fiche dans l'historique (state.depth entrées à
  // remonter), le retour du navigateur y ramène donc aussi. toList : « Retour aux interventions » et « Voir
  // les interventions », toujours vers la liste.
  function goBackFromDetail(toList) {
    const state = history && history.location ? history.location.state : null
    const from = !toList && state ? state.from : null
    if (from && history.go)
    {
      const depth = state.depth || 1
      if (history.length > depth)
        history.go(-depth)
      else
        history.replace(from)
      return
    }
    setIsDetailClosing(true)
    setTimeout(() => {
      setIsDetailClosing(false)
      setView('HOME')
    }, 290)
  }

  function closeEditOverlay() {
    setIsEditOverlayClosing(true)
    setTimeout(() => {
      setIsEditOverlayClosing(false)
      setIsEditOverlayOpen(false)
    }, 220)
  }

  function renderTicketDetail() {
    if (isLoadingTickets) return renderDetailSkeleton();
    const ticket = tickets.find(t => t.id === selectedTicketId);
    if (!ticket) return null;

    // Helper to log and apply worker actions
    const handleStatusChange = (fromStatus, toStatus, fallbackDetail) => {
      const statusEvent = {
        id: Math.random().toString(36).substr(2, 5),
        type: 'status_change',
        user: userName,
        timestamp: new Date().toISOString(),
        from: fromStatus,
        to: toStatus,
        details: fallbackDetail
      };

      const memoEvents = [];
      if (workerComment.trim() || workerPhotos.length > 0) {
        memoEvents.push({
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo',
          user: userName,
          timestamp: new Date().toISOString(),
          details: workerComment.trim() || 'Ajout d\'une observation terrain',
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        });
      }

      setTickets(prev => {
        const next = prev.map(t => t.id === ticket.id ? {
          ...t,
          status: toStatus,
          history: [...(t.history || []), statusEvent, ...memoEvents]
        } : t)
        const updated = next.find(t => t.id === ticket.id)
        if (updated) syncTicket(updated)
        return next
      });

      setWorkerComment('');
      setWorkerPhotos([]);
      setEditingMemoId(null);

      if (toStatus === Status.DONE) {
        setUndoDoneInfo({ ticketId: ticket.id, previousStatus: fromStatus });
        setView('DONE_SUCCESS');
      }
    };

    const handleAddCommentOnly = () => {
      if (!workerComment.trim() && workerPhotos.length === 0) return;

      if (editingMemoId) {
        const editEvent = {
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo_edit',
          user: userName,
          timestamp: new Date().toISOString(),
          editedMemoId: editingMemoId,
          details: workerComment.trim() || 'Modification d\'une observation terrain',
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        };
        setTickets(prev => {
          const next = prev.map(t => t.id === ticket.id ? {
            ...t,
            history: [
              ...(t.history || []).map(h => h.id === editingMemoId ? {
                ...h,
                details: workerComment.trim() || 'Ajout d\'une observation terrain',
                photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
              } : h),
              editEvent
            ]
          } : t)
          const updated = next.find(t => t.id === ticket.id)
          if (updated) syncTicket(updated)
          return next
        });
      } else {
        const event = {
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo',
          user: userName,
          timestamp: new Date().toISOString(),
          details: workerComment.trim() || 'Ajout d\'une observation terrain',
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        };

        setTickets(prev => {
          const next = prev.map(t => t.id === ticket.id ? {
            ...t,
            history: [...(t.history || []), event]
          } : t)
          const updated = next.find(t => t.id === ticket.id)
          if (updated) syncTicket(updated)
          return next
        });
      }

      setWorkerComment('');
      setWorkerPhotos([]);
      setEditingMemoId(null);
    };

    const closePauseSheet = () => {
      setIsPauseSheetClosing(true);
      setTimeout(() => {
        setIsPauseSheetOpen(false);
        setIsPauseSheetClosing(false);
      }, 200);
    };

    const handlePauseWithReason = (reason) => {
      handleStatusChange(Status.IN_PROGRESS, Status.BLOCKED, reason);
      closePauseSheet();
    };

    const doDiscardAndClose = () => {
      setIsConfirmDiscardOpen(false);
      setIsConfirmDiscardClosing(false);
      setIsFeedbackClosing(true);
      setTimeout(() => {
        setIsFeedbackSheetOpen(false);
        setIsFeedbackClosing(false);
        setEditingMemoId(null);
        setWorkerComment('');
        setWorkerPhotos([]);
        setInitialWorkerComment('');
        setInitialWorkerPhotos([]);
      }, 200);
    };

    const closeConfirmDiscard = () => {
      setIsConfirmDiscardClosing(true);
      setTimeout(() => {
        setIsConfirmDiscardOpen(false);
        setIsConfirmDiscardClosing(false);
      }, 200);
    };

    const closeFeedbackSheet = () => {
      setIsFeedbackClosing(true);
      setTimeout(() => {
        setIsFeedbackSheetOpen(false);
        setIsFeedbackClosing(false);
        setEditingMemoId(null);
        setWorkerComment('');
        setWorkerPhotos([]);
        setInitialWorkerComment('');
        setInitialWorkerPhotos([]);
      }, 200);
    };

    const handleCloseFeedbackSheet = () => {
      const commentChanged = workerComment.trim() !== initialWorkerComment.trim();
      const photosChanged = workerPhotos.length !== initialWorkerPhotos.length ||
        workerPhotos.some((url, i) => url !== initialWorkerPhotos[i]);
      if (commentChanged || photosChanged) {
        setIsConfirmDiscardOpen(true);
        return;
      }
      closeFeedbackSheet();
    };

    return (
      <div className="relative h-full bg-slate-50 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div
          className="flex-shrink-0 h-16 bg-white border-b border-slate-100 flex items-center px-3 gap-2"
          style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)', position: 'relative', zIndex: 30 }}
        >
          {/* Bouton retour */}
          <button
            type="button"
            onClick={() => { setWorkerComment(''); setWorkerPhotos([]); goBackFromDetail(); }}
            className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 active:scale-95 transition-all"
            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>

          <div className="flex-1 min-w-0 flex">
            {/* Ouverte depuis un tour de terrain : le chevron ramène au tour, ce bouton mène au menu des interventions */}
            {openedFromTour && (
              <button
                type="button"
                onClick={() => { setWorkerComment(''); setWorkerPhotos([]); goBackFromDetail(true); }}
                className="flex items-center gap-1.5 h-9 px-3 min-w-0 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 active:scale-95 transition-all font-semibold text-sm"
                style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
              >
                <ClipboardListIcon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Voir les interventions</span>
              </button>
            )}
          </div>
          {/* Actions droite */}
          <div className="flex-shrink-0 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsReadOnlyDetailsOpen(true)}
              className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 active:scale-95 transition-all font-semibold text-sm"
              style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
            >
              <InformationCircleIcon className="w-4 h-4" />
              Voir plus
            </button>
            {canWrite && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsActionMenuOpen(v => !v)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 active:scale-95 transition-all"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  <DotsVerticalIcon className="w-5 h-5" />
                </button>
                {isActionMenuOpen && (
                  <>
                    <div
                      onClick={() => setIsActionMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                    />
                    <div
                      style={{
                        position: 'absolute', top: '100%', right: 0, zIndex: 50,
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        border: '1px solid #e2e8f0',
                        minWidth: '168px',
                        overflow: 'hidden',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => { setIsActionMenuOpen(false); setIsEditOverlayOpen(true); }}
                        className="w-full px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-3 text-left"
                        style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                      >
                        <PencilIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        Modifier
                      </button>
                      <div className="h-px bg-slate-100 mx-3" />
                      <button
                        type="button"
                        onClick={() => { setIsActionMenuOpen(false); setDeleteConfirmId(ticket.id); }}
                        className="w-full px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-3 text-left"
                        style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                      >
                        <TrashIcon className="w-4 h-4 flex-shrink-0" />
                        Supprimer
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Outer scrolling content */}
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-40 space-y-6">

          {/* Groupe ticket principal + blocs enrichissement */}
          <div className="space-y-2">

          {/* TICKET HERO CARD — Option A */}
          {(() => {
            const bannerBg = ticket.status === Status.DONE
              ? '#059669'
              : ({ [Urgency.CRITICAL]: '#dc2626', [Urgency.IMPORTANT]: '#d97706', [Urgency.NORMAL]: '#475569' })[ticket.urgency] || '#475569'
            const bannerLabel = ticket.status === Status.DONE
              ? 'Terminé'
              : ({ [Urgency.CRITICAL]: 'Urgent', [Urgency.IMPORTANT]: 'Important', [Urgency.NORMAL]: 'Normal' })[ticket.urgency] || 'Normal'
            const statusLabel = ({ [Status.TODO]: 'À faire', [Status.IN_PROGRESS]: 'En cours', [Status.BLOCKED]: 'En pause', [Status.DONE]: 'Terminé' })[ticket.status] || 'À faire'
            return (
              <section className="bg-white rounded-3xl border border-slate-100 shadow-md overflow-hidden">

                {/* Bandeau urgence + statut */}
                <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: bannerBg }}>
                  <span className="text-sm font-black text-white uppercase tracking-widest">{bannerLabel}</span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide" style={{ backgroundColor: 'rgba(255,255,255,0.22)', color: 'white' }}>
                    {statusLabel}
                  </span>
                </div>

                {/* Lieu + Responsable — gros, priorité terrain */}
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  <div className="px-5 pt-6 pb-5 text-left">
                    <span className="flex items-center gap-1 text-2xs uppercase font-bold text-slate-400 tracking-widest mb-1">
                      <LocationMarkerIcon className="w-3.5 h-3.5 flex-shrink-0" /> Lieu
                    </span>
                    <p className="font-black text-slate-900 leading-none font-mono" style={{ fontSize: '2.25rem' }}>
                      {getLieuName(ticket.locationId)}
                    </p>
                  </div>
                  <div className="px-5 pt-6 pb-5 text-left">
                    <span className="flex items-center gap-1 text-2xs uppercase font-bold text-slate-400 tracking-widest mb-1">
                      <UserIcon className="w-3.5 h-3.5 flex-shrink-0" /> Assigné
                    </span>
                    <p className="text-lg font-black text-slate-800 leading-tight">
                      {(ticket.assignedTo && ticket.assignedTo.name) || ticket.assignedTo || 'Non assigné'}
                    </p>
                  </div>
                </div>

                {/* Découpe ticket */}
                <div className="mx-5" style={{ borderTop: '2px dashed #e2e8f0' }} />

                {/* Description */}
                <div className="px-5 py-5 text-left">
                  <span className="text-2xs uppercase font-bold text-slate-400 tracking-widest block mb-2">Description</span>
                  <p className="text-xl font-mono font-black text-slate-900 leading-snug">{ticket.description}</p>
                </div>

                {/* Chips enrichissement — conditionnels */}
                {(ticket.deadline || ticket.estimatedDuration || ticket.isCustomerRequest) && (
                  <>
                    <div className="mx-5" style={{ borderTop: '2px dashed #e2e8f0' }} />
                    <div className="px-5 py-5 flex flex-wrap gap-2">
                      {ticket.deadline && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                          <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                          Échéance · {new Date(ticket.deadline).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {ticket.estimatedDuration && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                          <ClockIcon className="w-4 h-4 flex-shrink-0" />
                          {ticket.estimatedDuration}
                        </span>
                      )}
                      {ticket.isCustomerRequest && (
                        ticket.conversationId ? (
                          <button
                            type="button"
                            onClick={() => history.push('/messages/chat', { openConversationId: ticket.conversationId })}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-sm font-bold active:scale-95"
                            style={{ backgroundImage: 'none', backgroundColor: '#eff6ff', transition: 'transform 0.15s ease' }}
                          >
                            <AnnotationIcon className="w-4 h-4 flex-shrink-0" />
                            Voir la demande client
                            <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-sm font-bold">
                            <AnnotationIcon className="w-4 h-4 flex-shrink-0" />
                            Demande client
                          </span>
                        )
                      )}
                    </div>
                  </>
                )}

                {/* Footer — date + créateur */}
                <div className="mx-5" style={{ borderTop: '2px dashed #e2e8f0' }} />
                <div className="px-5 py-4 flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">
                    Créé le {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) + ' à ' + new Date(ticket.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'date inconnue'}
                    {ticket.createdBy && (
                      <span> par <span className="font-bold text-slate-500">{getTicketCreator(ticket)}</span></span>
                    )}
                  </span>
                </div>

              </section>
            )
          })()}

          </div>{/* fin groupe ticket */}

          {/* Dynamic photos gallery attached */}
          {ticket.photoUrls && ticket.photoUrls.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest px-1">Galerie Photos ({ticket.photoUrls.length})</p>
              <div className="overflow-x-auto no-scrollbar pb-2">
                <div className="flex gap-3">
                  {ticket.photoUrls.map((url, idx) => (
                    <div key={idx} role="button" tabIndex={0} aria-label={`Afficher la photo ${idx + 1} en plein écran`} className="w-44 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-slate-100 shadow-sm relative bg-slate-100 cursor-pointer active:scale-95 transition-transform" style={{ aspectRatio: '4 / 3' }} onClick={() => setLightboxUrl(url)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxUrl(url) } }}>
                      <img src={thumbUrl(url)} alt={`Intervention ${idx}`} className="w-full h-full object-cover pointer-events-none" />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Mémos terrain & Photos (Section Unique, Cohesive & Interactive) */}
          <section className="space-y-4 text-left">
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-slate-600 font-black uppercase tracking-widest flex items-center gap-1.5">
                <ClipboardListIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                Observations terrain
              </p>
              {canActOnTicket(ticket) && ticket.status !== Status.DONE && ticket.history && ticket.history.some(h => h.type === 'memo') && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingMemoId(null);
                    setWorkerComment('');
                    setWorkerPhotos([]);
                    setInitialWorkerComment('');
                    setInitialWorkerPhotos([]);
                    setIsFeedbackSheetOpen(true);
                  }}
                  className="text-xs text-blue-600 font-black uppercase tracking-wider hover:underline flex items-center gap-1 py-3 px-2 -my-3 -mx-2"
                >
                  <PlusIcon className="w-4 h-4" /> Nouveau mémo
                </button>
              )}
            </div>

            {ticket.history && ticket.history.some(h => h.type === 'memo') ? (
              <div>
                {(() => {
                  const memos = ticket.history
                    .filter(h => h.type === 'memo')
                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  return memos.map((memo, index) => (
                    <div key={memo.id} className="flex gap-3">
                      {/* Timeline : dot + trait */}
                      <div className="flex flex-col items-center flex-shrink-0" style={{ width: '16px' }}>
                        <div
                          className="flex-shrink-0 rounded-full border-2 border-white"
                          style={{ width: '12px', height: '12px', marginTop: '18px', backgroundColor: '#6366f1', boxShadow: '0 0 0 2px #c7d2fe' }}
                        />
                        {index < memos.length - 1 && (
                          <div className="flex-1" style={{ width: '2px', backgroundColor: '#c7d2fe', minHeight: '16px', marginTop: '4px' }} />
                        )}
                      </div>

                      {/* Carte */}
                      <div className="flex-1 bg-indigo-50 p-4 rounded-2xl border border-indigo-100 shadow-sm space-y-3 mb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-black flex-shrink-0">
                              {memo.user.split(' ').map(n => n[0]).join('').toUpperCase() || 'W'}
                            </div>
                            <div>
                              <span className="text-xs font-bold text-slate-800 block">{memo.user}</span>
                              <span className="text-xs text-slate-500 font-medium font-mono">
                                le {new Date(memo.timestamp).toLocaleDateString('fr-FR')} à {new Date(memo.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                          {memo.user === userName && ticket.status !== Status.DONE && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingMemoId(memo.id);
                                setWorkerComment(memo.details || '');
                                setWorkerPhotos(memo.photoUrls || []);
                                setInitialWorkerComment(memo.details || '');
                                setInitialWorkerPhotos(memo.photoUrls || []);
                                setIsFeedbackSheetOpen(true);
                              }}
                              className="text-xs font-bold text-indigo-500 hover:text-indigo-700 transition-colors active:scale-95 flex-shrink-0"
                              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                            >
                              Modifier
                            </button>
                          )}
                        </div>
                        {memo.details && (
                          <p className="text-base text-slate-900 leading-relaxed font-semibold">
                            {memo.details}
                          </p>
                        )}
                        {memo.photoUrls && memo.photoUrls.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 pt-1 select-none">
                            {memo.photoUrls.map((url, imgIdx) => (
                              <div key={imgIdx} className="rounded-xl overflow-hidden border border-indigo-100 shadow-sm bg-white cursor-pointer active:scale-95 transition-transform" style={{ aspectRatio: '4 / 3' }} onClick={() => setLightboxUrl(url)}>
                                <img src={thumbUrl(url)} alt={`Mémo ${imgIdx + 1}`} className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                })()}
              </div>
            ) : (
              canActOnTicket(ticket) && ticket.status !== Status.DONE && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingMemoId(null);
                    setWorkerComment('');
                    setWorkerPhotos([]);
                    setInitialWorkerComment('');
                    setInitialWorkerPhotos([]);
                    setIsFeedbackSheetOpen(true);
                  }}
                  className="w-full bg-white py-8 px-6 rounded-3xl border border-slate-200 text-center space-y-3 shadow-sm active:scale-95 transition-all hover:bg-slate-50"
                  style={{ backgroundImage: 'none' }}
                >
                  <div className="w-12 h-12 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center mx-auto border border-slate-100">
                    <PlusIcon className="w-5 h-5" />
                  </div>
                  <span className="inline-block py-2.5 px-4 bg-blue-50 text-blue-700 font-extrabold rounded-xl text-sm uppercase tracking-wider border border-blue-200">
                    Ajouter une note / photo
                  </span>
                </button>
              )
            )}
          </section>

          {/* Timeline / History */}
          <section className="space-y-4">
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest px-1 text-left">Historique de l'intervention</p>
            <div className="space-y-4 text-left">
               {([...(ticket.history || []), {
                 id: 'legacy-creation',
                 type: 'creation',
                 user: getTicketCreator(ticket),
                 timestamp: ticket.createdAt,
                 details: 'Création du ticket'
               }].filter((ev) => ev.type !== 'memo')
                .filter((ev, idx, self) => 
                 ev.id !== 'legacy-creation' || !self.some(s => s.type === 'creation' && s.id !== 'legacy-creation')
               ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((event, idx, arr) => {
                 let eventIcon = <ClockIcon className="w-4 h-4 text-blue-600" />;
                 let eventBg = 'bg-blue-50';
                 let titleText = 'Création du ticket';

                 if (event.type !== 'creation') {
                   if (event.details === 'Clôture annulée') {
                     eventIcon = <RefreshIcon className="w-3.5 h-3.5 text-slate-600" />;
                     eventBg = 'bg-slate-100';
                     titleText = 'Clôture annulée';
                   } else if (event.type === 'memo_edit') {
                     eventIcon = <PencilIcon className="w-3.5 h-3.5 text-indigo-600" />;
                     eventBg = 'bg-indigo-50';
                     titleText = 'Note modifiée';
                   } else if (event.to === Status.IN_PROGRESS) {
                     eventIcon = <PlayIcon className="w-3.5 h-3.5 text-blue-600 fill-blue-600" />;
                     eventBg = 'bg-blue-50';
                     titleText = 'Début de l\'intervention';
                   } else if (event.to === Status.BLOCKED) {
                     eventIcon = <PauseIcon className="w-3.5 h-3.5 text-orange-600 fill-orange-600" />;
                     eventBg = 'bg-orange-50';
                     titleText = 'Intervention mise en pause';
                   } else if (event.to === Status.DONE) {
                     eventIcon = <CheckCircleIcon className="w-4 h-4 text-emerald-600" />;
                     eventBg = 'bg-emerald-50';
                     titleText = 'Intervention terminée';
                   } else {
                     eventIcon = <AnnotationIcon className="w-4 h-4 text-slate-600" />;
                     eventBg = 'bg-slate-50';
                     titleText = 'Commentaire / Observation';
                   }
                 }

                 return (
                   <div key={event.id} className="flex gap-4">
                      <div className="w-8 flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full ${eventBg} flex items-center justify-center shadow-sm`}>
                          {eventIcon}
                        </div>
                        {idx !== arr.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1"></div>}
                      </div>
                      <div className="pb-4 flex-1">
                        <p className="text-xs font-bold text-slate-800">
                          {titleText}
                        </p>
                        <p className="text-xs text-slate-500">
                          Par {event.user} le {new Date(event.timestamp).toLocaleDateString('fr-FR')} à {new Date(event.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {event.details && (
                          <p className="text-sm text-slate-600 mt-1 pl-2 border-l-2 border-slate-200">
                            {event.details}
                          </p>
                        )}
                        {event.photoUrls && event.photoUrls.length > 0 && (
                          <div className="flex gap-2 mt-2 select-none overflow-x-auto no-scrollbar py-1">
                            {event.photoUrls.map((url, imgIdx) => (
                              <div key={imgIdx} className="w-20 h-16 rounded-xl overflow-hidden border border-slate-200 shadow-sm flex-shrink-0 bg-slate-100">
                                <img src={thumbUrl(url)} alt={`Observation ${imgIdx}`} className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                   </div>
                 );
               }))}
            </div>
          </section>

        </div>

        {/* ULTRA-ERGONOMIC STICKY FOOTER ACTION BAR — actions réservées à
            l'assigné (lui ou son équipe) ou aux profils en écriture */}
        {canActOnTicket(ticket) && (
        <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-25 transition-all duration-300`}>
          <div className="max-w-xl mx-auto p-4 bg-white border-t border-slate-100 flex items-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', boxShadow: '0 -12px 40px rgba(0,0,0,0.06)' }}>
          {ticket.status === Status.TODO && (
            <button 
              type="button"
              onClick={() => handleStatusChange(Status.TODO, Status.IN_PROGRESS, 'Démarrage de l\'intervention')}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 text-base uppercase tracking-wider"
            >
              <PlayIcon className="w-6 h-6 fill-white text-white" />
              Commencer
            </button>
          )}

          {ticket.status === Status.IN_PROGRESS && (
            <div className="w-full grid grid-cols-2 gap-3 h-14">
              <button
                type="button"
                onClick={() => setIsPauseSheetOpen(true)}
                className="h-full bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold rounded-2xl border border-amber-200/50 transition-all flex items-center justify-center gap-2 active:scale-95 text-base uppercase tracking-wider"
              >
                <PauseIcon className="w-6 h-6 text-amber-600 fill-amber-600" />
                pause
              </button>
              
              <button 
                type="button"
                onClick={() => handleStatusChange(Status.IN_PROGRESS, Status.DONE, 'Intervention terminée avec succès')}
                className="h-full bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 text-base uppercase tracking-wider"
              >
                <CheckCircleIcon className="w-6 h-6" />
                Terminer
              </button>
            </div>
          )}

          {ticket.status === Status.BLOCKED && (
            <button 
              type="button"
              onClick={() => handleStatusChange(Status.BLOCKED, Status.IN_PROGRESS, 'Reprise de l\'intervention')}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 text-base uppercase tracking-wider"
            >
              <PlayIcon className="w-4 h-4 fill-white text-white" />
              Reprendre
            </button>
          )}

          {ticket.status === Status.DONE && (
            <button
              type="button"
              onClick={() => {
                setWorkerComment('');
                setWorkerPhotos([]);
                goBackFromDetail(true);
              }}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 active:scale-95 text-xs uppercase tracking-wider shadow-md shadow-sm"
            >
              Retour aux interventions
            </button>
          )}
          </div>
        </div>
        )}

        {/* MODERN SLIDING BOTTOM SHEET */}
        <>
          {(isFeedbackSheetOpen || isFeedbackClosing) && (
            <>
              {/* Overlay Backdrop */}
              <div
                onClick={handleCloseFeedbackSheet}
                style={{
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', zIndex: 40,
                  animation: isFeedbackClosing
                    ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />

              {/* Sliding Bottom Sheet Container */}
              <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-50 transition-all duration-300`}>
              <div className="max-w-xl mx-auto" style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '24px 24px 0 0',
                  padding: '12px 24px 0',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                  animation: isFeedbackClosing
                    ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 -8px 32px rgba(15,23,42,0.15)',
                  display: 'flex', flexDirection: 'column', gap: '20px',
                }}>
                {/* Visual Drag Handle */}
                <div className="flex justify-center -mt-2 pb-2">
                  <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between pb-2 border-b border-slate-50">
                  <div>
                    <h3 className="text-base font-black text-slate-900 my-1">
                      {editingMemoId ? 'Modifier vos notes' : 'Observations & Photos'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseFeedbackSheet}
                    className="w-9 h-9 rounded-full bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center transition-all border border-slate-100"
                    style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Mémos rapides 1-clic */}
                <div className="space-y-2">
                  <span className="text-sm uppercase font-black text-slate-400 tracking-widest block">Mémos rapides</span>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { text: "C'est fait !", emoji: '✅' },
                      { text: 'Problème résolu', emoji: '🔧' },
                      { text: 'Nettoyage OK', emoji: '🧽' },
                    ].map((preset) => (
                      <button
                        key={preset.text}
                        type="button"
                        onClick={() => setWorkerComment(preset.text)}
                        className={`py-2 px-3 rounded-2xl text-sm font-bold border transition-all active:scale-95 flex items-center gap-1.5 ${
                          workerComment === preset.text
                            ? 'bg-emerald-600 border-emerald-600 text-white'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                        style={{ backgroundImage: 'none', backgroundColor: workerComment === preset.text ? '#059669' : '#ffffff' }}
                      >
                        <span>{preset.emoji}</span>
                        {preset.text}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Textarea */}
                <div className="space-y-1.5">
                  <span className="text-sm uppercase font-black text-slate-800 tracking-widest block">Note de clôture ou remarque</span>
                  <textarea
                    value={workerComment}
                    onChange={(e) => setWorkerComment(e.target.value)}
                    rows={3}
                    placeholder="Tapez un message ci-dessus..."
                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl text-base font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none shadow-sm"
                    style={{ minHeight: '80px' }}
                  />
                </div>

                {/* Photos */}
				<PhotoUploadSelector
					photos={workerPhotos}
					onPhotosChange={(urls) => setWorkerPhotos(urls)}
					onUploadingChange={(count) => setIsWorkerPhotosUploading(count > 0)}
				/>

                {/* Actions */}
                <div className="space-y-2 pt-1">
                  {!editingMemoId && ticket.status !== Status.DONE && (
                    <button
                      type="button"
                      disabled={isWorkerPhotosUploading}
                      onClick={() => {
                        const finalComment = workerComment.trim() || "C'est fait !";
                        handleStatusChange(ticket.status, Status.DONE, finalComment);
                      }}
                      className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black rounded-2xl shadow-md transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-widest active:scale-95"
                      style={{ backgroundImage: 'none', backgroundColor: '#059669' }}
                    >
                      <CheckCircleIcon className="w-5 h-5" />
                      {isWorkerPhotosUploading ? 'Envoi photo en cours…' : 'Valider & Clôturer la tâche'}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={(!workerComment.trim() && workerPhotos.length === 0) || isWorkerPhotosUploading}
                    onClick={() => {
                      handleAddCommentOnly();
                      closeFeedbackSheet();
                    }}
                    className={editingMemoId
                      ? "w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-black rounded-2xl shadow-md transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-widest active:scale-95"
                      : "w-full py-3 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-700 font-bold border border-blue-200 rounded-2xl transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider active:scale-95"}
                    style={{ backgroundImage: 'none', backgroundColor: editingMemoId ? '#2563eb' : '#eff6ff' }}
                  >
                    <AnnotationIcon className="w-4 h-4" />
                    {editingMemoId ? 'Enregistrer la modification' : 'Publier sans clôturer'}
                  </button>
                </div>
              </div>
              </div>
            </>
          )}
        </>

        {/* PAUSE REASON ACTION SHEET */}
        {(isPauseSheetOpen || isPauseSheetClosing) && (
          <>
            <div
              onClick={closePauseSheet}
              style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', zIndex: 60,
                animation: isPauseSheetClosing
                  ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
            <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} transition-all duration-300`} style={{ zIndex: 70 }}>
              <div
                className="max-w-xl mx-auto"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '24px 24px 0 0',
                  padding: '12px 24px 0',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                  animation: isPauseSheetClosing
                    ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 -8px 32px rgba(15,23,42,0.15)',
                  display: 'flex', flexDirection: 'column', gap: '16px',
                }}
              >
                <div className="flex justify-center -mt-2 pb-1">
                  <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
                </div>

                <div className="flex items-center justify-between pb-1 border-b border-slate-50">
                  <div>
                    <h3 className="text-base font-black text-slate-900 my-1">Mettre en pause</h3>
                    <p className="text-xs text-slate-500 font-medium my-0">Pourquoi cette mise en pause ? (optionnel)</p>
                  </div>
                  <button
                    type="button"
                    onClick={closePauseSheet}
                    aria-label="Annuler la mise en pause"
                    className="w-11 h-11 rounded-full bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center transition-all border border-slate-100 shrink-0"
                    style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2.5">
                  {[
                    { emoji: '🔩', text: 'Pièce / matériel manquant' },
                    { emoji: '🚪', text: 'Client absent' },
                    { emoji: '⏳', text: 'Reporté à plus tard' },
                  ].map(({ emoji, text }) => (
                    <button
                      key={text}
                      type="button"
                      onClick={() => handlePauseWithReason(`Intervention mise en pause : ${text.toLowerCase()}`)}
                      className="w-full h-14 bg-white border border-slate-200 hover:bg-slate-50 rounded-2xl font-bold text-sm text-slate-700 flex items-center gap-3 px-5 transition-all active:scale-95 text-left"
                      style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                    >
                      <span className="text-lg">{emoji}</span>
                      {text}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => handlePauseWithReason('Intervention mise en pause')}
                    className="w-full h-14 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-2xl font-black text-sm text-amber-700 uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95"
                    style={{ backgroundImage: 'none', backgroundColor: '#fffbeb' }}
                  >
                    <PauseIcon className="w-5 h-5 text-amber-600 fill-amber-600" />
                    Passer et mettre en pause
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CONFIRM DISCARD ACTION SHEET */}
        {(isConfirmDiscardOpen || isConfirmDiscardClosing) && (
          <>
            <div
              onClick={closeConfirmDiscard}
              style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.4)', zIndex: 60,
                animation: isConfirmDiscardClosing
                  ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
            <div
              style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 70,
                backgroundColor: '#f1f5f9',
                borderRadius: '24px 24px 0 0',
                padding: '8px 16px',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                animation: isConfirmDiscardClosing
                  ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: '0 -8px 32px rgba(15,23,42,0.2)',
              }}
            >
              <div className="flex justify-center py-3">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <div className="text-center px-4 pb-5">
                <p className="text-base font-black text-slate-900"><span role="img" aria-label="attention">⚠️</span> Note non sauvegardée</p>
                <p className="text-sm text-slate-500 mt-1.5">Abandonner les données saisies ?</p>
              </div>
              <div className="flex flex-col gap-2 pb-2">
                <button
                  type="button"
                  onClick={doDiscardAndClose}
                  className="w-full py-4 rounded-2xl text-sm font-bold text-red-600 bg-white active:scale-95 transition-all"
                  style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                >
                  🗑 Abandonner
                </button>
                <button
                  type="button"
                  onClick={closeConfirmDiscard}
                  className="w-full py-4 rounded-2xl text-sm font-bold text-slate-600 bg-white active:scale-95 transition-all"
                  style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                >
                  Continuer à éditer
                </button>
              </div>
            </div>
          </>
        )}

        {/* READ-ONLY DETAILS BOTTOM SHEET */}
        <>
          {(isReadOnlyDetailsOpen || isReadOnlyClosing) && (
            <>
              {/* Overlay Backdrop */}
              <div
                onClick={closeReadOnly}
                style={{
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', zIndex: 40,
                  animation: isReadOnlyClosing
                    ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />

              {/* Sliding Bottom Sheet Container */}
              <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-50 transition-all duration-300`}>
              <div className="max-w-xl mx-auto" style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '24px 24px 0 0',
                  padding: '12px 24px 0',
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                  animation: isReadOnlyClosing
                    ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 -8px 32px rgba(15,23,42,0.15)',
                  display: 'flex', flexDirection: 'column', gap: '20px',
                  maxHeight: '85vh', overflowY: 'auto',
                }}>
                {/* Drag Handle */}
                <div className="flex justify-center -mt-2 pb-2">
                  <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="text-lg font-black text-slate-900 my-0">
                      Caractéristiques du Ticket
                    </h3>
                  </div>
                  <button 
                    type="button"
                    onClick={closeReadOnly}
                    className="w-9 h-9 rounded-full bg-slate-50 text-slate-500 hover:bg-slate-100 flex items-center justify-center transition-all border border-slate-100"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Structured Detailed Information */}
                <div className="space-y-4">
                  
                  {/* Category & Equipment */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Catégorie</span>
                      <p className="text-sm font-bold text-slate-800">{ticket.category || 'Non spécifiée'}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Équipement visé</span>
                      <p className="text-sm font-bold text-slate-800">{ticket.equipment || 'Aucun équipement'}</p>
                    </div>
                  </div>

                  {/* Deadline & Duration */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Échéance</span>
                      <p className="text-sm font-bold text-slate-800">
                        {ticket.deadline ? new Date(ticket.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Aucune date limite'}
                      </p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Durée estimée</span>
                      <p className="text-sm font-bold text-slate-800">{ticket.estimatedDuration || 'Non estimée'}</p>
                    </div>
                  </div>

                  {/* Origine & Accord */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Origine</span>
                      <p className="text-sm font-bold text-slate-800">{ticket.isCustomerRequest ? 'Demande Client' : 'Interne'}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                      <span className="text-xs uppercase font-black text-slate-400 tracking-wider block mb-1">Accord d'absence</span>
                      <p className="text-sm font-bold text-slate-800">
                        {ticket.isCustomerRequest
                          ? (ticket.customerPermission ? 'AUTORISÉ (Absence)' : 'REQUIS (Présence)')
                          : 'Non applicable'}
                      </p>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 font-sans">
                    <div className="flex items-center justify-between text-xs uppercase font-black text-slate-400 tracking-wider mb-2">
                      <span>Créateur</span>
                      <span>Date de création</span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold text-slate-800">
                      <span className="text-blue-600">{getTicketCreator(ticket)}</span>
                      <span className="text-slate-500 font-mono">
                        {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Fully formatted notes */}
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-1">
                    <span className="text-xs uppercase font-black text-slate-400 tracking-wider block">Description complète</span>
                    <p className="text-sm text-slate-600 leading-relaxed font-semibold italic">
                      "{ticket.description || ticket.label}"
                    </p>
                  </div>

                </div>

                {/* Actions */}
                {canWrite && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsReadOnlyDetailsOpen(false);
                        setIsEditBottomSheetOpen(true);
                      }}
                      className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold rounded-2xl shadow-lg transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 active:scale-95"
                      style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
                    >
                      <PencilIcon className="w-4 h-4" />
                      Modifier
                    </button>
                  </div>
                )}
              </div>
              </div>
            </>
          )}
        </>

        {/* BOTTOM SHEET — MODIFIER (REQUALIFICATION) */}
        {(isEditBottomSheetOpen || isEditClosing) && (
          <>
            <div
              onClick={() => {
                setIsEditBottomSheetOpen(false);
                setIsReadOnlyDetailsOpen(true);
              }}
              style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', zIndex: 40,
                animation: isEditClosing
                  ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
            <div className={`fixed bottom-0 left-0 right-0 ${footerLeft} z-50 transition-all duration-300`}>
            <div className="max-w-xl mx-auto" style={{
                backgroundColor: '#ffffff',
                borderRadius: '24px 24px 0 0',
                height: '85vh',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                animation: isEditClosing
                  ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: '0 -8px 32px rgba(15,23,42,0.15)',
              }}>
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                <TicketEditView
                  ticket={ticket}
                  currentUserName={userName}
                  onSave={(updatedTicket) => {
                    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));
                    syncTicket(updatedTicket);
                    closeEdit();
                    setIsReadOnlyDetailsOpen(false);
                  }}
                  onBack={() => {
                    setIsEditBottomSheetOpen(false);
                    setIsReadOnlyDetailsOpen(true);
                  }}
                />
              </div>
            </div>
            </div>
          </>
        )}

      </div>
    );
  }

  function renderDoneSuccess() {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-900 text-white overflow-y-auto">
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div
            className="w-24 h-24 bg-emerald-500 rounded-3xl flex items-center justify-center mb-8 shadow-2xl"
            style={{ animation: 'popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          >
            <CheckCircleIcon className="w-12 h-12 text-white" />
          </div>

          <div style={{ animation: 'fadeUpIn 0.4s ease 0.25s both' }}>
            <h3 className="text-3xl font-black mt-0 mb-3 tracking-tight">Mission accomplie !</h3>
            <p className="text-sm text-slate-400 mb-10 leading-relaxed font-medium" style={{ maxWidth: 240, margin: '0 auto 40px' }}>
              L'intervention a été marquée comme terminée. Bon travail !
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full max-w-xs" style={{ animation: 'fadeUpIn 0.4s ease 0.4s both' }}>
            <button
              onClick={() => { setUndoDoneInfo(null); setView('HOME'); setSelectedTicketId(null); }}
              className="w-full py-5 bg-white text-slate-900 rounded-3xl font-extrabold shadow-xl active:scale-95 transition-all text-lg"
              style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
            >
              Retour au tableau
            </button>
            {undoDoneInfo && undoCountdown > 0 && (
              <button
                type="button"
                onClick={handleUndoDone}
                className="w-full py-3 text-slate-400 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-1.5 active:scale-95"
                style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
              >
                ↩ Annuler la clôture ({undoCountdown}s)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }


  // --- Desktop Renderers ---

  function renderDesktopWelcome() {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 text-center p-12">
        <div className="w-16 h-16 bg-blue-50 rounded-3xl flex items-center justify-center mb-6 shadow-sm">
          <ClipboardListIcon className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-xl font-black text-slate-800 mt-0 mb-2">
          {establishment || 'Gestion des interventions'}
        </h2>
        <p className="text-sm text-slate-500 font-medium max-w-xs mb-8">
          Sélectionnez une intervention dans la liste pour consulter son détail.
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-xs w-full">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 text-center shadow-sm">
            <p className="text-2xl font-black text-slate-800">{stats.todo + stats.progress}</p>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Actives</p>
          </div>
          <div className="bg-white border border-red-100 rounded-2xl p-4 text-center shadow-sm">
            <p className="text-2xl font-black text-red-600">{stats.urgent}</p>
            <p className="text-xs font-bold text-red-500 uppercase tracking-widest mt-1">Urgentes</p>
          </div>
        </div>
      </div>
    )
  }

  function renderDesktopTicketDetail() {
    const ticket = tickets.find(t => t.id === selectedTicketId)
    if (!ticket) return renderDesktopWelcome()

    const assignedName = (ticket.assignedTo && ticket.assignedTo.name) || 'Non assigné'

    const statusLabels = {
      [Status.TODO]: 'À faire',
      [Status.IN_PROGRESS]: 'En cours',
      [Status.BLOCKED]: 'En pause',
      [Status.DONE]: 'Terminé'
    }

    const getInitials = (nameStr) => {
      return ((nameStr || 'S').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().substring(0, 2)) || 'S'
    }

    const handleStatusChange = (fromStatus, toStatus, fallbackDetail) => {
      const statusEvent = {
        id: Math.random().toString(36).substr(2, 5),
        type: 'status_change',
        user: userName,
        timestamp: new Date().toISOString(),
        from: fromStatus,
        to: toStatus,
        details: fallbackDetail
      }
      const memoEvents = []
      if (workerComment.trim() || workerPhotos.length > 0)
      {
        memoEvents.push({
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo',
          user: userName,
          timestamp: new Date().toISOString(),
          details: workerComment.trim() || "📷 Photo jointe",
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        })
      }
      const updated = { ...ticket, status: toStatus, history: [...(ticket.history || []), statusEvent, ...memoEvents] }
      setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t))
      syncTicket(updated)
      setWorkerComment('')
      setWorkerPhotos([])
      setEditingMemoId(null)
      setActiveQuickAction(null)
      if (toStatus === Status.DONE)
      {
        setUndoDoneInfo({ ticketId: ticket.id, previousStatus: fromStatus })
        setView('DONE_SUCCESS')
      }
    }

    const handleAddCommentOnly = () => {
      if (!workerComment.trim() && workerPhotos.length === 0) return
      let updated
      if (editingMemoId)
      {
        updated = {
          ...ticket,
          history: (ticket.history || []).map(h => h.id === editingMemoId ? {
            ...h,
            details: workerComment.trim() || "📷 Photo jointe",
            photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
          } : h)
        }
      }
      else
      {
        const event = {
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo',
          user: userName,
          timestamp: new Date().toISOString(),
          details: workerComment.trim() || "📷 Photo jointe",
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        }
        updated = { ...ticket, history: [...(ticket.history || []), event] }
      }
      setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t))
      syncTicket(updated)
      setWorkerComment('')
      setWorkerPhotos([])
      setEditingMemoId(null)
    }

    const handleUpdateStatusDirectly = (newStatus) => {
      let detailDesc = 'Changement de statut'
      if (newStatus === Status.TODO)        detailDesc = 'Remis en dossier À Faire'
      if (newStatus === Status.IN_PROGRESS) detailDesc = "Début de l'intervention"
      if (newStatus === Status.BLOCKED)     detailDesc = 'Intervention mise en pause'
      if (newStatus === Status.DONE)        detailDesc = 'Intervention terminée'
      const statusEvent = {
        id: Math.random().toString(36).substr(2, 5),
        type: 'status_change',
        user: userName,
        timestamp: new Date().toISOString(),
        from: ticket.status,
        to: newStatus,
        details: detailDesc
      }
      const memoEvents = []
      if (workerComment.trim() || workerPhotos.length > 0)
      {
        memoEvents.push({
          id: Math.random().toString(36).substr(2, 5),
          type: 'memo',
          user: userName,
          timestamp: new Date().toISOString(),
          details: workerComment.trim() || "📷 Photo jointe",
          photoUrls: workerPhotos.length > 0 ? workerPhotos : undefined
        })
      }
      const updated = { ...ticket, status: newStatus, history: [...(ticket.history || []), statusEvent, ...memoEvents] }
      setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t))
      syncTicket(updated)
      setWorkerComment('')
      setWorkerPhotos([])
      setEditingMemoId(null)
      closeQuickAction()
      if (newStatus === Status.DONE)
      {
        setUndoDoneInfo({ ticketId: ticket.id, previousStatus: ticket.status })
        setView('DONE_SUCCESS')
      }
      else
        showQuickActionToast(`Statut mis à jour : ${statusLabels[newStatus] || newStatus}`)
    }

    const handleAssignDirectly = (type, id, name) => {
      const event = {
        id: Math.random().toString(36).substr(2, 5),
        type: 'status_change',
        user: userName,
        timestamp: new Date().toISOString(),
        details: `Assigné à ${name}`
      }
      const updated = { ...ticket, assignedTo: { type, id, name }, history: [...(ticket.history || []), event] }
      setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t))
      syncTicket(updated)
      closeQuickAction()
      showQuickActionToast(`Assigné à ${name}`)
    }

    const handleDeadlineDirectly = (dateStr) => {
      const event = {
        id: Math.random().toString(36).substr(2, 5),
        type: 'status_change',
        user: userName,
        timestamp: new Date().toISOString(),
        details: dateStr
          ? `Échéance modifiée au ${new Date(dateStr).toLocaleDateString('fr-FR')}`
          : 'Échéance supprimée'
      }
      const updated = {
        ...ticket,
        deadline: dateStr ? new Date(dateStr).toISOString() : undefined,
        history: [...(ticket.history || []), event]
      }
      setTickets(prev => prev.map(t => t.id === ticket.id ? updated : t))
      syncTicket(updated)
      closeQuickAction()
      showQuickActionToast(dateStr
        ? `Échéance fixée au ${new Date(dateStr).toLocaleDateString('fr-FR')}`
        : 'Échéance supprimée')
    }

    const isAssigned = (type, id) =>
      ticket.assignedTo && ticket.assignedTo.type === type && ticket.assignedTo.id === id

    const memos = (ticket.history || []).filter(h => h.type === 'memo')
    const historyEvents = ([...(ticket.history || []), {
      id: 'legacy-creation',
      type: 'creation',
      user: getTicketCreator(ticket),
      timestamp: ticket.createdAt,
      details: "Création du ticket d'intervention"
    }])
      .filter(ev => ev.type !== 'memo')
      .filter((ev, idx, self) =>
        ev.id !== 'legacy-creation' || !self.some(s => s.type === 'creation' && s.id !== 'legacy-creation')
      )
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

    const HISTORY_PREVIEW_COUNT = 5
    const visibleHistoryEvents = isHistoryExpanded ? historyEvents : historyEvents.slice(0, HISTORY_PREVIEW_COUNT)
    const hiddenHistoryCount = historyEvents.length - HISTORY_PREVIEW_COUNT

    const historyTitle = (event) => {
      if (event.type === 'creation') return 'Ticket créé'
      if (event.details === 'Clôture annulée') return 'Clôture annulée'
      if (event.to === Status.IN_PROGRESS) return "Début de l'intervention"
      if (event.to === Status.BLOCKED)     return 'Intervention mise en pause'
      if (event.to === Status.DONE)        return 'Intervention terminée'
      return event.details || "Mise à jour d'information"
    }

    return (
      <div className="flex flex-col h-full bg-white overflow-hidden text-left">

        {/* Header — bloc unique : identité + méta à gauche, rail d'actions à droite */}
        <div className="bg-white border-b border-slate-200 flex-shrink-0 px-5 py-5 flex items-center justify-between gap-4">

          {/* Identité + métadonnées — tags au-dessus, grand titre, ligne de contexte */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-black uppercase tracking-widest flex-shrink-0 ${
                ticket.type === 'Technique' ? 'bg-indigo-50 text-indigo-700' :
                ticket.type === 'Ménage'    ? 'bg-teal-50 text-teal-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {ticket.type}
              </span>
              {ticket.urgency !== Urgency.NORMAL && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-black uppercase tracking-widest flex-shrink-0 ${
                  ticket.urgency === Urgency.CRITICAL ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'
                }`}>
                  ⚡ {ticket.urgency === Urgency.CRITICAL ? 'Urgent' : 'Important'}
                </span>
              )}
              {ticket.isCustomerRequest && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-black uppercase tracking-widest flex-shrink-0 bg-blue-50 text-blue-700">
                  💬 Demande client
                </span>
              )}
            </div>
            <h1 className="text-xl font-black text-slate-900 leading-tight truncate mt-1.5" style={{ marginBottom: 0 }}>
              <span className="font-mono">{getLieuName(ticket.locationId)}</span>
              <span className="text-slate-300 font-bold"> · </span>
              {ticket.description || ticket.label}
            </h1>
            <p className="text-xs text-slate-400 font-semibold truncate mt-1 my-0">
              Créée par <span className="text-slate-600 font-bold">{getTicketCreator(ticket)}</span>
              {' '}{(() => { const l = formatDayLabel(ticket.createdAt); return /\d/.test(l) ? 'le ' : '' })()}<span className="font-mono">{(() => { const l = formatDayLabel(ticket.createdAt); return /\d/.test(l) ? l : l.toLowerCase() })()} à {new Date(ticket.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
              {ticket.isCustomerRequest && ticket.createdUserName && (
                <span> · signalée par <span className="text-slate-600 font-bold">{ticket.createdUserName}</span></span>
              )}
            </p>
          </div>

          {/* Rail d'actions — secondaires, séparateur, puis CTA */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {canWrite && (
              <>
                <button
                  type="button"
                  onClick={() => setView('EDIT')}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-800 font-semibold text-xs flex items-center gap-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundImage: 'none', backgroundColor: 'white' }}
                  aria-label="Modifier l'intervention"
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(ticket.id)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-200 text-red-600 hover:text-red-700 font-semibold text-xs flex items-center gap-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400"
                  style={{ backgroundImage: 'none', backgroundColor: 'white' }}
                  aria-label="Supprimer l'intervention"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                  Supprimer
                </button>
                <div className="w-px h-6 bg-slate-200 mx-1" aria-hidden="true" />
              </>
            )}
            <button
              type="button"
              onClick={() => setIsObservationsDrawerOpen(true)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-700 font-semibold text-xs flex items-center gap-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ backgroundImage: 'none', backgroundColor: 'white' }}
              aria-label="Ouvrir les notes et observations"
            >
              <AnnotationIcon className="w-3.5 h-3.5" />
              <span>Notes</span>
              {memos.length > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-700 text-white font-black leading-none text-3xs">{memos.length}</span>
              )}
            </button>

            {canActOnTicket(ticket) && ticket.status === Status.TODO && (
              <button
                type="button"
                onClick={() => handleStatusChange(Status.TODO, Status.IN_PROGRESS, "Démarrage de l'intervention")}
                className="px-4 py-1.5 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-colors duration-200 flex items-center gap-1.5 active:scale-95 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
              >
                <PlayIcon className="w-3.5 h-3.5" />
                Démarrer
              </button>
            )}
            {canActOnTicket(ticket) && ticket.status === Status.IN_PROGRESS && (
              <>
                <button
                  type="button"
                  onClick={() => handleStatusChange(Status.IN_PROGRESS, Status.BLOCKED, 'Intervention mise en pause')}
                  className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 font-bold text-xs uppercase tracking-widest rounded-lg transition-colors duration-200 flex items-center gap-1.5 active:scale-95 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                  style={{ backgroundImage: 'none', backgroundColor: '#fffbeb' }}
                >
                  <PauseIcon className="w-3.5 h-3.5" />
                  Pause
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChange(Status.IN_PROGRESS, Status.DONE, 'Intervention terminée avec succès')}
                  className="px-4 py-1.5 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-colors duration-200 flex items-center gap-1.5 active:scale-95 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                  style={{ backgroundImage: 'none', backgroundColor: '#059669' }}
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Terminer
                </button>
              </>
            )}
            {canActOnTicket(ticket) && ticket.status === Status.BLOCKED && (
              <button
                type="button"
                onClick={() => handleStatusChange(Status.BLOCKED, Status.IN_PROGRESS, "Reprise de l'intervention")}
                className="px-4 py-1.5 text-white font-extrabold text-xs uppercase tracking-widest rounded-lg transition-colors duration-200 flex items-center gap-1.5 active:scale-95 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
              >
                <PlayIcon className="w-3.5 h-3.5" />
                Reprendre
              </button>
            )}
          </div>

        </div>

        {/* Contenu principal */}
        <div className="flex-1 overflow-hidden">

          <div className="p-6 overflow-y-auto space-y-6 h-full">

            {/* Barre de propriétés — remplace le bento 4 cartes et les cartes « Actions rapides » */}
            {(() => {
              const lieu = lieux.find(l => l.id === ticket.locationId)
              const lieuSub = lieu ? (lieu.zone || lieu.special_type || (lieu.type === LocationType.LOCATIF ? 'Emplacement' : lieu.type === LocationType.EQUIPMENT ? 'Équipement' : 'Zone commune')) : null
              const isLate = ticket.status !== Status.DONE && isDeadlinePast(ticket.deadline)
              const openCharge = ticket.assignedTo
                ? tickets.filter(t => t.status !== Status.DONE && t.assignedTo && t.assignedTo.type === ticket.assignedTo.type && t.assignedTo.id === ticket.assignedTo.id).length
                : 0
              const propBase = 'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold transition-colors duration-200'
              const kStyle = 'text-3xs uppercase tracking-widest font-black text-slate-400'
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`${propBase} bg-slate-100 text-slate-600`}>
                    <span className={kStyle}>Lieu</span>
                    <LocationMarkerIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="font-mono font-bold text-slate-800">{getLieuName(ticket.locationId)}</span>
                    {lieuSub && <span className="text-slate-400 font-semibold">· {lieuSub}</span>}
                  </span>
                  {canWrite ? (
                    <button
                      type="button"
                      onClick={() => activeQuickAction === 'assign' ? closeQuickAction() : setActiveQuickAction('assign')}
                      aria-expanded={activeQuickAction === 'assign'}
                      className={`${propBase} border cursor-pointer ${activeQuickAction === 'assign' ? 'border-blue-500 ring-1 ring-blue-200 text-slate-700' : 'border-transparent bg-slate-100 text-slate-600 hover:border-slate-300'}`}
                      style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
                    >
                      <span className={kStyle}>Assigné</span>
                      <UserIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-800">{assignedName}</span>
                      {ticket.assignedTo && openCharge > 0 && (
                        <span className="text-slate-400 font-semibold">· {openCharge} ouvert{openCharge > 1 ? 's' : ''}</span>
                      )}
                      <PencilIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    </button>
                  ) : (
                    <span className={`${propBase} bg-slate-100 text-slate-600`}>
                      <span className={kStyle}>Assigné</span>
                      <UserIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-800">{assignedName}</span>
                    </span>
                  )}
                  {canWrite ? (
                    <button
                      type="button"
                      onClick={() => activeQuickAction === 'deadline' ? closeQuickAction() : setActiveQuickAction('deadline')}
                      aria-expanded={activeQuickAction === 'deadline'}
                      className={`${propBase} border cursor-pointer ${isLate ? 'text-red-600' : 'text-slate-600'} ${activeQuickAction === 'deadline' ? 'border-blue-500 ring-1 ring-blue-200' : 'border-transparent hover:border-slate-300'}`}
                      style={{ backgroundImage: 'none', backgroundColor: isLate ? '#fde3e3' : '#f1f5f9' }}
                    >
                      <span className={`text-3xs uppercase tracking-widest font-black ${isLate ? 'text-red-400' : 'text-slate-400'}`}>Échéance</span>
                      <CalendarIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isLate ? 'text-red-400' : 'text-slate-400'}`} />
                      <span className={`font-mono ${isLate ? 'text-red-600' : 'text-slate-800'}`}>
                        {ticket.deadline ? formatDayLabel(ticket.deadline) : 'Sans limite'}
                      </span>
                      {isLate && <span className="font-black">· dépassée</span>}
                      <PencilIcon className={`w-3 h-3 flex-shrink-0 ${isLate ? 'text-red-400' : 'text-slate-400'}`} />
                    </button>
                  ) : (
                    <span className={`${propBase} ${isLate ? 'text-red-600' : 'bg-slate-100 text-slate-600'}`} style={isLate ? { backgroundColor: '#fde3e3' } : {}}>
                      <span className={kStyle}>Échéance</span>
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-mono">{ticket.deadline ? formatDayLabel(ticket.deadline) : 'Sans limite'}</span>
                    </span>
                  )}
                  {ticket.estimatedDuration && (
                    <span className={`${propBase} bg-slate-100 text-slate-600`}>
                      <span className={kStyle}>Durée est.</span>
                      <ClockIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-mono text-slate-800">{ticket.estimatedDuration}</span>
                    </span>
                  )}
                </div>
              )
            })()}

            {/* Statut — contrôle segmenté directement manipulable (assigné ou écriture) */}
            {canActOnTicket(ticket) && (
              <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-white" role="group" aria-label="Changer le statut">
                {[
                  { s: Status.TODO,        label: 'À faire',  bg: '#e8eeff', fg: '#1a41b8' },
                  { s: Status.IN_PROGRESS, label: 'En cours', bg: '#fdf0cf', fg: '#a35207' },
                  { s: Status.BLOCKED,     label: 'En pause', bg: '#eef1f7', fg: '#43506e' },
                  { s: Status.DONE,        label: 'Terminé',  bg: '#d3f6e7', fg: '#067a52' },
                ].map(({ s, label, bg, fg }, idx) => {
                  const on = ticket.status === s
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => { if (!on) handleUpdateStatusDirectly(s) }}
                      aria-pressed={on}
                      disabled={on}
                      className={`px-4 py-2 text-xs font-black flex items-center gap-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${idx > 0 ? 'border-l border-slate-200' : ''} ${on ? '' : 'hover:bg-slate-50'}`}
                      style={{ backgroundImage: 'none', backgroundColor: on ? bg : 'white', color: on ? fg : '#94a3b8', cursor: on ? 'default' : 'pointer' }}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${on ? 'animate-pulse motion-reduce:animate-none' : ''}`} style={{ backgroundColor: 'currentColor', opacity: on ? 1 : 0.4 }} />
                      {label}
                    </button>
                  )
                })}
              </div>
            )}


            {/* Panneau contextuel (assigner / échéance) — ouvert depuis les chips de propriétés */}
            {canWrite && <div className="space-y-2">

              {activeQuickAction && (
                <motion.div
                  initial={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
                  animate={isQuickActionClosing
                    ? { opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }
                    : { opacity: 1, height: 'auto', paddingTop: 20, paddingBottom: 20 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden bg-slate-100 border border-slate-200 rounded-2xl px-5 mt-3"
                >
                  {/* Assigner */}
                  {activeQuickAction === 'assign' && (
                    <div className="space-y-4">
                      <div>
                        <span className="text-xs uppercase font-bold text-slate-500 tracking-widest block mb-2">Équipes</span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {teams.map(team => {
                            const sel = ticket.assignedTo && ticket.assignedTo.type === 'team' && ticket.assignedTo.name === team
                            return (
                              <button
                                key={team}
                                type="button"
                                onClick={() => handleAssignDirectly('team', team, team)}
                                className="py-2 px-3 border rounded-xl font-bold text-xs transition-colors duration-200 flex items-center justify-center gap-1.5 min-w-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={{ backgroundImage: 'none', backgroundColor: sel ? '#2563eb' : 'white', color: sel ? 'white' : '#334155', borderColor: sel ? '#2563eb' : '#e2e8f0' }}
                              >
                                <UserGroupIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{team}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {employees.length > 0 && (
                        <div>
                          <span className="text-xs uppercase font-bold text-slate-500 tracking-widest block mb-2">Individuel</span>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {employees.map(emp => {
                              const sel = isAssigned('employee', emp.id)
                              return (
                                <button
                                  key={emp.id}
                                  type="button"
                                  onClick={() => handleAssignDirectly('employee', emp.id, emp.name)}
                                  className="py-2 px-3 border rounded-xl font-bold text-xs transition-colors duration-200 flex items-center justify-center gap-1.5 min-w-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  style={{ backgroundImage: 'none', backgroundColor: sel ? '#2563eb' : 'white', color: sel ? 'white' : '#334155', borderColor: sel ? '#2563eb' : '#e2e8f0' }}
                                >
                                  <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate">{emp.name}</span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Échéance */}
                  {activeQuickAction === 'deadline' && (
                    <div className="space-y-3">
                      <span className="text-xs uppercase font-bold text-slate-500 tracking-widest block">Définir la date limite</span>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="flex gap-2 flex-wrap">
                          {[
                            { label: "Aujourd'hui", fn: () => handleDeadlineDirectly(new Date().toISOString().split('T')[0]) },
                            { label: 'Demain', fn: () => { const d = new Date(); d.setDate(d.getDate() + 1); handleDeadlineDirectly(d.toISOString().split('T')[0]) } },
                            { label: 'Semaine prochaine', fn: () => { const d = new Date(); d.setDate(d.getDate() + 7); handleDeadlineDirectly(d.toISOString().split('T')[0]) } },
                          ].map(({ label, fn }) => (
                            <button
                              key={label}
                              type="button"
                              onClick={fn}
                              className="py-2 px-4 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                              style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                            >
                              {label}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleDeadlineDirectly(null)}
                            className="py-2 px-4 bg-white hover:bg-slate-50 border border-slate-200 text-slate-500 font-bold text-xs rounded-xl transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
                          >
                            Sans limite
                          </button>
                        </div>
                        <input
                          type="date"
                          value={ticket.deadline ? ticket.deadline.split('T')[0] : ''}
                          onChange={(e) => handleDeadlineDirectly(e.target.value)}
                          aria-label="Choisir une date limite"
                          className="browser-default flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-base font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ height: 'auto', margin: 0, boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>}

            {/* Corps — deux colonnes : contenu principal + rail latéral délimité */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            <div className="xl:col-span-2 space-y-6 min-w-0">

            {/* Description */}
            <div className="space-y-2">
              <span className="text-xs uppercase font-black text-slate-500 tracking-widest block pl-1">Description</span>
              <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: '#fffdf4', border: '1px solid #f1e7c8', borderLeft: '3px solid #e7c352' }}>
                <p className="text-sm text-slate-700 leading-relaxed font-semibold">
                  {ticket.description || "Aucune description détaillée n'a été spécifiée."}
                </p>
                {ticket.photoUrls && ticket.photoUrls.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {ticket.photoUrls.map((url, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setLightboxUrl(url)}
                        aria-label={`Agrandir la photo ${idx + 1}`}
                        className="rounded-xl overflow-hidden border border-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ backgroundImage: 'none', backgroundColor: 'white', width: 160, height: 120, padding: 0 }}
                      >
                        <img src={thumbUrl(url)} alt={`Pièce jointe ${idx + 1}`} className="w-full h-full object-cover hover:opacity-90 transition-opacity duration-200" referrerPolicy="no-referrer" />
                      </button>
                    ))}
                  </div>
                )}
                {(ticket.equipment || ticket.isCustomerRequest) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                    {ticket.equipment && (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs uppercase font-black text-slate-500 tracking-widest">Matériel requis</span>
                        <div className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 self-start">
                          <CogIcon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          {ticket.equipment}
                        </div>
                      </div>
                    )}
                    {ticket.isCustomerRequest && (
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div>
                          <span className="text-xs uppercase font-black text-slate-500 tracking-widest block">Permissions d'accès</span>
                          <p className="text-xs text-slate-500 font-semibold">Entrée autorisée en l'absence du client</p>
                        </div>
                        <span className={`text-xs font-black px-3 py-1.5 rounded-full ${ticket.customerPermission ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                          {ticket.customerPermission ? 'OUI — ACCÈS AUTORISÉ' : 'NON — PRÉSENCE REQUISE'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Observations terrain — visibles directement dans la fiche */}
            <div className="space-y-2">
              <div className="flex items-center justify-between pl-1">
                <span className="text-xs uppercase font-black text-slate-500 tracking-widest">
                  Observations terrain{memos.length > 0 ? ` (${memos.length})` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setIsObservationsDrawerOpen(true)}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  Tout gérer →
                </button>
              </div>
              <div className="space-y-2">
                {memos.length === 0 && (
                  <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400 font-semibold">
                    Aucune observation pour le moment.
                  </div>
                )}
                {memos.slice(-3).map(memo => (
                  <div key={memo.id} className="bg-white rounded-2xl border border-slate-200 p-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center font-bold flex-shrink-0" style={{ fontSize: 9 }}>
                        {getInitials(memo.user || 'Système')}
                      </div>
                      <span className="text-xs font-black text-slate-800">{memo.user || 'Système'}</span>
                      <span className="ml-auto text-2xs font-mono font-bold text-slate-400">
                        {formatDayLabel(memo.timestamp)} · {new Date(memo.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 font-semibold leading-relaxed mt-1.5 my-0">{memo.details}</p>
                    {memo.photoUrls && memo.photoUrls.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {memo.photoUrls.map((url, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setLightboxUrl(url)}
                            aria-label={`Agrandir la photo ${i + 1}`}
                            className="rounded-xl overflow-hidden border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={{ backgroundImage: 'none', backgroundColor: 'transparent', padding: 0, width: 160, height: 120 }}
                          >
                            <img src={thumbUrl(url)} alt="" className="w-full h-full object-cover hover:opacity-90 transition-opacity duration-200" referrerPolicy="no-referrer" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {memos.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setIsObservationsDrawerOpen(true)}
                    className="w-full text-xs font-bold text-blue-600 hover:text-blue-700 py-1 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    Voir les {memos.length - 3} plus anciennes →
                  </button>
                )}
                {canActOnTicket(ticket) && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={workerComment}
                      onChange={(e) => setWorkerComment(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddCommentOnly() }}
                      placeholder="Ajouter une observation…"
                      className="browser-default flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{ height: 'auto', margin: 0, boxSizing: 'border-box' }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCommentOnly}
                      disabled={!workerComment.trim()}
                      className="px-4 rounded-xl text-white text-xs font-black uppercase tracking-wider transition-opacity duration-200 disabled:opacity-40"
                      style={{ backgroundImage: 'none', backgroundColor: '#0f172a' }}
                    >
                      Publier
                    </button>
                  </div>
                )}
              </div>
            </div>

            </div>
            <div className="space-y-6 rounded-2xl border border-slate-200 p-4 self-start" style={{ backgroundColor: '#fafbfe' }}>

            {/* Demande client liée */}
            {ticket.conversationId && (
              <button
                type="button"
                onClick={() => history.push('/messages/chat', { openConversationId: ticket.conversationId })}
                className="w-full flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-100 text-left group focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ backgroundImage: 'none', backgroundColor: '#eff6ff' }}
              >
                <div className="min-w-0">
                  <span className="text-xs uppercase font-black text-blue-600 tracking-widest block">💬 Demande client</span>
                  <p className="text-xs text-blue-500 font-semibold mt-0.5 my-0 truncate">
                    {ticket.createdUserName ? `Demande de ${ticket.createdUserName} · ` : ''}Ouvrir la conversation
                  </p>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-blue-500 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
              </button>
            )}

            {/* Historique */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 pl-1">
                <ClockIcon className="w-4 h-4 text-slate-400" />
                <span className="text-xs uppercase font-black text-slate-500 tracking-widest">Historique de suivi</span>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4 text-left">
                <div className="relative">
                  <div className="absolute w-px bg-slate-200" style={{ left: 5, top: 6, bottom: 6 }} />
                  <div className="space-y-3">
                    {visibleHistoryEvents.map((event, idx) => {
                      const dotColor = event.type === 'creation' ? '#2456e6'
                        : event.details === 'Clôture annulée' ? '#dc2626'
                        : event.to === Status.DONE ? '#059669'
                        : event.to === Status.IN_PROGRESS ? '#f59e0b'
                        : (event.details || '').indexOf('Assigné') === 0 ? '#7c3aed'
                        : '#b9c4dc'
                      const d = new Date(event.timestamp)
                      const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h')
                      const when = d.toDateString() === new Date().toDateString()
                        ? time
                        : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ' ' + time
                      return (
                        <div key={event.id || idx} className="relative pl-6">
                          <span className="absolute rounded-full bg-white z-10" style={{ left: 0, top: 3, width: 11, height: 11, border: `3px solid ${dotColor}` }} />
                          <p className="text-xs font-black text-slate-800 leading-tight my-0">{historyTitle(event)}</p>
                          <p className="text-2xs font-semibold text-slate-400 mt-0.5 my-0">
                            {event.user || 'Système'} · <span className="font-mono">{when}</span>
                          </p>
                          {event.details && event.details !== historyTitle(event) && (
                            <p className="text-xs text-slate-500 mt-1 my-0 italic">{event.details}</p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {hiddenHistoryCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsHistoryExpanded(prev => !prev)}
                    aria-expanded={isHistoryExpanded}
                    className="w-full mt-3 pt-3 border-t border-slate-100 text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1.5 transition-colors duration-200 cursor-pointer rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isHistoryExpanded ? 'rotate-180' : ''}`} />
                    {isHistoryExpanded
                      ? "Réduire l'historique"
                      : `Afficher tout l'historique (${hiddenHistoryCount} de plus)`}
                  </button>
                )}
              </div>
            </div>

            </div>
            </div>

          </div>

        </div>

        {/* OBSERVATIONS SIDE DRAWER — Desktop */}
        {isObservationsDrawerOpen && (
            <div className="fixed inset-0 overflow-hidden flex justify-end" style={{ zIndex: 60 }}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.45 }}
                transition={{ duration: 0.2 }}
                onClick={() => { setIsObservationsDrawerOpen(false); setEditingMemoId(null); setWorkerComment(''); setWorkerPhotos([]); }}
                className="absolute inset-0 bg-slate-950 cursor-pointer"
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 240 }}
                role="dialog"
                aria-modal="true"
                aria-label="Mémos et observations"
                className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col z-10"
              >
                {/* En-tête */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md" style={{ backgroundColor: '#0f172a' }}>
                      <AnnotationIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-800 uppercase tracking-tight my-0">Mémos & Observations</h3>
                      <p className="text-xs text-slate-400 font-bold block mt-1 uppercase">Suivi technique terrain</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setIsObservationsDrawerOpen(false); setEditingMemoId(null); setWorkerComment(''); setWorkerPhotos([]); }}
                    aria-label="Fermer les observations"
                    className="w-9 h-9 rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center border border-slate-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Corps */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6" style={{ backgroundColor: 'rgba(248,250,252,0.5)' }}>
                  {canActOnTicket(ticket) && ticket.status !== Status.DONE && (
                    <div className="space-y-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                      <div>
                        <span className="text-xs font-black uppercase tracking-wider block mb-2" style={{ color: '#2563eb' }}>
                          {editingMemoId ? "Modifier l'observation" : 'Nouvelle observation'}
                        </span>
                        <textarea
                          rows={4}
                          value={workerComment}
                          onChange={(e) => setWorkerComment(e.target.value)}
                          placeholder="Indiquez vos interventions, remarques, constatations..."
                          aria-label="Texte de l'observation"
                          className="browser-default w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-base text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 font-bold resize-none"
                          style={{ height: 'auto', margin: 0, boxSizing: 'border-box' }}
                        />
                      </div>
                      <PhotoUploadSelector
                        photos={workerPhotos}
                        onPhotosChange={setWorkerPhotos}
                        onUploadingChange={(count) => setIsWorkerPhotosUploading(count > 0)}
                      />
                      <div className="flex gap-2">
                        {editingMemoId && (
                          <button
                            type="button"
                            onClick={() => { setEditingMemoId(null); setWorkerComment(''); setWorkerPhotos([]); }}
                            className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                            style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                          >
                            Annuler
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleAddCommentOnly}
                          disabled={(!workerComment.trim() && workerPhotos.length === 0) || isWorkerPhotosUploading}
                          className="flex-1 py-2.5 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-40 active:scale-95"
                          style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                          {isWorkerPhotosUploading ? 'Envoi…' : (editingMemoId ? 'Mettre à jour' : 'Enregistrer')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Flux d'observations */}
                  <div className="space-y-3">
                    <span className="text-xs uppercase font-black text-slate-500 tracking-widest block">Flux d'observations</span>
                    {memos.length > 0 ? (
                      [...memos].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((memo) => (
                        <div key={memo.id} className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-2xs font-black border border-blue-100">
                                {getInitials(memo.user || 'S')}
                              </div>
                              <div>
                                <span className="font-black text-xs text-slate-800">{memo.user || 'Système'}</span>
                                <p className="text-xs text-blue-600 font-bold mt-0.5">
                                  {new Date(memo.timestamp).toLocaleDateString('fr-FR')} à {new Date(memo.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                            {memo.user === userName && ticket.status !== Status.DONE && (
                              <button
                                type="button"
                                onClick={() => { setEditingMemoId(memo.id); setWorkerComment(memo.details || ''); setWorkerPhotos(memo.photoUrls || []); }}
                                aria-label="Modifier ce mémo"
                                className="p-1.5 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                              >
                                <PencilIcon className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          {memo.details && <p className="text-base text-slate-700 leading-relaxed font-semibold pl-1">{memo.details}</p>}
                          {memo.photoUrls && memo.photoUrls.length > 0 && (
                            <div className="grid grid-cols-3 gap-1.5">
                              {memo.photoUrls.map((url, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setLightboxUrl(url)}
                                  aria-label={`Agrandir la photo ${idx + 1} du mémo`}
                                  className="rounded-lg overflow-hidden border border-slate-200 bg-white cursor-pointer p-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  style={{ backgroundImage: 'none', backgroundColor: 'white', aspectRatio: '1 / 1' }}
                                >
                                  <img src={thumbUrl(url)} alt={`Obs ${idx + 1}`} className="w-full h-full object-cover hover:opacity-90 transition-opacity duration-200" referrerPolicy="no-referrer" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="border border-dashed border-slate-200 rounded-2xl py-12 flex flex-col items-center justify-center bg-white text-slate-400">
                        <span className="font-black tracking-wider block text-sm">Aucun mémo terrain</span>
                        <span className="text-2xs font-bold mt-1">Saisissez une observation ci-dessus.</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pied de page */}
                <div className="p-5 border-t border-slate-100 shrink-0" style={{ backgroundColor: '#f8fafc' }}>
                  <button
                    type="button"
                    onClick={() => { setIsObservationsDrawerOpen(false); setEditingMemoId(null); setWorkerComment(''); setWorkerPhotos([]); }}
                    className="w-full py-3.5 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-colors duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    style={{ backgroundImage: 'none', backgroundColor: '#0f172a' }}
                  >
                    Fermer les observations
                  </button>
                </div>
              </motion.div>
            </div>
        )}

        {/* Toast de confirmation des actions rapides */}
        {quickActionToast && (
          <div className="absolute bottom-5 inset-x-0 flex justify-center z-40 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-900 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2"
            >
              <CheckCircleIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              {quickActionToast}
            </motion.div>
          </div>
        )}
      </div>
    )
  }

  function renderDesktopWorkspace() {
    switch (view) {
      case 'SELECT_TYPE': return (
        <SelectTypeView
          onCancel={() => { presetLocationId.current = null; setView('HOME') }}
          onSelect={(nextView, interventionData) => {
            setNewIntervention({ ...EMPTY_INTERVENTION, ...takePresetLocation(), ...interventionData })
            setView(nextView)
          }}
        />
      )
      case 'SELECT_MENAGE_SUBTYPE': return (
        <SelectMenageSubtypeView
          newIntervention={newIntervention}
          onUpdate={(partial) => setNewIntervention(prev => ({ ...prev, ...partial }))}
          onCancel={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); }}
          onBack={() => setView('SELECT_TYPE')}
          onNext={() => setView('CREATE_MENAGE')}
        />
      )
      case 'CREATE_MENAGE': return <CreateMenageView {...createFormPropsDesktop} />
      case 'CREATE_TECH': return <CreateTechView {...createFormPropsDesktop} />
      case 'CREATE_DIVERS': return <CreateDiversView {...createFormPropsDesktop} />
      case 'CREATE_DETAILS': return (
        <CreationDetailsForm
          data={newIntervention}
          onUpdate={(data) => setNewIntervention(prev => ({ ...prev, ...data }))}
          onConfirm={handleCreate}
          isLocatif={isLocatif}
          footerLeft={''}
          onBack={() => {
            if (newIntervention.type === 'Ménage') setView('CREATE_MENAGE');
            else if (newIntervention.type === 'Technique') setView('CREATE_TECH');
            else if (newIntervention.type === 'Divers') setView('CREATE_DIVERS');
            else setView('CREATE_TECH');
          }}
        />
      )
      case 'REVIEW': return (
        <ReviewView
          newIntervention={newIntervention}
          onUpdate={(partial) => setNewIntervention(prev => ({ ...prev, ...partial }))}
          onCancel={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); setSearchQuery(''); }}
          onConfirm={handleCreate}
          getLieuName={getLieuName}
          footerLeft={''}
          isReviewEditOpen={isReviewEditOpen}
          isReviewEditClosing={isReviewEditClosing}
          closeReviewEdit={closeReviewEdit}
          currentUserName={userName}
        />
      )
      case 'DETAILS': return renderDesktopTicketDetail()
      case 'EDIT': return renderDesktopTicketDetail()
      case 'DONE_SUCCESS': return renderDoneSuccess()
      case 'CONFIRMATION': return (
        <ConfirmationView
          confirmAnimated={confirmAnimated}
          onHome={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); }}
          onNewTicket={() => { setView('SELECT_TYPE'); setNewIntervention(EMPTY_INTERVENTION); }}
        />
      )
      case 'HOME':
      default: {
        const ticket = tickets.find(t => t.id === selectedTicketId)
        if (ticket) return renderDesktopTicketDetail()
        return renderDesktopWelcome()
      }
    }
  }

  function renderCreateDrawer() {
    const types = [
      { id: 'Technique', title: 'Technique', icon: <CogIcon className="w-5 h-5" /> },
      { id: 'Ménage',    title: 'Ménage',    icon: <SparklesIcon className="w-5 h-5" /> },
      { id: 'Divers',    title: 'Divers',    icon: <AnnotationIcon className="w-5 h-5" /> },
    ]
    const filteredLieux = lieux.filter(l =>
      l.name.toLowerCase().includes(createDrawerSearch.toLowerCase())
    )
    const commonEquipments = [
      'Outillage standard',
      'Sacs poubelle',
      'Aspirateur & Balai',
      'Kit Ampoules LED',
      'Escabeau mobile',
      'Clé à molette / pinces',
      'Produits désinfectants',
    ]
    const durationOptions = ['15 min', '30 min', '45 min', '1 h', '2 h', '4 h', '1 journée']
    const handleSetDeadlinePreset = (daysOffset) => {
      const date = new Date()
      date.setDate(date.getDate() + daysOffset)
      setNewIntervention(prev => ({ ...prev, deadline: date.toISOString() }))
    }
    if (!isCreateDrawerOpen) return null
    return (
      <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 0.2 }}
          onClick={() => setIsCreateDrawerOpen(false)}
          className="absolute inset-0 bg-slate-900 cursor-pointer"
        />

        {/* Panneau glissant */}
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          transition={{ type: 'spring', damping: 26, stiffness: 220 }}
          className="relative w-full bg-white h-full shadow-2xl flex flex-col z-10 text-slate-800"
          style={{ maxWidth: '36rem' }}
        >
          {/* Header */}
          <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md flex-shrink-0">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-black text-slate-800 tracking-tight leading-none uppercase" style={{ margin: 0 }}>Nouvelle intervention</h3>
            </div>
            <button
              type="button"
              onClick={() => setIsCreateDrawerOpen(false)}
              className="w-9 h-9 rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center transition-all border border-slate-100"
              style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Corps scrollable */}
          <div className="flex-1 overflow-y-auto px-5 pt-5 space-y-5 bg-white" style={{ paddingBottom: '6rem' }}>

            {/* 1. TYPE */}
            <div className="space-y-2 text-left">
              <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block pl-1">
                Type d'intervention
              </label>
              <div className="grid grid-cols-3 gap-2">
                {types.map(t => {
                  const isSelected = newIntervention.type === t.id
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        const assignMap = { 'Ménage': 'Équipe ménage', 'Technique': 'Équipe technique', 'Divers': 'Réception' }
                        setNewIntervention(prev => ({ ...prev, type: t.id, assignedTo: assignMap[t.id] || prev.assignedTo }))
                      }}
                      className={`py-4 px-2 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${
                        isSelected
                          ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                          : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      style={{ backgroundImage: 'none', backgroundColor: isSelected ? 'rgb(239,246,255)' : 'white' }}
                    >
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {t.icon}
                      </div>
                      <span className={`text-xs font-black uppercase tracking-wide ${isSelected ? 'text-blue-700' : 'text-slate-500'}`}>{t.title}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* 2. LOCALISATION */}
            <div className="space-y-2 text-left relative">
              <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block pl-1">
                Localisation
              </label>

              {(newIntervention.locationIds || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(newIntervention.locationIds || []).map(id => {
                    const lieu = lieux.find(l => l.id === id)
                    return (
                      <span key={id} className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-xs font-bold">
                        {lieu ? lieu.name : id}
                        <button
                          type="button"
                          onClick={() => setNewIntervention(prev => ({ ...prev, locationIds: (prev.locationIds || []).filter(i => i !== id) }))}
                          className="w-4 h-4 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-500 hover:text-blue-700 flex items-center justify-center transition-colors ml-0.5 flex-shrink-0"
                          style={{ backgroundImage: 'none', backgroundColor: 'rgb(219,234,254)' }}
                        >
                          <XIcon className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <LocationMarkerIcon className="w-4 h-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un emplacement, zone… (ex: B210, Piscine)"
                  value={createDrawerSearch}
                  onFocus={() => setIsCreateLieuDropdownOpen(true)}
                  onChange={e => { setCreateDrawerSearch(e.target.value); setIsCreateLieuDropdownOpen(true) }}
                  className="browser-default w-full pl-10 pr-10 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 focus:shadow-none rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 outline-none transition-all"
                  style={{ boxShadow: 'none' }}
                />
                {createDrawerSearch && (
                  <button
                    type="button"
                    onClick={() => setCreateDrawerSearch('')}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {isCreateLieuDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsCreateLieuDropdownOpen(false)} />
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 max-h-56 overflow-y-auto py-1">
                    {filteredLieux.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-400 font-medium">Aucun lieu correspondant</div>
                    ) : (
                      filteredLieux.map(l => {
                        const alreadySelected = (newIntervention.locationIds || []).includes(l.id)
                        return (
                          <button
                            key={l.id}
                            type="button"
                            onClick={() => {
                              if (!alreadySelected)
                                setNewIntervention(prev => ({ ...prev, locationIds: [...(prev.locationIds || []), l.id] }))
                              setCreateDrawerSearch('')
                              setIsCreateLieuDropdownOpen(false)
                            }}
                            className={`w-full text-left px-4 py-2.5 transition-colors flex items-center justify-between border-b border-slate-50 last:border-0 ${alreadySelected ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
                            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                          >
                            <div>
                              <span className="font-semibold text-sm text-slate-800 block">{l.name}</span>
                              {l.zone && <span className="text-slate-400 text-xs font-medium uppercase">{l.zone}</span>}
                            </div>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${alreadySelected ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                              {alreadySelected ? '✓' : l.type}
                            </span>
                          </button>
                        )
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            {/* 3. DESCRIPTION */}
            <div className="space-y-2 text-left">
              <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block pl-1">
                Description
              </label>
              <textarea
                rows={3}
                placeholder="Décrivez le problème ou la demande…"
                value={newIntervention.description || ''}
                onChange={e => { const value = e.target.value; setNewIntervention(prev => ({ ...prev, description: value })) }}
                className="browser-default w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 focus:shadow-none rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 outline-none transition-all resize-none"
                style={{ boxShadow: 'none', minHeight: '88px' }}
              />
            </div>

            {/* 4. URGENCE */}
            <div className="space-y-2 text-left">
              <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block pl-1">
                Urgence
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: Urgency.NORMAL,    label: 'Normal',    dot: '#94a3b8', selBg: '#f8fafc', selBorder: '#475569', selText: '#1e293b' },
                  { value: Urgency.IMPORTANT, label: 'Important', dot: '#d97706', selBg: '#fffbeb', selBorder: '#d97706', selText: '#92400e' },
                  { value: Urgency.CRITICAL,  label: 'Urgent',    dot: '#dc2626', selBg: '#fef2f2', selBorder: '#dc2626', selText: '#991b1b' },
                ].map(({ value, label, dot, selBg, selBorder, selText }) => {
                  const isSelected = (newIntervention.urgency || Urgency.NORMAL) === value
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setNewIntervention(prev => ({ ...prev, urgency: value }))}
                      className="py-3.5 px-2 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-2 active:scale-95"
                      style={{
                        backgroundImage: 'none',
                        backgroundColor: isSelected ? selBg : 'white',
                        borderColor: isSelected ? selBorder : '#e2e8f0',
                      }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
                      <span className="text-xs font-black uppercase tracking-wide" style={{ color: isSelected ? selText : '#94a3b8' }}>
                        {label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* 5. ASSIGNÉ À — dropdown custom */}
            <div className="space-y-2 text-left relative">
              <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block pl-1">
                Assigné à
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsAssignedToDropdownOpen(prev => !prev)}
                  className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-2xl text-sm font-medium text-left flex items-center justify-between transition-all"
                  style={{ backgroundImage: 'none', backgroundColor: isAssignedToDropdownOpen ? '#fff' : '#f8fafc', borderColor: isAssignedToDropdownOpen ? '#3b82f6' : '#e2e8f0' }}
                >
                  <span className={newIntervention.assignedTo ? 'text-slate-800' : 'text-slate-400'}>
                    {newIntervention.assignedTo ? newIntervention.assignedTo.name : '— Non assigné'}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isAssignedToDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isAssignedToDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsAssignedToDropdownOpen(false)} />
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl z-20 max-h-56 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => { setNewIntervention(prev => ({ ...prev, assignedTo: null })); setIsAssignedToDropdownOpen(false) }}
                        className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50 transition-colors ${!newIntervention.assignedTo ? 'text-blue-600 font-bold' : 'text-slate-500 font-medium'} text-sm`}
                        style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                      >
                        — Non assigné
                        {!newIntervention.assignedTo && <span className="text-blue-500 text-xs">✓</span>}
                      </button>
                      {teams.length > 0 && (
                        <>
                          <div className="px-4 py-1.5 text-2xs font-black text-slate-400 uppercase tracking-widest bg-slate-50 border-b border-slate-100">Équipes</div>
                          {teams.map(team => {
                            const isSel = newIntervention.assignedTo && newIntervention.assignedTo.id === team
                            return (
                              <button
                                key={team}
                                type="button"
                                onClick={() => { setNewIntervention(prev => ({ ...prev, assignedTo: { type: 'team', id: team, name: team } })); setIsAssignedToDropdownOpen(false) }}
                                className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors text-sm ${isSel ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                                style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                              >
                                <div className="flex items-center gap-2.5">
                                  <UserIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                                  {team}
                                </div>
                                {isSel && <span className="text-blue-500 text-xs">✓</span>}
                              </button>
                            )
                          })}
                        </>
                      )}
                      {employees.length > 0 && (
                        <>
                          <div className="px-4 py-1.5 text-2xs font-black text-slate-400 uppercase tracking-widest bg-slate-50 border-b border-slate-100 border-t border-slate-100">Individuel</div>
                          {employees.map(emp => {
                            const isSel = newIntervention.assignedTo && newIntervention.assignedTo.id === emp.id
                            return (
                              <button
                                key={emp.id}
                                type="button"
                                onClick={() => { setNewIntervention(prev => ({ ...prev, assignedTo: { type: 'employee', id: emp.id, name: emp.name } })); setIsAssignedToDropdownOpen(false) }}
                                className={`w-full text-left px-4 py-2.5 flex items-center justify-between border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors text-sm ${isSel ? 'text-blue-600 font-bold' : 'text-slate-700 font-medium'}`}
                                style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                              >
                                <div className="flex items-center gap-2.5">
                                  <UserIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                                  {emp.name}
                                </div>
                                {isSel && <span className="text-blue-500 text-xs">✓</span>}
                              </button>
                            )
                          })}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* 6. OPTIONS AVANCÉES — accordéon inline */}
            <div className="border border-slate-200 rounded-2xl overflow-hidden">

              {/* Toggle */}
              <button
                type="button"
                onClick={() => setIsCreateDetailsOpen(prev => !prev)}
                className="w-full p-4 hover:bg-slate-50 transition-all flex items-center justify-between group cursor-pointer"
                style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-blue-600 transition-all flex-shrink-0">
                    <AdjustmentsIcon className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <span className="block text-xs font-black text-slate-800 uppercase tracking-wide">Options avancées</span>
                    <span className="text-2xs text-slate-400 font-medium block normal-case mt-0.5">
                      {(() => {
                        const configured = [newIntervention.equipment, newIntervention.estimatedDuration, newIntervention.deadline, newIntervention.descriptionExtra].filter(Boolean).length
                        return configured > 0 ? `${configured} option${configured > 1 ? 's' : ''} configurée${configured > 1 ? 's' : ''}` : 'Matériel, durée, échéance, accès…'
                      })()}
                    </span>
                  </div>
                </div>
                <ChevronRightIcon className={`w-4 h-4 text-slate-400 transition-transform ${isCreateDetailsOpen ? 'rotate-90' : ''}`} />
              </button>

              {/* Corps */}
              {isCreateDetailsOpen && (
                <div className="border-t border-slate-100 p-4 space-y-5 bg-white">

                  {/* Matériel */}
                  <div className="space-y-2 text-left">
                    <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                      Matériel & équipements requis
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {commonEquipments.map(eq => {
                        const isSelected = newIntervention.equipment === eq
                        return (
                          <button
                            key={eq}
                            type="button"
                            onClick={() => setNewIntervention(prev => ({ ...prev, equipment: isSelected ? undefined : eq }))}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${isSelected ? 'border-orange-400 text-white' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                            style={{ backgroundImage: 'none', backgroundColor: isSelected ? '#f97316' : '#f8fafc' }}
                          >
                            {eq}
                          </button>
                        )
                      })}
                    </div>
                    <input
                      type="text"
                      placeholder="Ou spécifier un autre équipement…"
                      value={newIntervention.equipment || ''}
                      onChange={e => { const value = e.target.value; setNewIntervention(prev => ({ ...prev, equipment: value })) }}
                      className="browser-default w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 focus:shadow-none rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 outline-none transition-all"
                      style={{ boxShadow: 'none' }}
                    />
                  </div>

                  {/* Durée estimée */}
                  <div className="space-y-2 text-left">
                    <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                      Durée estimée
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {durationOptions.map(dur => {
                        const isSelected = newIntervention.estimatedDuration === dur
                        return (
                          <button
                            key={dur}
                            type="button"
                            onClick={() => setNewIntervention(prev => ({ ...prev, estimatedDuration: dur }))}
                            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors duration-200 cursor-pointer border inline-flex items-center gap-1.5 ${isSelected ? 'border-blue-500 text-white' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                            style={{ backgroundImage: 'none', backgroundColor: isSelected ? '#2563eb' : '#f8fafc' }}
                          >
                            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            {dur}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Échéance */}
                  <div className="space-y-2 text-left">
                    <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                      Échéance limite
                    </label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[
                        { label: "Aujourd'hui", offset: 0 },
                        { label: 'Demain',      offset: 1 },
                        { label: 'Dans 3j',     offset: 3 },
                        { label: '7 jours',     offset: 7 },
                      ].map(({ label, offset }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => handleSetDeadlinePreset(offset)}
                          className="py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold uppercase transition-all cursor-pointer"
                          style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                      </div>
                      <input
                        type="date"
                        value={newIntervention.deadline ? newIntervention.deadline.split('T')[0] : ''}
                        onChange={e => {
                          const value = e.target.value
                          setNewIntervention(prev => ({
                            ...prev,
                            deadline: value ? new Date(value).toISOString() : undefined
                          }))
                        }}
                        className="browser-default w-full pl-10 pr-4 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 focus:shadow-none rounded-2xl text-base font-medium text-slate-800 outline-none transition-all cursor-pointer"
                        style={{ boxShadow: 'none' }}
                      />
                    </div>
                  </div>

                  {/* Consignes */}
                  <div className="space-y-2 text-left">
                    <label className="text-2xs my-0 font-bold text-slate-400 uppercase tracking-widest block">
                      Consignes d'accès et commentaires
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Ex : code portillon 1234, clé à la réception, attention au chien…"
                      value={newIntervention.descriptionExtra || ''}
                      onChange={e => { const value = e.target.value; setNewIntervention(prev => ({ ...prev, descriptionExtra: value })) }}
                      className="browser-default w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 focus:bg-white border border-slate-200 focus:border-blue-500 focus:shadow-none rounded-2xl text-base font-medium text-slate-800 placeholder-slate-400 outline-none transition-all resize-none"
                      style={{ boxShadow: 'none', minHeight: '80px' }}
                    />
                  </div>

                  {/* Accord d'absence */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <ShieldExclamationIcon className="w-4 h-4" />
                      </div>
                      <div className="text-left">
                        <span className="text-xs font-bold text-slate-800 block">Accord d'absence</span>
                        <span className="text-2xs text-slate-400 font-medium block mt-0.5">Autoriser l'accès en cas d'absence</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewIntervention(prev => ({ ...prev, customerPermission: !prev.customerPermission }))}
                      className="w-11 h-6 rounded-full relative outline-none flex items-center p-0.5 flex-shrink-0 transition-colors"
                      style={{ backgroundImage: 'none', backgroundColor: newIntervention.customerPermission ? '#10b981' : '#cbd5e1' }}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${newIntervention.customerPermission ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>

                </div>
              )}
            </div>

          </div>

          {/* Footer fixe */}
          {(() => {
            const locationCount = (newIntervention.locationIds || []).length
            return (
            <div className="absolute bottom-0 inset-x-0 p-5 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3.5 flex-shrink-0 z-20">
              <button
                type="button"
                onClick={() => setIsCreateDrawerOpen(false)}
                className="w-1/2 py-3.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-extrabold text-xs uppercase tracking-widest rounded-2xl transition-all text-center"
                style={{ backgroundImage: 'none', backgroundColor: '#ffffff' }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCreateFromDrawer}
                disabled={locationCount === 0}
                className="w-1/2 py-3.5 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-md flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                style={{ backgroundImage: 'none', backgroundColor: locationCount === 0 ? '#93c5fd' : '#2563eb' }}
              >
                {locationCount > 1 ? `Créer ${locationCount} interventions` : "Créer l'intervention"}
              </button>
            </div>
            )
          })()}

        </motion.div>
      </div>
    )
  }

  // --- Onglet Analyse (P2 pilotage) ---
  function renderAnalytics() {
    // Palette dataviz validée (skill) : série 1 bleu, série 2 aqua ; statuts réservés
    const C_CREATED = '#2a78d6'
    const C_DONE = '#1baf7a'
    const a = analytics

    const fmtDelay = (seconds) => {
      if (seconds === null || seconds === undefined)
        return '—'
      if (seconds < 3600)
        return Math.max(1, Math.round(seconds / 60)) + ' min'
      if (seconds < 86400)
        return (Math.round(seconds / 360) / 10).toString().replace('.', ',') + ' h'
      return (Math.round(seconds / 8640) / 10).toString().replace('.', ',') + ' j'
    }

    const onTimePct = a && a.on_time.done_with_deadline > 0
      ? Math.round((a.on_time.on_time / a.on_time.done_with_deadline) * 100)
      : null

    // Tuile KPI : icône colorée + valeur — le nombre porte la couleur du signal
    const tile = (label, value, sub, icon, iconBg, iconFg, valueColor) => (
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start gap-3 flex-1 hover:shadow-sm transition-shadow duration-200" style={{ minWidth: 165 }}>
        <div className="rounded-xl flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, backgroundColor: iconBg, color: iconFg }}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-2xs uppercase font-black text-slate-400 tracking-widest my-0">{label}</p>
          <p className={`font-black font-mono my-0 leading-none mt-1.5 ${valueColor || 'text-slate-800'}`} style={{ fontSize: 26 }}>{value}</p>
          {sub && <p className="text-2xs font-semibold text-slate-400 my-0 mt-1.5 leading-snug">{sub}</p>}
        </div>
      </div>
    )

    const sectionTitle = (icon, label, iconBg, iconFg) => (
      <div className="flex items-center gap-2 mb-4">
        <div className="rounded-lg flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, backgroundColor: iconBg, color: iconFg }}>
          {icon}
        </div>
        <p className="text-xs uppercase font-black text-slate-600 tracking-widest my-0">{label}</p>
      </div>
    )

    // Barre horizontale avec avatar — magnitude, une teinte par contexte
    const hBar = (key, avatar, label, sub, count, max, color, lateCount) => (
      <div key={key} className="flex items-center gap-2.5 group">
        {avatar}
        <div className="flex-shrink-0" style={{ width: 128 }}>
          <p className="text-xs font-bold text-slate-700 my-0 truncate">{label}</p>
          {sub && <p className="text-3xs font-semibold text-slate-400 my-0 truncate">{sub}</p>}
        </div>
        <div className="flex-1 rounded-lg overflow-hidden" style={{ height: 22, backgroundColor: '#eef1f7' }}>
          <div
            className="h-full transition-opacity group-hover:opacity-85 flex items-center justify-end"
            style={{ width: (max > 0 ? Math.max(6, (count / max) * 100) : 0) + '%', backgroundColor: color, borderRadius: '0 6px 6px 0', minWidth: 26 }}
            title={label + ' : ' + count}
          >
            <span className="text-2xs font-mono font-black text-white pr-2">{count}</span>
          </div>
        </div>
        <span className="flex-shrink-0 text-right" style={{ width: 76 }}>
          {lateCount > 0 && (
            <span className="text-2xs font-black text-red-600 bg-red-50 rounded-md px-1.5 py-0.5">{lateCount} en retard</span>
          )}
        </span>
      </div>
    )

    return (
      <div className="flex-1 overflow-y-auto p-6 bg-white">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Filtres de période — une rangée au-dessus des graphiques */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-black text-slate-900 my-0">Analyse des interventions</h3>
              <p className="text-xs font-semibold text-slate-400 my-0 mt-0.5">
                {a ? `${a.created} créées · ${a.done} terminées sur la période` : 'Chargement…'}
              </p>
            </div>
            <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-white">
              {[
                { d: 7,   label: '7 jours'  },
                { d: 30,  label: '30 jours' },
                { d: 365, label: 'Saison'   },
              ].map(({ d, label }, idx) => {
                const on = analyticsDays === d
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setAnalyticsDays(d)}
                    aria-pressed={on}
                    className={`px-3.5 py-2 text-xs font-black transition-colors duration-200 ${idx > 0 ? 'border-l border-slate-200' : ''}`}
                    style={{ backgroundImage: 'none', backgroundColor: on ? '#0f172a' : 'white', color: on ? 'white' : '#64748b', cursor: on ? 'default' : 'pointer' }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

          {isAnalyticsLoading && !a && (
            <div className="py-24 text-center text-slate-400 text-sm font-bold">Chargement des données…</div>
          )}

          {a && (
          <>
          {/* Rangée KPI */}
          <div className="flex gap-3 flex-wrap" style={{ opacity: isAnalyticsLoading ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
            {tile('Créées', a.created, 'sur la période',
              <PlusIcon className="w-4 h-4" />, '#e8eeff', '#1a41b8')}
            {tile('Terminées', a.done, 'sur la période',
              <CheckCircleIcon className="w-4 h-4" />, '#d3f6e7', '#067a52', 'text-emerald-700')}
            {tile("Taux à l'heure", onTimePct !== null ? onTimePct + ' %' : '—',
              a.on_time.done_with_deadline > 0
                ? `${a.on_time.on_time} sur ${a.on_time.done_with_deadline} avec échéance`
                : 'aucune terminée avec échéance',
              <ClockIcon className="w-4 h-4" />,
              onTimePct === null ? '#eef1f7' : onTimePct >= 80 ? '#d3f6e7' : onTimePct >= 50 ? '#fdf0cf' : '#fde3e3',
              onTimePct === null ? '#64748b' : onTimePct >= 80 ? '#067a52' : onTimePct >= 50 ? '#a35207' : '#d92626',
              onTimePct !== null ? (onTimePct >= 80 ? 'text-emerald-700' : onTimePct >= 50 ? 'text-amber-700' : 'text-red-600') : 'text-slate-300')}
            {tile('En retard', a.late, `sur ${a.open} ouverte${a.open > 1 ? 's' : ''}`,
              <ExclamationIcon className="w-4 h-4" />,
              a.late > 0 ? '#fde3e3' : '#eef1f7', a.late > 0 ? '#d92626' : '#64748b',
              a.late > 0 ? 'text-red-600' : 'text-slate-800')}
            {tile('Demandes client', a.customer_requests, 'sur la période',
              <AnnotationIcon className="w-4 h-4" />, '#e3e5ff', '#4438c9')}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" style={{ opacity: isAnalyticsLoading ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>

            {/* Activité — colonnes appariées créées / terminées */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-start justify-between">
                {sectionTitle(<ChartBarIcon className="w-3.5 h-3.5" />, 'Activité', '#e8eeff', '#1a41b8')}
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-2xs font-bold text-slate-500">
                    <span className="rounded-sm" style={{ width: 9, height: 9, backgroundColor: C_CREATED }} /> Créées · <span className="font-mono">{a.created}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-2xs font-bold text-slate-500">
                    <span className="rounded-sm" style={{ width: 9, height: 9, backgroundColor: C_DONE }} /> Terminées · <span className="font-mono">{a.done}</span>
                  </span>
                </div>
              </div>
              {(() => {
                const series = a.series || []
                const maxV = Math.max(1, ...series.map(s => Math.max(s.created, s.done)))
                const H = 128
                const labelEvery = Math.max(1, Math.ceil(series.length / 6))
                const isEmpty = series.every(s => s.created === 0 && s.done === 0)
                return (
                  <>
                    <div className="relative rounded-xl px-2 pt-2" style={{ backgroundColor: '#fafbfe', border: '1px solid #eef1f7' }}>
                      {/* Gridlines + valeur max */}
                      {[0.5].map(f => (
                        <div key={f} className="absolute left-2 right-2" style={{ top: 2 + (H * (1 - f)), borderTop: '1px dashed #e2e8f0' }} />
                      ))}
                      <span className="absolute text-3xs font-mono font-bold text-slate-300" style={{ top: 4, right: 8 }}>max {maxV}</span>
                      {isEmpty ? (
                        <div className="flex items-center justify-center" style={{ height: H }}>
                          <span className="text-xs font-semibold text-slate-400">Aucune activité sur la période</span>
                        </div>
                      ) : (
                        <div className="flex items-end" style={{ height: H, gap: series.length > 20 ? 1 : 4 }}>
                          {series.map((s, i) => {
                            const d = new Date(s.start * 1000)
                            const lbl = (a.granularity === 'week' ? 'Sem. du ' : '') + d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                            return (
                              <div
                                key={i}
                                className="flex-1 flex items-end justify-center rounded-t-lg"
                                style={{ gap: 2, height: '100%', minWidth: 0 }}
                                title={`${lbl} — ${s.created} créée${s.created > 1 ? 's' : ''}, ${s.done} terminée${s.done > 1 ? 's' : ''}`}
                              >
                                <div style={{ width: '42%', maxWidth: 16, height: Math.max(s.created > 0 ? 4 : 2, Math.round((s.created / maxV) * (H - 14))), backgroundColor: s.created > 0 ? C_CREATED : '#e2e8f0', borderRadius: '4px 4px 0 0' }} />
                                <div style={{ width: '42%', maxWidth: 16, height: Math.max(s.done > 0 ? 4 : 2, Math.round((s.done / maxV) * (H - 14))), backgroundColor: s.done > 0 ? C_DONE : '#e2e8f0', borderRadius: '4px 4px 0 0' }} />
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <div style={{ borderTop: '2px solid #dde3ee' }} />
                      <div className="flex pb-1" style={{ gap: series.length > 20 ? 1 : 4 }}>
                        {series.map((s, i) => {
                          const d = new Date(s.start * 1000)
                          const show = i % labelEvery === 0
                          return (
                            <div key={i} className="flex-1 text-center" style={{ minWidth: 0, overflow: 'visible' }}>
                              {show && (
                                <span className="text-3xs font-semibold text-slate-400 whitespace-nowrap">
                                  {d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )
              })()}
            </div>

            {/* Charge par équipe / agent */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow duration-200">
              {sectionTitle(<UserGroupIcon className="w-3.5 h-3.5" />, 'Charge ouverte par équipe / agent', '#e8eeff', '#1a41b8')}
              {(a.workload || []).length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm font-bold text-emerald-700 my-0">Aucune intervention ouverte 🎉</p>
                  <p className="text-xs text-slate-400 font-semibold my-0 mt-1">Tout le monde est à jour.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {(() => {
                    const maxW = Math.max(...a.workload.map(w => w.open))
                    return a.workload.slice(0, 8).map(w =>
                      hBar(
                        w.name,
                        <span className="rounded-lg text-white flex-shrink-0 flex items-center justify-center font-black" style={{ width: 24, height: 24, backgroundColor: w.name === 'Non assigné' ? '#94a3b8' : avatarColor(w.name), fontSize: 9 }}>
                          {w.name === 'Non assigné' ? '?' : initialsOf(w.name)}
                        </span>,
                        w.name,
                        w.type === 'team' ? 'Équipe' : w.type === 'employee' ? 'Individuel' : 'À répartir',
                        w.open, maxW, w.name === 'Non assigné' ? '#94a3b8' : C_CREATED, w.late
                      )
                    )
                  })()}
                </div>
              )}
            </div>

            {/* Lieux à récidives */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow duration-200">
              {sectionTitle(<LocationMarkerIcon className="w-3.5 h-3.5" />, 'Lieux à récidives (2 interventions et +)', '#ccfbf1', '#0d7268')}
              {(a.top_lieux || []).length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm font-bold text-emerald-700 my-0">Aucun lieu ne revient plusieurs fois ✅</p>
                  <p className="text-xs text-slate-400 font-semibold my-0 mt-1">Pas de panne récurrente détectée sur la période.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {(() => {
                    const maxL = Math.max(...a.top_lieux.map(l => l.count))
                    return a.top_lieux.map(l =>
                      hBar(
                        l.name,
                        <span className="rounded-lg flex-shrink-0 flex items-center justify-center" style={{ width: 24, height: 24, backgroundColor: '#ccfbf1', color: '#0d7268' }}>
                          <LocationMarkerIcon className="w-3.5 h-3.5" />
                        </span>,
                        l.name, l.zone, l.count, maxL, C_DONE, 0
                      )
                    )
                  })()}
                  <p className="text-3xs font-semibold text-slate-400 my-0 mt-2 flex items-start gap-1">
                    <span role="img" aria-label="conseil">💡</span> Un lieu qui revient souvent peut mériter un remplacement plutôt que des réparations.
                  </p>
                </div>
              )}
            </div>

            {/* Délais par type */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition-shadow duration-200">
              {sectionTitle(<ClockIcon className="w-3.5 h-3.5" />, 'Délais par type', '#fdf0cf', '#a35207')}
              {(a.types || []).length === 0 ? (
                <p className="text-xs text-slate-400 font-semibold my-0">Aucune intervention terminée sur la période.</p>
              ) : (
                <div>
                  <div className="grid grid-cols-4 gap-2 pb-2 border-b border-slate-200">
                    <span className="text-3xs uppercase font-black text-slate-400 tracking-widest">Type</span>
                    <span className="text-3xs uppercase font-black text-slate-400 tracking-widest text-right">Résolution</span>
                    <span className="text-3xs uppercase font-black text-slate-400 tracking-widest text-right">Travail réel</span>
                    <span className="text-3xs uppercase font-black text-slate-400 tracking-widest text-right">Estimé</span>
                  </div>
                  {a.types.map(t => (
                    <div key={t.type} className="grid grid-cols-4 gap-2 py-2 border-b border-slate-100 items-center">
                      <span className={`text-xs font-black px-1.5 py-0.5 rounded-md justify-self-start ${
                        t.type === 'Ménage' ? 'bg-teal-50 text-teal-700' : t.type === 'Technique' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {t.type} · {t.done}
                      </span>
                      <span className="text-xs font-mono font-bold text-slate-700 text-right">{fmtDelay(t.avg_resolution)}</span>
                      <span className="text-xs font-mono font-bold text-slate-700 text-right">{fmtDelay(t.avg_work)}</span>
                      <span className={`text-xs font-mono font-bold text-right ${t.avg_work && t.avg_estimated && t.avg_work > t.avg_estimated * 1.25 ? 'text-amber-700' : 'text-slate-400'}`}>
                        {fmtDelay(t.avg_estimated)}
                      </span>
                    </div>
                  ))}
                  <p className="text-3xs font-semibold text-slate-400 my-0 mt-2">
                    Résolution = de la création à la clôture. Travail réel = temps passé « En cours » (calculé, sans saisie). En ambre : travail réel nettement au-dessus de l'estimation.
                  </p>
                </div>
              )}
            </div>

          </div>
          </>
          )}

          {!a && !isAnalyticsLoading && (
            <div className="py-24 text-center text-slate-400 text-sm font-bold">Impossible de charger les données.</div>
          )}
        </div>
      </div>
    )
  }

  function renderDesktopLayout() {
    const urgencyLabel = {
      [Urgency.NORMAL]:    'Normal',
      [Urgency.IMPORTANT]: 'Important',
      [Urgency.CRITICAL]:  'Urgent',
    }

    const pauseReasonOf = (ticket) => {
      const hist = ticket.history || []
      for (let i = hist.length - 1; i >= 0; i--)
      {
        if (hist[i].type === 'status_change' && hist[i].to === Status.BLOCKED)
          return hist[i].details || null
      }
      return null
    }
    const isLateTicket = (t) => t.status !== Status.DONE && isDeadlinePast(t.deadline)
    const formatDeadlineShort = (iso) => {
      const d = new Date(iso)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const day = new Date(d)
      day.setHours(0, 0, 0, 0)
      const diff = Math.round((day - today) / 86400000)
      if (diff === 0)
        return "auj."
      if (diff === 1)
        return 'demain'
      if (diff === -1)
        return 'hier'
      return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
    }

    const renderTicketCard = (ticket) => {
      const isSel = ticket.id === selectedTicketId
      const isNew = isTicketNew(ticket)
      const isDeleting = ticket.id === deletingTicketId
      const assignedName = (ticket.assignedTo && ticket.assignedTo.name) || 'Non assigné'
      const late = isLateTicket(ticket)
      const pauseReason = ticket.status === Status.BLOCKED ? pauseReasonOf(ticket) : null
      const spine = ticket.urgency === Urgency.CRITICAL ? '#dc2626' : ticket.urgency === Urgency.IMPORTANT ? '#f59e0b' : 'transparent'

      const openTicket = () => {
        if (isDeleting)
          return
        setSelectedTicketId(ticket.id)
        setView('DETAILS')
      }

      return (
        <div
          key={ticket.id}
          role="button"
          tabIndex={0}
          onClick={openTicket}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ')
            {
              e.preventDefault()
              openTicket()
            }
          }}
          className={`px-3 py-2.5 cursor-pointer transition-colors duration-200 text-left flex flex-col gap-1 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
            isSel ? 'bg-blue-50' : 'hover:bg-slate-50'
          }`}
          style={{
            borderLeft: `4px solid ${spine}`,
            boxShadow: isSel ? 'inset -3px 0 0 #2563eb' : 'none',
            ...(isDeleting ? {
              opacity: 0,
              transform: 'translateX(-40px) scale(0.95)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              pointerEvents: 'none',
              backgroundColor: '#fef2f2',
            } : {}),
          }}
        >
          {/* Ligne 1 — lieu (mono) + type + urgence */}
          <div className="flex items-center gap-1.5 min-w-0">
            <h4 className="font-mono font-bold text-sm my-0 text-slate-800 leading-tight truncate">
              {getLieuName(ticket.locationId)}
            </h4>
            <span className={`text-2xs font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${
              ticket.type === 'Ménage' ? 'bg-teal-50 text-teal-700' : ticket.type === 'Technique' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {ticket.type}
            </span>
            {isNew && (
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" title="Nouveau depuis votre dernière visite" />
            )}
            {ticket.urgency !== Urgency.NORMAL && (
              <span className={`ml-auto flex-shrink-0 text-2xs font-black px-1.5 py-0.5 rounded-md ${
                ticket.urgency === Urgency.CRITICAL ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'
              }`}>
                ⚡ {urgencyLabel[ticket.urgency]}
              </span>
            )}
          </div>

          {/* Ligne 2 — description */}
          <p className="text-xs text-slate-500 font-semibold leading-snug truncate my-0">
            {ticket.description || ticket.label || ticket.type}
          </p>

          {/* Ligne 3 — statut + raison de pause + assigné + échéance */}
          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
            <span className={`text-2xs font-black px-1.5 py-0.5 rounded-md flex-shrink-0 ${
              ticket.status === Status.TODO ? 'bg-blue-50 text-blue-700' :
              ticket.status === Status.IN_PROGRESS ? 'bg-amber-50 text-amber-700' :
              ticket.status === Status.BLOCKED ? 'bg-slate-100 text-slate-600' :
              'bg-emerald-50 text-emerald-700'
            }`}>
              {ticket.status === Status.TODO ? 'À faire' : ticket.status === Status.IN_PROGRESS ? 'En cours' : ticket.status === Status.BLOCKED ? 'En pause' : 'Terminé'}
            </span>
            {pauseReason && (
              <span className="text-2xs font-bold text-slate-400 border border-dashed border-slate-300 rounded-md px-1.5 py-0.5 truncate" style={{ maxWidth: 110 }} title={pauseReason}>
                {pauseReason}
              </span>
            )}
            <span
              className="rounded text-white flex-shrink-0 flex items-center justify-center font-black"
              style={{ backgroundColor: ticket.assignedTo ? avatarColor(assignedName) : '#94a3b8', fontSize: 8, width: 17, height: 17 }}
            >
              {ticket.assignedTo ? initialsOf(assignedName) : '?'}
            </span>
            <span className="text-2xs text-slate-600 font-bold truncate min-w-0" style={{ flexShrink: 1 }}>
              {assignedName}
            </span>
            {ticket.status !== Status.DONE && ticket.deadline && (
              <span className={`ml-auto flex-shrink-0 text-2xs font-mono font-bold ${late ? 'text-red-600' : 'text-slate-400'}`}>
                <span role="img" aria-label="échéance">📅</span> {formatDeadlineShort(ticket.deadline)}{late ? ' · dépassée' : ''}
              </span>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col h-full w-full bg-slate-50 text-slate-900 font-sans overflow-hidden">

        {/* Topbar — identité, progression du jour, notifications.
            Sidebar repliée : le module colle au bord et le contenu s'écarte
            pour accueillir le bouton flottant ">" du shell (fixed left-4) */}
        <div
          className="flex items-center gap-4 px-4 py-2 bg-white border-b border-slate-200 flex-shrink-0"
          style={{ paddingLeft: sidebarCollapsed ? 60 : undefined, minHeight: 53, transition: 'padding-left 0.3s ease' }}
        >
          <div className="flex items-baseline gap-2.5 min-w-0">
            <h2 className="text-base font-black text-slate-900 tracking-tight my-0">Interventions</h2>
            <span className="text-xs font-bold text-slate-400 truncate">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
          {canWrite && (
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden flex-shrink-0">
              {[
                { id: 'board',     label: 'Liste'   },
                { id: 'analytics', label: 'Analyse' },
              ].map(({ id, label }, idx) => {
                const on = desktopView === id
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDesktopView(id)}
                    aria-pressed={on}
                    className={`px-3 py-1 text-xs font-black transition-colors duration-200 ${idx > 0 ? 'border-l border-slate-200' : ''}`}
                    style={{ backgroundImage: 'none', backgroundColor: on ? '#0f172a' : 'white', color: on ? 'white' : '#64748b', cursor: on ? 'default' : 'pointer' }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          )}
          {(() => {
            const today = new Date().toDateString()
            const doneToday = tickets.filter(t => {
              if (t.status !== Status.DONE)
                return false
              const doneEvents = (t.history || []).filter(e => e.type === 'status_change' && e.to === Status.DONE)
              const last = doneEvents[doneEvents.length - 1]
              return last && new Date(last.timestamp).toDateString() === today
            }).length
            const openCount = tickets.filter(t => t.status !== Status.DONE).length
            const total = doneToday + openCount
            const pct = total > 0 ? Math.round((doneToday / total) * 100) : 0
            return (
              <div className="hidden lg:flex items-center gap-2.5 min-w-0">
                <div className="w-32 h-1.5 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#059669', transition: 'width 0.6s ease' }} />
                </div>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
                  <span className="font-mono font-black text-emerald-600">{doneToday}</span> terminée{doneToday > 1 ? 's' : ''} auj. · <span className="font-mono">{openCount}</span> ouverte{openCount > 1 ? 's' : ''}
                </span>
              </div>
            )
          })()}
          <div className="flex items-center gap-2 flex-shrink-0" style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => { if (canWrite) openSettings(); else history.push('/parametres', { from: '/interventions' }) }}
              className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:border-slate-300 flex items-center justify-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title={canWrite ? 'Paramètres des interventions' : 'Paramètres du compte'}
              style={{ backgroundImage: 'none', backgroundColor: 'white' }}
            >
              <CogIcon className="w-4 h-4" />
            </button>
            <span
              className="w-9 h-9 rounded-xl text-white text-xs font-black flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: avatarColor(userName || '?') }}
              title={userName}
            >
              {userInitials}
            </span>
          </div>
        </div>

        {desktopView === 'analytics' && canWrite ? renderAnalytics() : (
        <div className="flex flex-1 min-h-0 w-full overflow-hidden">

        {/* Colonne gauche — liste (Tailwind v2 sans JIT : largeur en style inline) */}
        <div className="border-r border-slate-200 flex flex-col bg-white flex-shrink-0 relative" style={{ width: 380 }}>

          {/* Recherche */}
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="relative">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={ticketSearchQuery}
                onChange={(e) => setTicketSearchQuery(e.target.value)}
                placeholder="Rechercher un lieu, ticket, agent…"
                aria-label="Rechercher une intervention"
                className="browser-default w-full bg-white border border-slate-200 rounded-xl pl-9 pr-8 py-2 text-base font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                style={{ height: 'auto', margin: 0, boxSizing: 'border-box' }}
              />
              {ticketSearchQuery && (
                <button
                  type="button"
                  onClick={() => setTicketSearchQuery('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors duration-200"
                  style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {ticketSearchQuery && (
              <p className="text-2xs font-bold text-slate-500 mt-1 pl-1">
                {desktopFilteredTickets.length} résultat{desktopFilteredTickets.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Filtres — chips avec compteurs (dont « En retard ») */}
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer les interventions">
              {(() => {
                // Un choix par groupe : toucher le choix actif le retire, toucher l'autre le remplace
                const choice = (group, value) => ({ count: countFor({ [group]: value }), on: desktopFilters[group] === value, set: { [group]: desktopFilters[group] === value ? null : value } })
                // Chacun compte les interventions qu'il donnerait en plus des choix des autres groupes
                const countFor = (patch) => tickets.filter(t => matchesDesktopFilters(t, { ...desktopFilters, ...patch }, isTodayTicket)).length
                const recentlyDone = tickets.filter(t => isRecentlyDone(t)).length
                return [
                  { id: 'All',       label: 'Tous',        count: countFor(NO_DESKTOP_FILTERS), on: !hasDesktopFilters(desktopFilters), set: NO_DESKTOP_FILTERS },
                  { id: 'Late',      label: 'En retard',   count: countFor({ late: true }), on: desktopFilters.late, set: { late: !desktopFilters.late }, late: true },
                  { id: 'Urgent',    label: 'Urgents',     ...choice('urgency', 'Urgent') },
                  { id: 'Important', label: 'Important',   ...choice('urgency', 'Important') },
                  { id: 'Normal',    label: 'Normal',      ...choice('urgency', 'Normal') },
                  { id: 'Ménage',    label: 'Ménage',      ...choice('type', 'Ménage') },
                  { id: 'Technique', label: 'Technique',   ...choice('type', 'Technique') },
                  { id: 'Today',     label: "Aujourd'hui", ...choice('when', 'Today') },
                  { id: 'Upcoming',  label: 'À venir',     ...choice('when', 'Upcoming') },
                  { id: 'Todo',      label: 'À faire',     ...choice('state', 'Todo') },
                  { id: 'Progress',  label: 'En cours',    ...choice('state', 'Progress') },
                  { id: 'Terminés',  label: 'Terminés',    ...choice('state', 'Terminés') },
                ].map(({ id, label, late, count, on, set }) => {
                  const isActive = on
                  const tone = isActive
                    ? 'text-white border-slate-900'
                    : late
                      ? 'text-red-600 border-red-200 hover:border-red-300'
                      : 'text-slate-600 border-slate-200 hover:border-slate-300'
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setDesktopFilters(current => ({ ...(id === 'All' ? NO_DESKTOP_FILTERS : current), ...set }))}
                      aria-pressed={isActive}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-bold text-xs border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${tone}`}
                      style={{ backgroundImage: 'none', backgroundColor: isActive ? '#0f172a' : late ? '#fff6f6' : 'white' }}
                    >
                      {label}
                      <span className={`font-black font-mono ${isActive ? 'text-slate-300' : late ? 'text-red-500' : 'text-slate-400'}`}>
                        {count}
                      </span>
                      {id === 'Terminés' && recentlyDone > 0 && (
                        <span
                          className="font-black font-mono rounded-md px-1 text-2xs"
                          style={{ backgroundColor: '#d3f6e7', color: '#067a52' }}
                          title={`${recentlyDone} terminée${recentlyDone > 1 ? 's' : ''} depuis votre dernière visite`}
                        >
                          +{recentlyDone}
                        </span>
                      )}
                    </button>
                  )
                })
              })()}
            </div>
          </div>

          {/* Bandeau hors-ligne desktop */}
          {!isOnline && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 shrink-0">
              <span className="w-2 h-2 bg-amber-500 rounded-full shrink-0 animate-pulse motion-reduce:animate-none" />
              <span className="text-2xs font-black text-amber-700 uppercase tracking-wider">
                Hors ligne{pendingCount > 0 ? ` · ${pendingCount} en attente` : ''}
              </span>
            </div>
          )}
          {isOnline && pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100 shrink-0">
              <span className="w-3 h-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
              <span className="text-2xs font-black text-blue-600 uppercase tracking-wider">Synchronisation…</span>
            </div>
          )}

          {/* Bouton nouvelle intervention */}
          {canWrite && (
          <div className="px-3 py-2 border-b border-slate-200">
            <button
              onClick={() => { setIsCreateDrawerOpen(true); setNewIntervention(EMPTY_INTERVENTION); setCreateDrawerSearch('') }}
              className="w-full py-2.5 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              style={{ backgroundImage: 'none', backgroundColor: '#ea6a1e', boxShadow: '0 4px 10px rgba(234,106,30,0.28)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#d55a10' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ea6a1e' }}
            >
              <PlusIcon className="w-4 h-4" />
              <span>Nouvelle intervention</span>
            </button>
          </div>
          )}

          {/* Liste scrollable */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-200 text-left">
            {isLoadingTickets ? (
              <div aria-label="Chargement des interventions" role="status">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="p-4 flex flex-col gap-2.5 animate-pulse motion-reduce:animate-none border-b border-slate-100">
                    <div className="flex justify-between items-center">
                      <div className="h-4 w-16 bg-slate-100 rounded-full" />
                      <div className="h-3 w-10 bg-slate-100 rounded" />
                    </div>
                    <div className="h-4 w-2/3 bg-slate-200 rounded" />
                    <div className="h-3 w-1/2 bg-slate-100 rounded" />
                    <div className="flex justify-between items-center">
                      <div className="h-4 w-24 bg-slate-100 rounded" />
                      <div className="h-3 w-12 bg-slate-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : desktopFilteredTickets.length === 0 ? (
              <div className="py-16 text-center text-slate-500 px-6">
                <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <InboxIcon className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-xs font-black uppercase tracking-widest mt-2">Aucun ticket correspondant</p>
                {(hasDesktopFilters(desktopFilters) || ticketSearchQuery) && (
                  <button
                    type="button"
                    onClick={() => { setDesktopFilters(NO_DESKTOP_FILTERS); setTicketSearchQuery('') }}
                    className="mt-4 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: 'white' }}
                  >
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            ) : desktopFilters.state === 'Terminés' ? (
              desktopFilteredTickets.map(t => renderTicketCard(t))
            ) : (
              (() => {
                // Groupes « Aujourd'hui / À venir » (règle : isTodayTicket)
                const todayItems = desktopFilteredTickets.filter(isTodayTicket)
                const upcomingItems = desktopFilteredTickets.filter(t => !isTodayTicket(t))
                const groupLabel = (label, count, isToday) => (
                  <div
                    className="flex items-center justify-between px-3 py-1.5 font-black uppercase tracking-widest text-2xs border-b border-slate-200 sticky top-0 z-10"
                    style={{ backgroundColor: isToday ? '#ffffff' : '#eef1f7', color: isToday ? '#1e293b' : '#64748b', borderTop: isToday ? 'none' : '2px solid #dde3ee' }}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {isToday
                        ? <span className="rounded-full flex-shrink-0" style={{ width: 7, height: 7, backgroundColor: '#2456e6' }} />
                        : <CalendarIcon className="w-3 h-3 flex-shrink-0" />}
                      {label}
                    </span>
                    <span className="font-mono rounded-md px-1.5" style={{ backgroundColor: isToday ? '#e8eeff' : '#e2e8f0', color: isToday ? '#1a41b8' : '#64748b' }}>{count}</span>
                  </div>
                )
                return (
                  <>
                    {todayItems.length > 0 && groupLabel("Aujourd'hui", todayItems.length, true)}
                    {todayItems.map(t => renderTicketCard(t))}
                    {todayItems.length === 0 && upcomingItems.length > 0 && desktopFilters.when !== 'Upcoming' && (
                      <div className="px-4 py-6 text-center border-b border-slate-100">
                        <p className="text-sm font-bold text-emerald-700 my-0">Rien pour aujourd'hui 🎉</p>
                      </div>
                    )}
                    {upcomingItems.length > 0 && groupLabel('À venir', upcomingItems.length, false)}
                    {upcomingItems.map(t => renderTicketCard(t))}
                  </>
                )
              })()
            )}
          </div>

        </div>

        {/* Colonne droite — workspace */}
        <div className="flex-1 h-full overflow-hidden bg-slate-50 flex flex-col relative">
          {renderDesktopWorkspace()}
          {view === 'EDIT' && (() => {
            const editTicket = tickets.find(t => t.id === selectedTicketId)
            if (!editTicket) return null
            const closeEditView = () => {
              setIsEditPanelClosing(true)
              setTimeout(() => {
                setIsEditPanelClosing(false)
                setView('DETAILS')
              }, 320)
            }
            return (
              <motion.div
                key="edit-slide"
                initial={{ x: '100%' }}
                animate={{ x: isEditPanelClosing ? '100%' : 0 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0 z-30 shadow-2xl"
              >
                <TicketEditViewDesktop
                  ticket={editTicket}
                  currentUserName={userName}
                  lieux={lieux}
                  teams={teams}
                  employees={employees}
                  onSave={(updatedTicket) => {
                    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t))
                    syncTicket(updatedTicket)
                    closeEditView()
                  }}
                  onBack={closeEditView}
                />
              </motion.div>
            )
          })()}
        </div>

        </div>
        )}

        {/* PARAMÈTRES DES INTERVENTIONS — drawer latéral */}
        {(isSettingsOpen || isSettingsClosing) && (() => {
          const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newRecipientEmail.trim())
          const alreadyListed = (email) => (digestConfig.recipients || []).some(r => (r.email || '').toLowerCase() === email.toLowerCase())
          const addRecipient = (label, email) => {
            if (!email || alreadyListed(email))
              return
            saveDigestConfig({ ...digestConfig, recipients: [...(digestConfig.recipients || []), { label: label || email, email: email }] })
            setNewRecipientLabel('')
            setNewRecipientEmail('')
          }
          const quickAddUsers = (dashboardUsers || [])
            .filter(u => u.email && !alreadyListed(u.email))
            .slice(0, 6)
          return (
            <div className="fixed inset-0 flex justify-end" style={{ zIndex: 90 }}>
              <div
                onClick={closeSettings}
                className="absolute inset-0 bg-slate-950 cursor-pointer"
                style={{ opacity: isSettingsClosing ? 0 : 0.45, transition: 'opacity 0.25s ease' }}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Paramètres des interventions"
                className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col z-10"
                style={{ transform: isSettingsClosing ? 'translateX(100%)' : 'translateX(0)', transition: 'transform 0.26s cubic-bezier(0.25, 0.46, 0.45, 0.94)', animation: 'slide-in-right 0.26s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
              >
                {/* En-tête */}
                <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: '#0f172a' }}>
                      <CogIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-800 tracking-tight my-0">Paramètres des interventions</h3>
                      <p className="text-xs text-slate-400 font-bold my-0 mt-0.5">Réglages de l'établissement</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeSettings}
                    aria-label="Fermer les paramètres"
                    className="w-9 h-9 rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center border border-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">

                  {/* Section digest */}
                  <section className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-white">
                      <div className="pr-3">
                        <p className="text-sm font-black text-slate-800 my-0">Digest par email</p>
                        <p className="text-xs text-slate-400 font-semibold my-0 mt-0.5">Le point sur les interventions, envoyé à 7h à votre liste de diffusion</p>
                      </div>
                      <SettingsToggle
                        checked={digestConfig.enabled}
                        onChange={() => saveDigestConfig({ ...digestConfig, enabled: !digestConfig.enabled })}
                        ariaLabel="Activer le digest par email"
                      />
                    </div>

                    {digestConfig.enabled && (
                    <div className="border-t border-slate-200 p-4 space-y-4" style={{ backgroundColor: '#fafbfe' }}>

                      {/* Fréquence */}
                      <div>
                        <span className="text-2xs uppercase font-black text-slate-400 tracking-widest block mb-1.5">Fréquence</span>
                        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden bg-white">
                          {[
                            { id: 'daily',  label: 'Quotidien (7h)' },
                            { id: 'weekly', label: 'Hebdomadaire (lundi 7h)' },
                          ].map(({ id, label }, idx) => {
                            const on = digestConfig.frequency === id
                            return (
                              <button
                                key={id}
                                type="button"
                                onClick={() => { if (!on) saveDigestConfig({ ...digestConfig, frequency: id }) }}
                                aria-pressed={on}
                                className={`px-3 py-2 text-xs font-black transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${idx > 0 ? 'border-l border-slate-200' : ''}`}
                                style={{ backgroundImage: 'none', backgroundColor: on ? '#e8eeff' : 'white', color: on ? '#1a41b8' : '#94a3b8', cursor: on ? 'default' : 'pointer' }}
                              >
                                {label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {/* Liste de diffusion */}
                      <div>
                        <span className="text-2xs uppercase font-black text-slate-400 tracking-widest block mb-1.5">
                          Liste de diffusion{(digestConfig.recipients || []).length > 0 ? ` (${digestConfig.recipients.length})` : ''}
                        </span>
                        {(digestConfig.recipients || []).length === 0 && (
                          <p className="text-xs text-slate-400 font-semibold my-0 mb-2">Aucun destinataire : le digest ne sera pas envoyé.</p>
                        )}
                        <div className="space-y-1.5 mb-3">
                          {(digestConfig.recipients || []).map((r, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
                              <span className="w-6 h-6 rounded-lg text-white flex items-center justify-center font-black flex-shrink-0" style={{ backgroundColor: '#4338ca', fontSize: 9 }}>
                                {((r.label || r.email)[0] || '?').toUpperCase()}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-slate-800 my-0 truncate">{r.label || r.email}</p>
                                <p className="text-2xs font-mono font-semibold text-slate-400 my-0 truncate">{r.email}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => saveDigestConfig({ ...digestConfig, recipients: digestConfig.recipients.filter((_, i) => i !== idx) })}
                                aria-label={`Retirer ${r.email}`}
                                className="w-6 h-6 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                                style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                              >
                                <XIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Ajout rapide depuis les comptes dashboard */}
                        {quickAddUsers.length > 0 && (
                          <div className="mb-3">
                            <span className="text-2xs font-bold text-slate-400 block mb-1">Ajouter un collaborateur :</span>
                            <div className="flex flex-wrap gap-1.5">
                              {quickAddUsers.map(u => {
                                const name = ((u.firstname || '') + ' ' + (u.lastname || '')).trim() || u.email
                                return (
                                  <button
                                    key={String(u._id)}
                                    type="button"
                                    onClick={() => addRecipient(name, u.email)}
                                    className="text-xs font-bold text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700 rounded-lg px-2 py-1 transition-colors duration-200"
                                    style={{ backgroundImage: 'none', backgroundColor: 'white' }}
                                  >
                                    ＋ {name}
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {/* Ajout libre */}
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={newRecipientLabel}
                            onChange={e => setNewRecipientLabel(e.target.value)}
                            placeholder="Nom"
                            className="browser-default bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:border-blue-500"
                            style={{ height: 'auto', margin: 0, boxSizing: 'border-box', width: '30%' }}
                          />
                          <input
                            type="email"
                            value={newRecipientEmail}
                            onChange={e => setNewRecipientEmail(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && emailValid) addRecipient(newRecipientLabel.trim(), newRecipientEmail.trim()) }}
                            placeholder="email@exemple.fr"
                            className="browser-default flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:border-blue-500"
                            style={{ height: 'auto', margin: 0, boxSizing: 'border-box' }}
                          />
                          <button
                            type="button"
                            onClick={() => addRecipient(newRecipientLabel.trim(), newRecipientEmail.trim())}
                            disabled={!emailValid}
                            className="px-3 rounded-xl text-white text-xs font-black transition-opacity duration-200 disabled:opacity-40"
                            style={{ backgroundImage: 'none', backgroundColor: '#0f172a' }}
                          >
                            Ajouter
                          </button>
                        </div>
                        {newRecipientEmail.trim() !== '' && !emailValid && (
                          <p className="text-2xs text-red-500 font-bold my-0 mt-1">Adresse email invalide.</p>
                        )}
                        {newRecipientEmail.trim() !== '' && emailValid && alreadyListed(newRecipientEmail.trim()) && (
                          <p className="text-2xs text-amber-600 font-bold my-0 mt-1">Cette adresse est déjà dans la liste.</p>
                        )}
                      </div>

                    </div>
                    )}
                  </section>

                  {/* Section notifications push (interrupteur d'établissement) */}
                  <section className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-white">
                      <div className="pr-3">
                        <p className="text-sm font-black text-slate-800 my-0">Notifications push</p>
                        <p className="text-xs text-slate-400 font-semibold my-0 mt-0.5">
                          Alertes envoyées aux équipes : nouvelle intervention assignée, réaffectation,
                          clôture d'une urgence. Chacun active ses notifications sur son appareil depuis
                          « Paramètres du compte ».
                        </p>
                      </div>
                      <SettingsToggle
                        checked={pushConfig.enabled}
                        onChange={() => savePushConfig({ ...pushConfig, enabled: !pushConfig.enabled })}
                        ariaLabel="Activer les notifications push pour l'établissement"
                      />
                    </div>
                    {!pushConfig.enabled && (
                      <div className="border-t border-slate-200 p-4" style={{ backgroundColor: '#fffbeb' }}>
                        <p className="text-xs text-amber-700 font-semibold my-0">
                          Désactivées : plus aucune notification push ne sera envoyée aux équipes,
                          même sur les appareils où elles sont activées.
                        </p>
                      </div>
                    )}
                  </section>

                  {/* Section ménages automatiques */}
                  <section className="border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-white">
                      <div className="pr-3">
                        <p className="text-sm font-black text-slate-800 my-0">Ménages automatiques</p>
                        <p className="text-xs text-slate-400 font-semibold my-0 mt-0.5">Un départ = un ticket ménage créé automatiquement sur l'hébergement, avec échéance à l'arrivée suivante</p>
                      </div>
                      <SettingsToggle
                        checked={autoMenageConfig.enabled}
                        onChange={() => saveAutoMenageConfig({ ...autoMenageConfig, enabled: !autoMenageConfig.enabled })}
                        ariaLabel="Activer les ménages automatiques"
                      />
                    </div>
                    {autoMenageConfig.enabled && (
                      <div className="border-t border-slate-200 p-4" style={{ backgroundColor: '#fafbfe' }}>
                        <p className="text-xs text-slate-500 font-semibold my-0 leading-relaxed">
                          Vérification toutes les heures de 6h à 22h · hébergements locatifs uniquement ·
                          assignés à l'équipe ménage · échéance le jour de l'arrivée suivante, à l'heure
                          d'arrivée des locatifs paramétrée dans le pré-checkin (16h par défaut).
                        </p>
                      </div>
                    )}
                  </section>

                  {/* État de sauvegarde */}
                  {settingsSaveState && (
                    <p className={`text-xs font-bold my-0 text-center ${settingsSaveState === 'error' ? 'text-red-500' : settingsSaveState === 'saving' ? 'text-slate-400' : 'text-emerald-600'}`}>
                      {settingsSaveState === 'saving' ? 'Enregistrement…' : settingsSaveState === 'error' ? "Échec de l'enregistrement — réessayez" : '✓ Enregistré'}
                    </p>
                  )}
                  {!settingsLoaded && (
                    <p className="text-xs font-bold text-slate-400 my-0 text-center">Chargement…</p>
                  )}

                </div>

                {/* Pied — lien vers les paramètres du compte */}
                <div className="p-4 border-t border-slate-200 shrink-0">
                  <button
                    type="button"
                    onClick={() => history.push('/parametres', { from: '/interventions' })}
                    className="w-full text-xs font-bold text-slate-500 hover:text-slate-700 py-2 transition-colors duration-200"
                    style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                  >
                    Paramètres du compte (profil, mot de passe…) →
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

      </div>
    )
  }

  // Final Router
  const createFormProps = {
    newIntervention,
    onUpdate: (partial) => setNewIntervention(prev => ({ ...prev, ...partial })),
    onCancel: () => { setView('SELECT_TYPE'); setNewIntervention(EMPTY_INTERVENTION); setSearchQuery(''); },
    onDetails: (defaultDuration) => {
      if (!newIntervention.estimatedDuration)
        setNewIntervention(prev => ({ ...prev, estimatedDuration: defaultDuration }))
      setView('CREATE_DETAILS')
    },
    onCreate: handleCreate,
    lieux,
    getLieuName,
    isLocatif,
    teams,
    employees,
    footerLeft,
    searchQuery,
    setSearchQuery,
    assignSearchQuery,
    setAssignSearchQuery,
  }

  const createFormPropsDesktop = { ...createFormProps, footerLeft: '' }

  function renderCurrentScreen() {
    switch (view) {
      case 'HOME': return renderHome();
      case 'SELECT_TYPE': return (
        <SelectTypeView
          onCancel={() => { presetLocationId.current = null; setView('HOME') }}
          onSelect={(nextView, interventionData) => {
            setNewIntervention({ ...EMPTY_INTERVENTION, ...takePresetLocation(), ...interventionData })
            setView(nextView)
          }}
        />
      );
      case 'SELECT_MENAGE_SUBTYPE': return (
        <SelectMenageSubtypeView
          newIntervention={newIntervention}
          onUpdate={(partial) => setNewIntervention(prev => ({ ...prev, ...partial }))}
          onCancel={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); }}
          onBack={() => setView('SELECT_TYPE')}
          onNext={() => setView('CREATE_MENAGE')}
        />
      );
      case 'CREATE_MENAGE': return <CreateMenageView {...createFormProps} />;
      case 'CREATE_TECH': return <CreateTechView {...createFormProps} />;
      case 'CREATE_DIVERS': return <CreateDiversView {...createFormProps} />;
      case 'CREATE_DETAILS': return (
        <CreationDetailsForm
          data={newIntervention}
          onUpdate={(data) => setNewIntervention(prev => ({ ...prev, ...data }))}
          onConfirm={handleCreate}
          isLocatif={isLocatif}
          footerLeft={footerLeft}
          onBack={() => {
            if (newIntervention.type === 'Ménage') setView('CREATE_MENAGE');
            else if (newIntervention.type === 'Technique') setView('CREATE_TECH');
            else if (newIntervention.type === 'Divers') setView('CREATE_DIVERS');
            else setView('CREATE_TECH');
          }}
        />
      );
      case 'REVIEW': return (
        <ReviewView
          newIntervention={newIntervention}
          onUpdate={(partial) => setNewIntervention(prev => ({ ...prev, ...partial }))}
          onCancel={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); setSearchQuery(''); }}
          onConfirm={handleCreate}
          getLieuName={getLieuName}
          footerLeft={footerLeft}
          isReviewEditOpen={isReviewEditOpen}
          isReviewEditClosing={isReviewEditClosing}
          closeReviewEdit={closeReviewEdit}
          currentUserName={userName}
        />
      );
      case 'DETAILS': return renderTicketDetail();
      case 'EDIT': {
        const ticket = tickets.find(t => t.id === selectedTicketId);
        if (!ticket) return renderHome();
        return (
          <TicketEditView
            ticket={ticket}
            currentUserName={userName}
            onSave={(updatedTicket) => {
              setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));
              syncTicket(updatedTicket);
              setView('DETAILS');
            }}
            onBack={() => setView('DETAILS')}
          />
        );
      }
      case 'DONE_SUCCESS': return renderDoneSuccess();
      case 'CONFIRMATION': return (
        <ConfirmationView
          confirmAnimated={confirmAnimated}
          onHome={() => { setView('HOME'); setNewIntervention(EMPTY_INTERVENTION); }}
          onNewTicket={() => { setView('SELECT_TYPE'); setNewIntervention(EMPTY_INTERVENTION); }}
        />
      );
      default: return renderHome();
    }
  }

  return (
    <div className="h-screen bg-slate-50 font-sans text-slate-900">

      {/* Vue mobile */}
      <div className="interventions-mobile block md:hidden max-w-xl mx-auto bg-white h-screen shadow-xl relative overflow-hidden">

        {/* HOME — couche de base, toujours montée */}
        <motion.div
          key="home-base"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 350 }}
          className="absolute inset-0"
        >
          {renderHome()}
        </motion.div>

        {/* Autres vues (CREATE, EDIT, etc.) — effet fade+y existant */}
        {view !== 'HOME' && view !== 'DETAILS' && !isDetailClosing && (
          <div className="absolute inset-0 bg-white" style={{ zIndex: 30 }}>
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 350 }}
              className="absolute inset-0"
            >
              {renderCurrentScreen()}
            </motion.div>
          </div>
        )}

        {/* DETAILS — glissement depuis la droite */}
        {(view === 'DETAILS' || isDetailClosing) && (
          <motion.div
            className="absolute inset-0"
            style={{ backgroundColor: '#f8fafc', zIndex: 100 }}
            initial={{ x: '100%' }}
            animate={{ x: isDetailClosing ? '100%' : 0 }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {renderTicketDetail()}
          </motion.div>
        )}

        {/* EDIT — bottom sheet par-dessus DETAILS */}
        {(isEditOverlayOpen || isEditOverlayClosing) && (() => {
          const editTicket = tickets.find(t => t.id === selectedTicketId)
          if (!editTicket) return null
          return (
            <>
              <div
                onClick={closeEditOverlay}
                style={{
                  position: 'absolute', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)', zIndex: 140,
                  animation: isEditOverlayClosing
                    ? 'fadeOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                    : 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 150,
                backgroundColor: '#ffffff',
                borderRadius: '24px 24px 0 0',
                height: '85%',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                animation: isEditOverlayClosing
                  ? 'slideDown 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                  : 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                boxShadow: '0 -8px 32px rgba(15,23,42,0.15)',
              }}>
                <TicketEditView
                  ticket={editTicket}
                  currentUserName={userName}
                  onSave={(updatedTicket) => {
                    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t))
                    syncTicket(updatedTicket)
                    closeEditOverlay()
                  }}
                  onBack={closeEditOverlay}
                />
              </div>
            </>
          )
        })()}

      </div>

      {/* Vue desktop */}
      <div
        className="hidden md:flex overflow-hidden bg-slate-50"
        style={{ position: 'fixed', top: 0, left: sidebarCollapsed ? 0 : '16rem', right: 0, bottom: 0, zIndex: 1, transition: 'left 0.3s ease' }}
      >
        {renderDesktopLayout()}
        {renderCreateDrawer()}
      </div>

      {/* Dialog de confirmation de suppression */}
      {deleteConfirmId && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div
            className="absolute inset-0 bg-slate-900 opacity-50 cursor-pointer"
            onClick={() => setDeleteConfirmId(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Confirmer la suppression"
            className="relative bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm flex flex-col gap-4 z-10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                <TrashIcon className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-800 my-0">Supprimer cette intervention ?</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Cette action est irréversible.</p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-xs uppercase tracking-widest rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ backgroundImage: 'none', backgroundColor: '#f8fafc' }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => handleDeleteTicket(deleteConfirmId)}
                className="flex-1 py-3 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-colors duration-200 shadow-sm active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                style={{ backgroundImage: 'none', backgroundColor: '#dc2626' }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox photo : sur téléphone, plein écran sur fond noir (variante 720 px) */}
      {lightboxUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Photo agrandie"
          className={`fixed inset-0 flex items-center justify-center ${lightboxFullScreen ? 'p-0' : 'p-8'}`}
          style={{ zIndex: 9999, backgroundColor: lightboxFullScreen ? '#000000' : 'rgba(2,6,23,0.72)' }}
          onClick={() => setLightboxUrl(null)}
        >
          {/* Image + bouton télécharger */}
          <div className="relative z-10" onClick={e => e.stopPropagation()}>
            <img
              src={thumbUrl(lightboxUrl, 720)}
              alt="Vue agrandie de la pièce jointe"
              referrerPolicy="no-referrer"
              className={`max-w-full object-contain block ${lightboxFullScreen ? '' : 'rounded-2xl shadow-2xl'}`}
              style={{ maxHeight: lightboxFullScreen ? '100vh' : '82vh' }}
            />
            <a
              href={lightboxUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-black text-white text-xs font-bold shadow-lg hover:bg-slate-800 transition-colors"
              style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
              </svg>
              Enregistrer
            </a>
          </div>
          {/* Fermer */}
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            aria-label="Fermer la photo"
            className="absolute top-6 right-6 z-20 w-10 h-10 rounded-full bg-white text-slate-700 hover:bg-slate-100 flex items-center justify-center shadow-lg transition-colors duration-200"
            style={{ backgroundImage: 'none', backgroundColor: 'white' }}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

    </div>
  );
}
